/**
 * FachbereichManagement Component
 * 
 * Manages Helferpunkte (helper points) for a specific Fachbereich.
 * Helferpunkte are the primary organizational structure, each with:
 * - Einsatzort (Helferpunkt) - 100 characters free text
 * - Einsatzuhrzeit - hh:mm format
 * 
 * Features:
 * - View all Helferpunkte for the Fachbereich (sorted by time, then location)
 * - Create new Helferpunkte
 * - Edit existing Helferpunkte
 * - Delete Helferpunkte
 * - Assign helpers to Helferpunkte
 * - View helpers assigned to each Helferpunkt
 * - Export to Excel
 * 
 * @param fachbereich - The Fachbereich name
 * @param onClose - Callback function to close the popup
 */
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../constants';
import { convertBereitFuerMehrereEinsaetze, convertHelperBereitFuerMehrereEinsaetze, formatHelperDisplayName, getVisibleOtherFachbereichFeedback, helperMatchesSearch } from '../utils';
import HelperListEmailModal from './HelperListEmailModal';
import './FachbereichManagement.css';
import './Switch.css';

interface HelperFeedback {
  fachbereich: string;
  rückmeldung: string | null;
  bemerkung: string | null;
  caretaker_id?: number | null;
  passtNichtZentralBestaetigtAt?: string | null;
}

interface Helper {
  id: number;
  first_name: string;
  surname: string;
  email: string | null;
  phone: string | null;
  created_at: string;
  rückmeldung?: string | null;
  bemerkung?: string | null;
  caretaker_id?: number | null;
  fachbereiche?: string[];
  wunscheinsatzort?: string | null;
  einsatzzeit?: string | null;
  bereit_fuer_mehrere_einsaetze?: string | boolean | null; // Backend returns boolean, frontend uses 'Ja'/'Nein'
  hilft_nicht_am_wettkampftag?: number | null; // 0 = helps on race day, 1 = doesn't help
  anmerkung?: string | null;
  aufbauhilfe?: string | null;
  kuchenspende?: number | null;
  tshirt?: string | null;
  feedback?: HelperFeedback[]; // Fachbereich-specific feedback array
}

interface Caretaker {
  id: number;
  username: string;
  role: string;
}

interface Helferpunkt {
  id: number;
  fachbereich: string;
  einsatzort: string;
  einsatzdatum?: string;
  einsatzuhrzeit: string;
  einsatzuhrzeit_bis?: string | null;
  required_assignees?: number | null;
  completed?: number | boolean;
  einsatzinformationen?: string | null;
  caretaker_id?: number | null;
  created_at: string;
}

interface FachbereichManagementProps {
  fachbereich: string;
  onClose: () => void;
  /** When true, FachbereichHelperList defaults to "Keine Rückmeldung" + "Ohne Position" */
  defaultToNewHelperFilters?: boolean;
  /** When true, hide create/edit/delete/assign controls (Beobachter view) */
  readOnly?: boolean;
}

// Helper function to get German weekday abbreviation from date string (YYYY-MM-DD)
const getWeekdayAbbreviation = (dateString: string | undefined): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString + 'T00:00:00'); // Add time to avoid timezone issues
    const dayOfWeek = date.getDay();
    const abbreviations = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    return abbreviations[dayOfWeek] || '';
  } catch (e) {
    return '';
  }
};

// Helper function to get German weekday full name from date string (YYYY-MM-DD)
const getWeekdayName = (dateString: string | undefined): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString + 'T00:00:00'); // Add time to avoid timezone issues
    const dayOfWeek = date.getDay();
    const weekdays = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
    return weekdays[dayOfWeek] || '';
  } catch (e) {
    return '';
  }
};

// Helper function to format date from YYYY-MM-DD to dd.mm.yyyy
const formatDateDDMMYYYY = (dateString: string | undefined): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString + 'T00:00:00'); // Add time to avoid timezone issues
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  } catch (e) {
    return '';
  }
};

// Helper function to format phone number columns as text in Excel to preserve leading zeros
const formatPhoneColumnAsText = (worksheet: XLSX.WorkSheet, columnName: string): void => {
  if (!worksheet || !worksheet['!ref']) return;
  
  const range = XLSX.utils.decode_range(worksheet['!ref']);
  if (!range || range.e.r < 0) return;
  
  // Find the column index for the phone column
  const headerRow = 0;
  let phoneColumnIndex = -1;
  
  // Get header row to find column index
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellRef = XLSX.utils.encode_cell({ r: headerRow, c: col });
    const cell = worksheet[cellRef];
    if (cell && (cell.v === columnName || cell.w === columnName)) {
      phoneColumnIndex = col;
      break;
    }
  }
  
  if (phoneColumnIndex === -1) return;
  
  // Format all cells in the phone column as text
  for (let row = range.s.r + 1; row <= range.e.r; row++) {
    const cellRef = XLSX.utils.encode_cell({ r: row, c: phoneColumnIndex });
    const cell = worksheet[cellRef];
    if (cell) {
      // Set cell type to string and format as text
      cell.t = 's'; // string type
      cell.z = '@'; // text format code in Excel
      // Ensure value is stored as string
      if (cell.v !== null && cell.v !== undefined) {
        cell.v = String(cell.v);
      }
    }
  }
};

// Helper function to parse various date formats from Excel and convert to YYYY-MM-DD
const parseDateFromExcel = (date: any): string | null => {
  if (date === null || date === undefined || date === '') {
    return null;
  }

  // Handle Date objects (Excel/XLSX provides Date objects for date cells)
  // IMPORTANT: When XLSX reads Excel date cells, it typically creates Date objects at LOCAL midnight
  // Example: Excel date "7/20/2024" -> XLSX creates: new Date(2024, 6, 20) (local time)
  // This Date object represents 2024-07-20 00:00:00 in the browser's local timezone
  // We MUST use LOCAL methods to extract the date components to preserve the date as stored
  // Using UTC methods on a locally-created Date would cause -1 day errors in timezones ahead of UTC
  if (date instanceof Date) {
    if (isNaN(date.getTime())) {
      return null;
    }
    // Use LOCAL methods because XLSX creates Date objects at local midnight
    // This preserves the date as it appears in Excel, regardless of timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Handle Excel date serial numbers (number of days since 1900-01-01)
  // Excel epoch is January 1, 1900 (serial number 1), but Excel incorrectly treats 1900 as a leap year
  // IMPORTANT: When XLSX reads date cells from Excel, it may return serial numbers that are off by +1
  // This is because Excel's internal date system can differ from JavaScript's Date system
  // To fix the +1 day error for modern dates (2024+), we subtract 1 from serials >= 60
  // Serial number 1 = 1900-01-01, serial number 60 = 1900-02-29 (which doesn't exist, should be 1900-03-01)
  if (typeof date === 'number') {
    // Floor the serial number to remove any fractional part (time component)
    const flooredSerial = Math.floor(date);
    // Subtract 1 to account for the +1 day offset when XLSX reads Excel dates
    // Excel serial 45493 for "2024-07-20" should be converted as 45492
    // Only apply this correction for serials >= 60 (after the Feb 29, 1900 bug period)
    // This avoids breaking small serial numbers (1-59) which work correctly
    const adjustedSerial = flooredSerial >= 60 ? flooredSerial - 1 : flooredSerial;
    // Use Date.UTC(1900, 0, adjustedSerial) where month 0 = January
    // JavaScript automatically handles overflow (including the Feb 29, 1900 bug)
    const resultDate = new Date(Date.UTC(1900, 0, adjustedSerial));
    
    if (isNaN(resultDate.getTime())) {
      return null;
    }
    // Use UTC methods to extract date components since we calculated in UTC
    // This ensures consistent results regardless of browser timezone
    const year = resultDate.getUTCFullYear();
    const month = String(resultDate.getUTCMonth() + 1).padStart(2, '0');
    const day = String(resultDate.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Convert to string and trim
  const dateStr = String(date).trim();
  if (!dateStr) return null;

  // LAST RESORT: Try to parse as Excel date serial (string representation of number)
  // Only do this if the string doesn't contain date separators (/, ., -) which would indicate a formatted date
  // This prevents strings like "7/20/2024" from being incorrectly parsed as serial number "7"
  if (!dateStr.match(/[/.-]/)) {
    const numericValue = parseFloat(dateStr);
    if (!isNaN(numericValue) && numericValue > 0 && numericValue < 100000) {
      // Likely an Excel date serial number (can have fractional part for time)
      // Floor to remove fractional part, then subtract 1 to fix +1 day offset for serials >= 60
      const flooredSerial = Math.floor(numericValue);
      const adjustedSerial = flooredSerial >= 60 ? flooredSerial - 1 : flooredSerial;
      // Use Date.UTC(1900, 0, adjustedSerial) - JavaScript handles overflow automatically
      const resultDate = new Date(Date.UTC(1900, 0, adjustedSerial));
      
      if (!isNaN(resultDate.getTime())) {
        // Use UTC methods to extract date components since we calculated in UTC
        // This ensures consistent results regardless of browser timezone
        const year = resultDate.getUTCFullYear();
        const month = String(resultDate.getUTCMonth() + 1).padStart(2, '0');
        const day = String(resultDate.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    }
  }

  // Try YYYY-MM-DD format (ISO)
  const isoMatch = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    const year = parseInt(isoMatch[1], 10);
    const month = parseInt(isoMatch[2], 10);
    const day = parseInt(isoMatch[3], 10);
    const testDate = new Date(year, month - 1, day);
    if (testDate.getFullYear() === year && testDate.getMonth() === month - 1 && testDate.getDate() === day) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }

  // Try MM/DD/YYYY or DD/MM/YYYY format (slashes)
  // If first number > 12, it must be DD/MM/YYYY (European)
  // Otherwise, try MM/DD/YYYY (US) first, then DD/MM/YYYY if that fails
  const slashMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const firstNum = parseInt(slashMatch[1], 10);
    const secondNum = parseInt(slashMatch[2], 10);
    const year = parseInt(slashMatch[3], 10);
    
    if (firstNum > 12) {
      // First number > 12, must be DD/MM/YYYY (European)
      const day = firstNum;
      const month = secondNum;
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        const testDate = new Date(year, month - 1, day);
        if (testDate.getFullYear() === year && testDate.getMonth() === month - 1 && testDate.getDate() === day) {
          return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        }
      }
    } else {
      // Try MM/DD/YYYY (US) first
      const month = firstNum;
      const day = secondNum;
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        const testDate = new Date(year, month - 1, day);
        if (testDate.getFullYear() === year && testDate.getMonth() === month - 1 && testDate.getDate() === day) {
          return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        }
      }
      // If MM/DD didn't work, try DD/MM/YYYY (European)
      const dayEu = firstNum;
      const monthEu = secondNum;
      if (monthEu >= 1 && monthEu <= 12 && dayEu >= 1 && dayEu <= 31) {
        const testDateEu = new Date(year, monthEu - 1, dayEu);
        if (testDateEu.getFullYear() === year && testDateEu.getMonth() === monthEu - 1 && testDateEu.getDate() === dayEu) {
          return `${year}-${String(monthEu).padStart(2, '0')}-${String(dayEu).padStart(2, '0')}`;
        }
      }
    }
  }

  // Try DD.MM.YYYY format (German)
  const germanMatch = dateStr.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (germanMatch) {
    const day = parseInt(germanMatch[1], 10);
    const month = parseInt(germanMatch[2], 10);
    const year = parseInt(germanMatch[3], 10);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      const testDate = new Date(year, month - 1, day);
      if (testDate.getFullYear() === year && testDate.getMonth() === month - 1 && testDate.getDate() === day) {
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    }
  }

  // Try MM-DD-YYYY format
  const usDashMatch = dateStr.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (usDashMatch && !isoMatch) { // Only if ISO format didn't already match
    const month = parseInt(usDashMatch[1], 10);
    const day = parseInt(usDashMatch[2], 10);
    const year = parseInt(usDashMatch[3], 10);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      const testDate = new Date(year, month - 1, day);
      if (testDate.getFullYear() === year && testDate.getMonth() === month - 1 && testDate.getDate() === day) {
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    }
  }

  // Try DD-MM-YYYY format (only if US format didn't match)
  const euDashMatch = dateStr.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (euDashMatch && !isoMatch && !usDashMatch) {
    const day = parseInt(euDashMatch[1], 10);
    const month = parseInt(euDashMatch[2], 10);
    const year = parseInt(euDashMatch[3], 10);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      const testDate = new Date(year, month - 1, day);
      if (testDate.getFullYear() === year && testDate.getMonth() === month - 1 && testDate.getDate() === day) {
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    }
  }

  // Last resort: try JavaScript Date.parse (handles many formats)
  // IMPORTANT: For date strings like "7/20/2024", Date.parse creates a date at midnight LOCAL time
  // We must use LOCAL methods (getFullYear, getMonth, getDate) NOT UTC methods to avoid off-by-one errors
  // Using UTC methods would shift the date by timezone offset, causing dates like 7/20/2024 to become 7/19/2024
  const parsedDate = new Date(dateStr);
  if (!isNaN(parsedDate.getTime())) {
    // Always use local methods for date-only strings to preserve the date as written
    // Only use UTC methods if the input was explicitly in ISO format (YYYY-MM-DD) which is timezone-agnostic
    // But even for ISO, if it's date-only (no time), use local to be safe
    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    // Validate that the parsed date is reasonable (between 1900 and 2100)
    if (year >= 1900 && year <= 2100) {
      return `${year}-${month}-${day}`;
    }
  }

  // If none of the formats match, return null
  return null;
};

const FachbereichManagement: React.FC<FachbereichManagementProps> = ({ fachbereich, onClose, defaultToNewHelperFilters = false, readOnly = false }) => {
  const { isAdmin, token, year, user } = useAuth();
  const [helferpunkte, setHelferpunkte] = useState<Helferpunkt[]>([]);
  const [allHelpers, setAllHelpers] = useState<Helper[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [raceDayDate, setRaceDayDate] = useState<string | null>(null);
  const dateDefaultedRef = useRef(false); // Track if date has been defaulted for current form session
  const scrollPositionRef = useRef<number>(0); // Track scroll position to prevent jumping to top
  const clickedHelferpunktIdRef = useRef<number | null>(null); // Track which helferpunkt was clicked to scroll back to it
  const scrollLockRef = useRef<boolean>(false); // Lock to prevent any scrolling during update
  const scrollableContainerRef = useRef<HTMLDivElement | null>(null); // Reference to the scrollable container

  // Helper function to get default race day date (from year config or fallback to June 15th)
  const getDefaultRaceDayDate = (yearNum: number | null): string => {
    if (raceDayDate) {
      return raceDayDate;
    }
    const raceYear = yearNum || new Date().getFullYear();
    return `${raceYear}-06-15`; // Fallback to June 15th if not configured
  };

  // Fetch race day date and maintenance status for current year
  const fetchRaceDayDate = async (): Promise<string | null> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/years/maintenance`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.success && response.data.years) {
        const currentYearData = response.data.years.find((y: { year: number; race_day_date?: string | null; disabled?: boolean }) => y.year === year);
        if (currentYearData) {
          if (currentYearData.race_day_date) {
            setRaceDayDate(currentYearData.race_day_date);
          }
          // Set maintenance disabled status
          setYearMaintenanceDisabled(currentYearData.disabled || false);
          return currentYearData.race_day_date || null;
        }
      }
    } catch (err) {
      // Silently fail - will use fallback date
      console.error('Error fetching race day date and maintenance status:', err);
    }
    return null;
  };

  // Fetch race day date on mount and when year/token changes
  useEffect(() => {
    if (year && token) {
      fetchRaceDayDate();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchRaceDayDate uses year/token from closure
  }, [year, token]);
  
  const [betreuersEnabled, setBetreuersEnabled] = useState<boolean>(true);
  const [yearMaintenanceDisabled, setYearMaintenanceDisabled] = useState<boolean>(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingHelferpunkt, setEditingHelferpunkt] = useState<Helferpunkt | null>(null);
  const [selectedHelferpunkt, setSelectedHelferpunkt] = useState<number | null>(null);
  const [helferpunktHelpers, setHelferpunktHelpers] = useState<{ [key: number]: Helper[] }>({});
  const [helperAssignments, setHelperAssignments] = useState<{ [key: number]: Helferpunkt[] }>({});
  const [allHelperAssignments, setAllHelperAssignments] = useState<{ [key: number]: Helferpunkt[] }>({});
  const [assigningHelpers, setAssigningHelpers] = useState<number | null>(null);
  const [assignmentHasChanges, setAssignmentHasChanges] = useState<boolean>(false);
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const [showHelperList, setShowHelperList] = useState(false);
  const [initialHelperSearchQuery, setInitialHelperSearchQuery] = useState<string>('');
  const [showAdminWarningEdit, setShowAdminWarningEdit] = useState(false);
  const [showAdminWarningCompleted, setShowAdminWarningCompleted] = useState(false);
  const [showRueckmeldungWarning, setShowRueckmeldungWarning] = useState(false);
  const [pendingEditAction, setPendingEditAction] = useState<(() => void) | null>(null);
  const [pendingCompletedAction, setPendingCompletedAction] = useState<{ id: number; completed: boolean } | null>(null);
  const [openKebabMenuId, setOpenKebabMenuId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const neuButtonRef = useRef<HTMLButtonElement>(null);
  const importButtonRef = useRef<HTMLButtonElement>(null);
  const exportButtonRef = useRef<HTMLButtonElement>(null);
  const [sortBy, setSortBy] = useState<'time' | 'name'>('time');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [helferpunktSearchQuery, setHelferpunktSearchQuery] = useState<string>('');
  const [hideCompleted, setHideCompleted] = useState<boolean>(false);
  const [caretakers, setCaretakers] = useState<Caretaker[]>([]);
  const [showMyPositions, setShowMyPositions] = useState<boolean>(true);
  const [positionsFilterExpanded, setPositionsFilterExpanded] = useState<boolean>(false); // Mobile: filters collapsed by default
  const [showActionsKebabMenu, setShowActionsKebabMenu] = useState<boolean>(false);

  // Form state
  const [einsatzort, setEinsatzort] = useState('');
  const [einsatzdatum, setEinsatzdatum] = useState('');
  const [einsatzuhrzeit, setEinsatzuhrzeit] = useState('');
  const [einsatzuhrzeitBis, setEinsatzuhrzeitBis] = useState('');
  const [requiredAssignees, setRequiredAssignees] = useState('');
  const [einsatzinformationen, setEinsatzinformationen] = useState('');
  const [caretakerId, setCaretakerId] = useState<string>('');
  
  // Track initial form values to detect changes
  const [initialFormValues, setInitialFormValues] = useState<{
    einsatzort: string;
    einsatzdatum: string;
    einsatzuhrzeit: string;
    einsatzuhrzeitBis: string;
    requiredAssignees: string;
    einsatzinformationen: string;
    caretakerId: string;
  } | null>(null);

  // Update einsatzdatum when raceDayDate is fetched and form is open (for create mode ONLY)
  // This runs ONLY once when creating a new position - never during edit
  // NOTE: einsatzdatum is NOT in dependencies - we don't want this to run when user changes the date
  // NOTE: year is NOT in dependencies - we don't want this to override user changes when year changes
  useEffect(() => {
    // Reset the ref when form closes or switches to edit mode
    if (!showCreateForm || editingHelferpunkt) {
      dateDefaultedRef.current = false;
      return;
    }
    
    // Only run for CREATE mode (showCreateForm is true AND editingHelferpunkt is null)
    // AND only if we haven't already defaulted the date for this form session
    // AND only if the date is currently empty (to avoid overriding user changes)
    if (raceDayDate && showCreateForm && !editingHelferpunkt && !dateDefaultedRef.current) {
      // Use functional update to read current value without needing it in dependencies
      setEinsatzdatum(currentDate => {
        // Only default if date is completely empty - never override user's manual changes
        if (!currentDate || currentDate.trim() === '') {
          dateDefaultedRef.current = true; // Mark as defaulted
          return raceDayDate;
        }
        // Keep user's manual change - never override
        dateDefaultedRef.current = true; // Mark as defaulted even if user already set it
        return currentDate;
      });
      // Also update initialFormValues if it exists (only for new positions)
      setInitialFormValues(prev => {
        if (prev && (!prev.einsatzdatum || prev.einsatzdatum.trim() === '')) {
          return {
            ...prev,
            einsatzdatum: raceDayDate
          };
        }
        return prev;
      });
    }
  }, [raceDayDate, showCreateForm, editingHelferpunkt]); // Removed 'year' - don't rerun when year changes
  
  // Confirmation dialog state
  const [showCloseConfirmation, setShowCloseConfirmation] = useState<{
    type: 'form' | 'assignment' | 'helperList';
    onSaveAndClose: () => void;
    onDiscardAndClose: () => void;
  } | null>(null);

  useEffect(() => {
    fetchHelferpunkte();
    fetchAllHelpers();
    fetchHelperAssignments();
    fetchAllHelperAssignments(); // Fetch all assignments for global conflict detection
    fetchBetreuersEnabled();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run on fachbereich change only
  }, [fachbereich]);

  // Fetch caretakers for this Fachbereich (only if betreuersEnabled)
  useEffect(() => {
    if (!betreuersEnabled || !token) {
      setCaretakers([]);
      return;
    }
    const fetchCaretakers = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/fachbereich/${encodeURIComponent(fachbereich)}/caretakers`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.data.success) {
          setCaretakers(response.data.caretakers || []);
        }
      } catch (err) {
        // Error fetching caretakers
      }
    };
    fetchCaretakers();
  }, [fachbereich, betreuersEnabled, token]);

  // Restore scroll position synchronously before browser paints (useLayoutEffect runs synchronously)
  useLayoutEffect(() => {
    if (!loading && (scrollPositionRef.current > 0 || clickedHelferpunktIdRef.current !== null)) {
      const scrollableContainer = scrollableContainerRef.current || 
        document.querySelector('.fachbereich-scrollable-wrapper') as HTMLElement;
      
      if (!scrollableContainer) return;
      
      // Disable smooth scrolling temporarily
      const originalScrollBehavior = scrollableContainer.style.scrollBehavior;
      scrollableContainer.style.scrollBehavior = 'auto';
      
      // First try to find and scroll to the specific helferpunkt element by data attribute
      if (clickedHelferpunktIdRef.current !== null) {
        const helferpunktId = clickedHelferpunktIdRef.current;
        const targetElement = document.querySelector(`[data-helferpunkt-id="${helferpunktId}"]`) as HTMLElement;
        
        if (targetElement && scrollableContainer) {
          const containerRect = scrollableContainer.getBoundingClientRect();
          const elementRect = targetElement.getBoundingClientRect();
          const offset = 100; // Offset from top of viewport
          const targetScroll = scrollableContainer.scrollTop + (elementRect.top - containerRect.top) - offset;
          scrollableContainer.scrollTop = targetScroll;
          clickedHelferpunktIdRef.current = null;
          scrollPositionRef.current = 0;
          scrollableContainer.style.scrollBehavior = originalScrollBehavior;
          return;
        } else {
          clickedHelferpunktIdRef.current = null;
        }
      }
      
      // Fallback to scroll position
      const savedPosition = scrollPositionRef.current;
      if (savedPosition > 0 && scrollableContainer) {
        scrollableContainer.scrollTop = savedPosition;
        scrollPositionRef.current = 0;
      }
      
      scrollableContainer.style.scrollBehavior = originalScrollBehavior;
    }
  }, [helferpunkte, loading]);
  
  // Also use useEffect as backup for async operations
  useEffect(() => {
    if (!loading && (scrollPositionRef.current > 0 || clickedHelferpunktIdRef.current !== null)) {
      const scrollableContainer = scrollableContainerRef.current || 
        document.querySelector('.fachbereich-scrollable-wrapper') as HTMLElement;
      
      if (!scrollableContainer) return;
      
      const restoreScroll = () => {
        if (clickedHelferpunktIdRef.current !== null) {
          const helferpunktId = clickedHelferpunktIdRef.current;
          const targetElement = document.querySelector(`[data-helferpunkt-id="${helferpunktId}"]`) as HTMLElement;
          
          if (targetElement && scrollableContainer) {
            const containerRect = scrollableContainer.getBoundingClientRect();
            const elementRect = targetElement.getBoundingClientRect();
            const offset = 100;
            const targetScroll = scrollableContainer.scrollTop + (elementRect.top - containerRect.top) - offset;
            scrollableContainer.scrollTop = targetScroll;
            clickedHelferpunktIdRef.current = null;
            scrollPositionRef.current = 0;
            return;
          }
          clickedHelferpunktIdRef.current = null;
        }
        
        const savedPosition = scrollPositionRef.current;
        if (savedPosition > 0 && scrollableContainer) {
          scrollableContainer.scrollTop = savedPosition;
          scrollPositionRef.current = 0;
        }
      };
      
      // Try multiple times
      requestAnimationFrame(restoreScroll);
      setTimeout(restoreScroll, 0);
      setTimeout(restoreScroll, 50);
      setTimeout(restoreScroll, 100);
    }
  }, [helferpunkte, loading]);

  // Reset showMyPositions filter when filters are hidden (10 or fewer positions)
  // This prevents positions from being hidden when filters are not shown
  useEffect(() => {
    if (helferpunkte.length <= 10 && showMyPositions && betreuersEnabled && !isAdmin()) {
      setShowMyPositions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [helferpunkte.length, betreuersEnabled, isAdmin]);

  const fetchBetreuersEnabled = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/api/fachbereiche`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.success) {
        const fachbereichData = response.data.fachbereiche.find((fb: any) => fb.name === fachbereich);
        if (fachbereichData) {
          setBetreuersEnabled(fachbereichData.betreuers_enabled !== undefined && fachbereichData.betreuers_enabled !== 0 && fachbereichData.betreuers_enabled !== false);
        }
      }
    } catch (err) {
      // Default to enabled if fetch fails
      setBetreuersEnabled(true);
    }
  };

  // Handle Escape key to close modals
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // If admin warning dialogs are open, close them first
        if (showAdminWarningEdit) {
          setShowAdminWarningEdit(false);
          setPendingEditAction(null);
          return;
        }
        if (showAdminWarningCompleted) {
          setShowAdminWarningCompleted(false);
          setPendingCompletedAction(null);
          return;
        }
        if (showRueckmeldungWarning) {
          setShowRueckmeldungWarning(false);
          setPendingCompletedAction(null);
          return;
        }
        
        // If confirmation dialog is open, close it (cancel) and return to previous state
        // This means we stay in whatever dialog/modal was open before the confirmation
        if (showCloseConfirmation) {
          setShowCloseConfirmation(null);
          return; // Don't proceed to close anything else - just return to previous state
        }
        
        // Priority order: close innermost modal first
        // HelperAssignment handles its own Escape key and change detection
        if (assigningHelpers !== null) {
          // Let HelperAssignment component handle Escape - do nothing here
          return;
        } else if (showCreateForm || editingHelferpunkt) {
          // cancelEdit will check for changes and show confirmation if needed
          cancelEdit();
        } else if (selectedHelferpunkt !== null) {
          setSelectedHelferpunkt(null);
        } else if (showHelperList) {
          setShowHelperList(false);
        } else {
          // Check for form changes before closing main overlay
          if (hasFormChanges()) {
            setShowCloseConfirmation({
              type: 'form',
              onSaveAndClose: async () => {
                if (hasFormChanges()) {
                  const form = document.querySelector('.fachbereich-form form') as HTMLFormElement;
                  if (form) {
                    form.requestSubmit();
                    await new Promise(resolve => setTimeout(resolve, 500));
                  }
                }
                setShowCloseConfirmation(null);
                onClose();
              },
              onDiscardAndClose: () => {
                cancelEdit();
                setShowCloseConfirmation(null);
                onClose();
              }
            });
          } else {
            onClose();
          }
        }
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- cancelEdit and hasFormChanges are stable
  }, [assigningHelpers, showCreateForm, editingHelferpunkt, selectedHelferpunkt, showHelperList, onClose, showCloseConfirmation, showAdminWarningEdit, showAdminWarningCompleted, showRueckmeldungWarning]);

  // Close actions kebab menu when clicking outside (mobile)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.fachbereich-actions-kebab') && !target.closest('.fachbereich-actions-kebab-menu') &&
          !target.closest('.fachbereich-positions-actions-kebab') && !target.closest('.fachbereich-positions-actions-kebab-menu')) {
        setShowActionsKebabMenu(false);
      }
    };
    if (showActionsKebabMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showActionsKebabMenu]);

  // Close kebab menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.helferpunkt-kebab') && !target.closest('.helferpunkt-kebab-menu')) {
        setOpenKebabMenuId(null);
      }
    };
    if (openKebabMenuId !== null) {
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openKebabMenuId]);

  const fetchHelferpunkte = async () => {
    try {
      // Lock scroll position if we're restoring scroll after toggle completed
      const scrollableContainer = scrollableContainerRef.current || 
        document.querySelector('.fachbereich-scrollable-wrapper') as HTMLElement;
      const shouldLockScroll = scrollPositionRef.current > 0 || clickedHelferpunktIdRef.current !== null;
      const savedScroll = scrollPositionRef.current || (scrollableContainer?.scrollTop || 0);
      
      if (shouldLockScroll && scrollableContainer) {
        scrollLockRef.current = true;
        scrollableContainer.style.scrollBehavior = 'auto';
        scrollableContainer.scrollTop = savedScroll;
      }
      
      setLoading(true);
      setError('');
      const response = await axios.get(
        `${API_BASE_URL}/api/fachbereich/${encodeURIComponent(fachbereich)}/helferpunkte`
      );
      if (response.data.success) {
        setHelferpunkte(response.data.helferpunkte);
        // Fetch helpers for each helferpunkt
        response.data.helferpunkte.forEach((hp: Helferpunkt) => {
          fetchHelferpunktHelpers(hp.id);
        });
      } else {
        setError(response.data.message || 'Fehler beim Laden der Helferpunkte');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message || 
        'Verbindung zum Server nicht möglich. Bitte stellen Sie sicher, dass der Backend-Server läuft.'
      );
    } finally {
      setLoading(false);
      // Keep scroll lock active briefly after loading completes
      if (scrollLockRef.current) {
        setTimeout(() => {
          scrollLockRef.current = false;
        }, 100);
      }
    }
  };

  const fetchAllHelpers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/fachbereich/${encodeURIComponent(fachbereich)}`);
      if (response.data.success) {
        // Convert bereit_fuer_mehrere_einsaetze from integer (0/1) to string ('Nein'/'Ja')
        // Also ensure aufbauhilfe is properly preserved
        const convertedHelpers = response.data.helpers.map((helper: Helper) => {
          const converted = convertHelperBereitFuerMehrereEinsaetze(helper);
          // Explicitly ensure aufbauhilfe is preserved (convert empty string to null for consistency)
          return {
            ...converted,
            aufbauhilfe: converted.aufbauhilfe !== null && converted.aufbauhilfe !== undefined && String(converted.aufbauhilfe).trim() !== '' 
              ? String(converted.aufbauhilfe).trim() 
              : null
          };
        });
        setAllHelpers(convertedHelpers);
      }
    } catch (err) {
      // Error fetching all helpers
    }
  };

  const fetchHelperAssignments = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/fachbereich/${encodeURIComponent(fachbereich)}/helpers/assignments`);
      if (response.data.success) {
        setHelperAssignments(response.data.assignments || {});
      }
    } catch (err) {
      // Error fetching helper assignments
    }
  };

  // Fetch ALL helper assignments across ALL Fachbereiche for global conflict detection
  const fetchAllHelperAssignments = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/helpers/assignments`);
      if (response.data.success) {
        setAllHelperAssignments(response.data.assignments || {});
      }
    } catch (err) {
      // Error fetching all helper assignments
    }
  };

  const fetchHelferpunktHelpers = async (helferpunktId: number) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/helferpunkte/${helferpunktId}/helpers`);
      if (response.data.success) {
        // Convert bereit_fuer_mehrere_einsaetze from integer (0/1) to string ('Nein'/'Ja')
        const convertedHelpers = response.data.helpers.map((helper: Helper) => 
          convertHelperBereitFuerMehrereEinsaetze(helper)
        );
        setHelferpunktHelpers(prev => ({
          ...prev,
          [helferpunktId]: convertedHelpers
        }));
      }
    } catch (err) {
      // Error fetching helferpunkt helpers
    }
  };

  const handleCreateHelferpunkt = async (e: React.FormEvent) => {
    const form = e.currentTarget as HTMLFormElement;
    
    // Check form validity - if invalid, set custom messages and show validation
    if (!form.checkValidity()) {
      // Find all invalid fields and set custom messages
      const invalidFields = form.querySelectorAll(':invalid') as NodeListOf<HTMLInputElement>;
      invalidFields.forEach((field) => {
        if (field.validity.valueMissing) {
          field.setCustomValidity('Bitte füllen Sie dieses Feld aus');
        } else if (field.validity.patternMismatch && field.id === 'einsatzuhrzeit') {
          field.setCustomValidity('Bitte geben Sie eine gültige Zeit ein (Format: hh:mm, z.B. 08:30)');
        } else if (field.validity.patternMismatch && field.id === 'einsatzuhrzeitBis') {
          field.setCustomValidity('Bitte geben Sie eine gültige Zeit ein (Format: hh:mm, z.B. 17:00)');
        } else if (field.validity.rangeUnderflow && field.id === 'requiredAssignees') {
          field.setCustomValidity('Bitte geben Sie eine Zahl größer oder gleich 1 ein');
        }
      });
      
      // Find the first invalid field and report it (this will show the custom message)
      const firstInvalid = form.querySelector(':invalid') as HTMLInputElement;
      if (firstInvalid) {
        firstInvalid.focus();
        firstInvalid.reportValidity();
      }
      e.preventDefault();
      return;
    }
    
    // Form is valid, prevent default to do async submission
    e.preventDefault();
    
    if (!einsatzort.trim() || !einsatzuhrzeit.trim() || !einsatzuhrzeitBis.trim() || !requiredAssignees.trim()) {
      setError('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    // Validate time format (accepts both h:mm and hh:mm)
    const timeRegex = /^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(einsatzuhrzeit)) {
      setError('Ungültige Zeit. Format: h:mm oder hh:mm (z.B. 8:30 oder 08:30)');
      return;
    }
    
    if (!timeRegex.test(einsatzuhrzeitBis)) {
      setError('Ungültige Endzeit. Format: h:mm oder hh:mm (z.B. 0:00 oder 17:00)');
      return;
    }

    // Normalize times to hh:mm format before sending to backend
    const normalizeTime = (time: string): string => {
      const match = time.match(/^([0-9]):([0-5][0-9])$/);
      if (match) {
        return `0${match[1]}:${match[2]}`;
      }
      return time;
    };
    
    const normalizedStartTime = normalizeTime(einsatzuhrzeit);
    const normalizedEndTime = normalizeTime(einsatzuhrzeitBis);

    // Validate required assignees
    const assigneesNum = parseInt(requiredAssignees.trim());
    if (isNaN(assigneesNum) || assigneesNum < 1) {
      setError('Anzahl benötigter Helfer muss eine positive Zahl sein (mindestens 1)');
      return;
    }

    if (einsatzort.trim().length > 100) {
      setError('Einsatzort darf maximal 100 Zeichen lang sein');
      return;
    }

    if (einsatzinformationen.length > 2000) {
      setError('Einsatzinformationen darf maximal 2000 Zeichen lang sein');
      return;
    }

    // Validate and normalize date format (YYYY-MM-DD)
    const dateValue = einsatzdatum.trim();
    
    if (!dateValue) {
      setError('Einsatzdatum ist erforderlich');
      return;
    }
    
    // Validate date format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateValue)) {
      setError('Ungültiges Datum. Format: tt.mm.jjjj (z.B. 19.07.2025)');
      return;
    }
    
    // Validate that it's a valid date
    const dateObj = new Date(dateValue + 'T00:00:00');
    if (isNaN(dateObj.getTime())) {
      setError('Ungültiges Datum');
      return;
    }
    try {
      const caretakerIdValue = caretakerId && caretakerId !== '' ? parseInt(caretakerId) : null;
      
      const response = await axios.post(
        `${API_BASE_URL}/api/fachbereich/${encodeURIComponent(fachbereich)}/helferpunkte`,
        {
          einsatzort: einsatzort.trim(),
          einsatzdatum: dateValue,
          einsatzuhrzeit: normalizedStartTime,
          einsatzuhrzeit_bis: normalizedEndTime,
          required_assignees: assigneesNum,
          einsatzinformationen: einsatzinformationen.trim() || null,
          caretaker_id: caretakerIdValue
        }
      );

      if (response.data.success) {
        setShowCreateForm(false);
        dateDefaultedRef.current = false; // Reset ref when form closes
        setEinsatzort('');
        // Reset date to empty - will be defaulted ONCE by useEffect when form opens again for new position
        setEinsatzdatum('');
        setEinsatzuhrzeit('');
        setEinsatzuhrzeitBis('');
        setRequiredAssignees('');
        setEinsatzinformationen('');
        setCaretakerId('');
        setError('');
        setInitialFormValues(null);
        fetchHelferpunkte();
      } else {
        setError(response.data.message || 'Fehler beim Erstellen des Helferpunkts');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Erstellen des Helferpunkts');
    }
  };

  const handleUpdateHelferpunkt = async (e: React.FormEvent) => {
    if (!editingHelferpunkt) {
      e.preventDefault();
      return;
    }

    const form = e.currentTarget as HTMLFormElement;
    
    // Check form validity - if invalid, set custom messages and show validation
    if (!form.checkValidity()) {
      // Find all invalid fields and set custom messages
      const invalidFields = form.querySelectorAll(':invalid') as NodeListOf<HTMLInputElement>;
      invalidFields.forEach((field) => {
        if (field.validity.valueMissing) {
          field.setCustomValidity('Bitte füllen Sie dieses Feld aus');
        } else if (field.validity.patternMismatch && field.id === 'einsatzuhrzeit') {
          field.setCustomValidity('Bitte geben Sie eine gültige Zeit ein (Format: hh:mm, z.B. 08:30)');
        } else if (field.validity.patternMismatch && field.id === 'einsatzuhrzeitBis') {
          field.setCustomValidity('Bitte geben Sie eine gültige Zeit ein (Format: hh:mm, z.B. 17:00)');
        } else if (field.validity.rangeUnderflow && field.id === 'requiredAssignees') {
          field.setCustomValidity('Bitte geben Sie eine Zahl größer oder gleich 1 ein');
        }
      });
      
      // Find the first invalid field and report it (this will show the custom message)
      const firstInvalid = form.querySelector(':invalid') as HTMLInputElement;
      if (firstInvalid) {
        firstInvalid.focus();
        firstInvalid.reportValidity();
      }
      e.preventDefault();
      return;
    }
    
    // Form is valid, prevent default to do async submission
    e.preventDefault();

    if (!einsatzort.trim() || !einsatzuhrzeit.trim() || !einsatzuhrzeitBis.trim() || !requiredAssignees.trim()) {
      setError('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    // Validate time format (accepts both h:mm and hh:mm)
    const timeRegex = /^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(einsatzuhrzeit)) {
      setError('Ungültige Zeit. Format: h:mm oder hh:mm (z.B. 8:30 oder 08:30)');
      return;
    }
    
    if (!timeRegex.test(einsatzuhrzeitBis)) {
      setError('Ungültige Endzeit. Format: h:mm oder hh:mm (z.B. 0:00 oder 17:00)');
      return;
    }

    // Normalize times to hh:mm format before sending to backend
    const normalizeTime = (time: string): string => {
      const match = time.match(/^([0-9]):([0-5][0-9])$/);
      if (match) {
        return `0${match[1]}:${match[2]}`;
      }
      return time;
    };
    
    const normalizedStartTime = normalizeTime(einsatzuhrzeit);
    const normalizedEndTime = normalizeTime(einsatzuhrzeitBis);

    // Validate required assignees
    const assigneesNum = parseInt(requiredAssignees.trim());
    if (isNaN(assigneesNum) || assigneesNum < 1) {
      setError('Anzahl benötigter Helfer muss eine positive Zahl sein (mindestens 1)');
      return;
    }

    if (einsatzort.trim().length > 100) {
      setError('Einsatzort darf maximal 100 Zeichen lang sein');
      return;
    }

    if (einsatzinformationen.length > 2000) {
      setError('Einsatzinformationen darf maximal 2000 Zeichen lang sein');
      return;
    }

    // Check if user is Admin (not Fachbereich Manager or Fachbereich Administrator)
    // Show warning that this is typically the work of Fachbereich Managers/Administrators
    if (isAdmin() && user?.role === 'Administrator') {
      setPendingEditAction(() => () => performUpdateHelferpunkt(normalizedStartTime, normalizedEndTime, assigneesNum));
      setShowAdminWarningEdit(true);
      return;
    }

    performUpdateHelferpunkt(normalizedStartTime, normalizedEndTime, assigneesNum);
  };

  const performUpdateHelferpunkt = async (normalizedStartTime: string, normalizedEndTime: string, assigneesNum: number) => {
    if (!editingHelferpunkt) return;

    // Validate and normalize date format (YYYY-MM-DD)
    const dateValue = einsatzdatum.trim();
    
    if (!dateValue) {
      setError('Einsatzdatum ist erforderlich');
      return;
    }
    
    // Validate date format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateValue)) {
      setError('Ungültiges Datum. Format: tt.mm.jjjj (z.B. 19.07.2025)');
      return;
    }
    
    // Validate that it's a valid date
    const dateObj = new Date(dateValue + 'T00:00:00');
    if (isNaN(dateObj.getTime())) {
      setError('Ungültiges Datum');
      return;
    }
    try {
      const caretakerIdValue = caretakerId && caretakerId !== '' ? parseInt(caretakerId) : null;
      
      const response = await axios.put(
        `${API_BASE_URL}/api/helferpunkte/${editingHelferpunkt.id}`,
        {
          einsatzort: einsatzort.trim(),
          einsatzdatum: dateValue,
          einsatzuhrzeit: normalizedStartTime,
          einsatzuhrzeit_bis: normalizedEndTime,
          required_assignees: assigneesNum,
          einsatzinformationen: einsatzinformationen.trim() || null,
          caretaker_id: caretakerIdValue
        }
      );

      if (response.data.success) {
        setEditingHelferpunkt(null);
        setEinsatzort('');
        setEinsatzuhrzeit('');
        setEinsatzuhrzeitBis('');
        setRequiredAssignees('');
        setEinsatzinformationen('');
        setCaretakerId('');
        setError('');
        setInitialFormValues(null);
        fetchHelferpunkte();
      } else {
        setError(response.data.message || 'Fehler beim Aktualisieren des Helferpunkts');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Aktualisieren des Helferpunkts');
    }
  };

  const handleToggleCompleted = async (id: number, completed: boolean) => {
    // Check if there's an open assignment dialog with changes
    if (assigningHelpers === id && assignmentHasChanges) {
      // Show confirmation dialog
      setShowCloseConfirmation({
        type: 'assignment',
        onSaveAndClose: async () => {
          // The HelperAssignment component will handle saving via its own confirmation
          // We just need to close it after saving
          setShowCloseConfirmation(null);
          // Then proceed with toggle completed
          handleToggleCompletedAfterClose(id, completed);
        },
        onDiscardAndClose: () => {
          setAssigningHelpers(null);
          setAssignmentHasChanges(false);
          setShowCloseConfirmation(null);
          // Then proceed with toggle completed
          handleToggleCompletedAfterClose(id, completed);
        }
      });
      return;
    }
    
    // No open assignment dialog or no changes, proceed normally
    handleToggleCompletedAfterClose(id, completed);
  };

  const handleToggleCompletedAfterClose = async (id: number, completed: boolean) => {
    // Prevent marking as completed if no helpers are assigned
    if (completed && (helferpunktHelpers[id]?.length || 0) === 0) {
      setError('Position kann nicht als erledigt markiert werden, wenn keine Helfer zugeordnet sind.');
      return;
    }

    // Check if user is Admin (not Fachbereich Manager or Fachbereich Administrator)
    // Show warning that this is typically the work of Fachbereich Managers/Administrators
    if (isAdmin() && user?.role === 'Administrator') {
      setPendingCompletedAction({ id, completed });
      setShowAdminWarningCompleted(true);
      return;
    }

    // Check if marking as completed and if all helpers have "Bestätigt" Rückmeldung
    if (completed) {
      const helpers = helferpunktHelpers[id] || [];
      const helpersWithoutBestätigt = helpers.filter(helper => 
        !helper.rückmeldung || helper.rückmeldung !== 'Bestätigt'
      );
      
      if (helpersWithoutBestätigt.length > 0) {
        // Show confirmation dialog
        setPendingCompletedAction({ id, completed });
        setShowRueckmeldungWarning(true);
        return;
      }
    }

    performToggleCompleted(id, completed);
  };

  const performToggleCompleted = async (id: number, completed: boolean) => {
    try {
      // Update state optimistically to prevent scroll jump
      setHelferpunkte(prev => prev.map(hp => 
        hp.id === id ? { ...hp, completed: completed ? 1 : 0 } : hp
      ));
      
      const response = await axios.put(
        `${API_BASE_URL}/api/helferpunkte/${id}/completed`,
        { completed }
      );

      if (response.data.success) {
        setError('');
        // Refetch to ensure data is in sync, but scroll is already maintained by optimistic update
        fetchHelferpunkte();
      } else {
        setError(response.data.message || 'Fehler beim Aktualisieren des Status');
        // Revert optimistic update on error
        fetchHelferpunkte();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Aktualisieren des Status');
      // Revert optimistic update on error
      fetchHelferpunkte();
    }
  };

  const handleDeleteHelferpunkt = async (id: number) => {
    if (!window.confirm('Möchtest du diesen Helferpunkt wirklich löschen? Alle Helfer-Zuordnungen werden ebenfalls gelöscht.')) {
      return;
    }

    try {
      const response = await axios.delete(`${API_BASE_URL}/api/helferpunkte/${id}`);
      if (response.data.success) {
        setError('');
        fetchHelferpunkte();
        // Remove from helferpunktHelpers state
        setHelferpunktHelpers(prev => {
          const newState = { ...prev };
          delete newState[id];
          return newState;
        });
      } else {
        setError(response.data.message || 'Fehler beim Löschen des Helferpunkts');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Löschen des Helferpunkts');
    }
  };

  const handleAssignHelpers = async (helferpunktId: number, helperIds: number[]) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/helferpunkte/${helferpunktId}/helpers`,
        { helperIds }
      );

      if (response.data.success) {
        setAssigningHelpers(null);
        setAssignmentHasChanges(false);
        setError('');
        fetchHelferpunktHelpers(helferpunktId);
        fetchHelperAssignments(); // Refresh assignments to show updated data
        fetchAllHelperAssignments(); // Refresh all assignments for global conflict detection
      } else {
        setError(response.data.message || 'Fehler beim Zuordnen der Helfer');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Zuordnen der Helfer');
    }
  };

  const handleSendEmailToHelper = (helper: Helper, helferpunkt: Helferpunkt) => {
    // Check if helper has email address
    if (!helper.email || helper.email.trim() === '') {
      alert('Helfer hat keine E-Mail-Adresse');
      return;
    }

    // Build email address
    const emailAddress = helper.email;

    // Get all positions the helper is assigned to within the current Fachbereich
    const assignments = helperAssignments[helper.id] || [];
    const fachbereichAssignments = assignments.filter(a => a.fachbereich === fachbereich);
    
    // Sort assignments by time, then by location
    const sortedAssignments = [...fachbereichAssignments].sort((a, b) => {
      if (a.einsatzuhrzeit && b.einsatzuhrzeit) {
        const timeCompare = a.einsatzuhrzeit.localeCompare(b.einsatzuhrzeit);
        if (timeCompare !== 0) return timeCompare;
      }
      return (a.einsatzort || '').localeCompare(b.einsatzort || '');
    });

    // Build subject
    const subject = fachbereichAssignments.length === 1
      ? `Helferposition: ${helferpunkt.einsatzort} - ${helferpunkt.fachbereich}`
      : `Helferpositionen: ${fachbereich} - ${fachbereichAssignments.length} Position${fachbereichAssignments.length > 1 ? 'en' : ''}`;

    // Build greeting
    const greeting = `Hallo ${helper.first_name},\n\n`;
    
    // Build email body
    let body = greeting;
    body += `anbei die Informationen für ${fachbereichAssignments.length === 1 ? 'deinen Helfereinsatz' : 'deine Helfereinsätze'} beim Römerman:\n\n`;
    body += `Fachbereich: ${fachbereich}\n\n`;
    
    // Add all positions
    sortedAssignments.forEach((assignment, index) => {
      if (index > 0) {
        body += `\n---\n\n`;
      }
      body += `Position ${index + 1}:\n`;
      
      // Einsatztag: <Tag name> <dd.mm.yyyy> first
      if (assignment.einsatzdatum) {
        const weekdayName = getWeekdayName(assignment.einsatzdatum);
        const formattedDate = formatDateDDMMYYYY(assignment.einsatzdatum);
        if (weekdayName && formattedDate) {
          body += `Einsatztag: ${weekdayName} ${formattedDate}\n`;
        } else if (formattedDate) {
          body += `Einsatztag: ${formattedDate}\n`;
        }
      }
      
      // Einsatzzeit: <hh:mm> second
      if (assignment.einsatzuhrzeit) {
        if (assignment.einsatzuhrzeit_bis) {
          body += `Einsatzzeit: ${assignment.einsatzuhrzeit} - ${assignment.einsatzuhrzeit_bis}\n`;
        } else {
          body += `Einsatzzeit: ${assignment.einsatzuhrzeit}\n`;
        }
      }
      
      // Einsatzort last
      body += `Einsatzort: ${assignment.einsatzort}\n`;
      
      if (assignment.required_assignees) {
        body += `Benötigte Helfer: ${assignment.required_assignees}\n`;
      }
      
      if (assignment.einsatzinformationen) {
        body += `\n${assignment.einsatzinformationen}\n`;
      }
    });
    
    body += `\nVielen Dank für dein Engagement!\n\nMit freundlichen Grüßen\nRömerman Team`;

    // Create mailto link
    const mailtoLink = `mailto:${emailAddress}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };

  const handleSendEmailToHelpers = (helferpunkt: Helferpunkt) => {
    const helpers = helferpunktHelpers[helferpunkt.id] || [];
    
    // Check if there are any helpers assigned
    if (helpers.length === 0) {
      alert('Keine Helfer zugeordnet');
      return;
    }

    // Filter helpers that have email addresses
    const helpersWithEmail = helpers.filter(helper => helper.email && helper.email.trim() !== '');
    
    if (helpersWithEmail.length === 0) {
      alert('Keine Helfer mit E-Mail-Adresse zugeordnet');
      return;
    }

    // Build email addresses (comma-separated)
    const emailAddresses = helpersWithEmail.map(helper => helper.email).join(',');

    // Build subject
    const subject = `Helferposition: ${helferpunkt.einsatzort} - ${helferpunkt.fachbereich}`;

    // Build greeting with all helper first names
    const firstNames = helpersWithEmail.map(helper => helper.first_name).join(', ');
    const greeting = `Hallo ${firstNames},\n\n`;
    
    // Use appropriate form based on number of helpers
    const isMultiple = helpersWithEmail.length > 1;
    const helfereinsatzText = isMultiple ? 'euren Helfereinsatz' : 'deinen Helfereinsatz';
    const introText = `anbei die Informationen für ${helfereinsatzText} beim Römerman:\n\n`;

    // Build email body
    let body = greeting;
    body += introText;
    body += `Fachbereich: ${helferpunkt.fachbereich}\n`;
    
    // Einsatztag: <Tag name> <dd.mm.yyyy> first
    if (helferpunkt.einsatzdatum) {
      const weekdayName = getWeekdayName(helferpunkt.einsatzdatum);
      const formattedDate = formatDateDDMMYYYY(helferpunkt.einsatzdatum);
      if (weekdayName && formattedDate) {
        body += `Einsatztag: ${weekdayName} ${formattedDate}\n`;
      } else if (formattedDate) {
        body += `Einsatztag: ${formattedDate}\n`;
      }
    }
    
    // Einsatzzeit: <hh:mm> second
    if (helferpunkt.einsatzuhrzeit) {
      if (helferpunkt.einsatzuhrzeit_bis) {
        body += `Einsatzzeit: ${helferpunkt.einsatzuhrzeit} - ${helferpunkt.einsatzuhrzeit_bis}\n`;
      } else {
        body += `Einsatzzeit: ${helferpunkt.einsatzuhrzeit}\n`;
      }
    }
    
    // Einsatzort last
    body += `Einsatzort: ${helferpunkt.einsatzort}\n`;
    
    if (helferpunkt.required_assignees) {
      body += `Benötigte Helfer: ${helferpunkt.required_assignees}\n`;
    }
    
    if (helferpunkt.einsatzinformationen) {
      body += `\nEinsatzinformationen:\n${helferpunkt.einsatzinformationen}\n`;
    }
    
    // List helper names only (without email addresses for data protection)
    body += `\nZugeordnete Helfer:\n`;
    helpersWithEmail.forEach(helper => {
      body += `- ${formatHelperDisplayName(helper.first_name, helper.surname)}\n`;
    });

    // Add ending statement (use appropriate form based on number of helpers)
    const engagementText = isMultiple ? 'euren Helfereinsatz' : 'deinen Helfereinsatz';
    body += `\nVielen Dank für ${engagementText}!\n\nMit freundlichen Grüßen\nRömerman Team`;

    // Build mailto: link with BCC instead of TO to protect email addresses
    // Use empty TO field and put all emails in BCC so recipients can't see each other's addresses
    const mailtoLink = `mailto:?bcc=${encodeURIComponent(emailAddresses)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Open email client
    window.location.href = mailtoLink;
  };

  const startEdit = (helferpunkt: Helferpunkt) => {
    setEditingHelferpunkt(helferpunkt);
    setEinsatzort(helferpunkt.einsatzort);
    setEinsatzuhrzeit(helferpunkt.einsatzuhrzeit);
    setEinsatzuhrzeitBis(helferpunkt.einsatzuhrzeit_bis || '');
    setRequiredAssignees(helferpunkt.required_assignees?.toString() || '');
    setEinsatzinformationen(helferpunkt.einsatzinformationen || '');
    setCaretakerId(helferpunkt.caretaker_id?.toString() || '');
    // For editing: Use the existing date, never default it. If missing, leave empty (will be required)
    setEinsatzdatum(helferpunkt.einsatzdatum || '');
    dateDefaultedRef.current = false; // Reset ref when switching to edit mode
    setShowCreateForm(false);
    // Store initial values for change detection
    setInitialFormValues({
      einsatzort: helferpunkt.einsatzort,
      einsatzdatum: helferpunkt.einsatzdatum || '', // Never default during edit
      einsatzuhrzeit: helferpunkt.einsatzuhrzeit,
      einsatzuhrzeitBis: helferpunkt.einsatzuhrzeit_bis || '',
      requiredAssignees: helferpunkt.required_assignees?.toString() || '',
      einsatzinformationen: helferpunkt.einsatzinformationen || '',
      caretakerId: helferpunkt.caretaker_id?.toString() || ''
    });
  };

  // Check if form has unsaved changes
  const hasFormChanges = (): boolean => {
    if (!initialFormValues) return false;
    return (
      einsatzort !== initialFormValues.einsatzort ||
      einsatzdatum !== initialFormValues.einsatzdatum ||
      einsatzuhrzeit !== initialFormValues.einsatzuhrzeit ||
      einsatzuhrzeitBis !== initialFormValues.einsatzuhrzeitBis ||
      requiredAssignees !== initialFormValues.requiredAssignees ||
      einsatzinformationen !== initialFormValues.einsatzinformationen ||
      caretakerId !== initialFormValues.caretakerId
    );
  };

  const cancelEdit = () => {
    if (hasFormChanges()) {
      setShowCloseConfirmation({
        type: 'form',
        onSaveAndClose: async () => {
          // Trigger form submission
          const form = document.querySelector('.fachbereich-form form') as HTMLFormElement;
          if (form) {
            form.requestSubmit();
          }
          setShowCloseConfirmation(null);
        },
        onDiscardAndClose: () => {
    setEditingHelferpunkt(null);
    setShowCreateForm(false);
    dateDefaultedRef.current = false; // Reset ref when form closes
    setEinsatzort('');
    setEinsatzuhrzeit('');
    setEinsatzuhrzeitBis('');
    setRequiredAssignees('');
    setEinsatzinformationen('');
    setCaretakerId('');
    setError('');
          setInitialFormValues(null);
          setShowCloseConfirmation(null);
        }
      });
    } else {
      setEditingHelferpunkt(null);
      setShowCreateForm(false);
      dateDefaultedRef.current = false; // Reset ref when form closes
      setEinsatzort('');
      setEinsatzuhrzeit('');
      setEinsatzuhrzeitBis('');
      setRequiredAssignees('');
      setEinsatzinformationen('');
      setCaretakerId('');
      setError('');
      setInitialFormValues(null);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setImporting(true);
    setImportMessage(null);
    setError('');

    try {
      // Read Excel file
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      
      // Get first sheet
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      
      // Convert to JSON
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        setImportMessage({
          type: 'error',
          text: 'Die Excel-Datei ist leer'
        });
        setImporting(false);
        return;
      }

      // Validate and parse Helferpunkte
      const helferpunkteToImport: Array<{id?: number, einsatzort: string, einsatzdatum: string, einsatzuhrzeit: string, einsatzuhrzeit_bis?: string | null, required_assignees?: number | null, einsatzinformationen?: string | null, caretaker_id?: number | null, completed?: number | null}> = [];
      const errors: string[] = [];
      // Helper function to parse time from Excel (handles both hh:mm format and Excel time format)
      const parseTimeFromExcel = (value: any): string | null => {
        if (value === null || value === undefined || value === '') return null;
        
        // Handle Date objects (xlsx might convert Excel dates to Date objects)
        if (value instanceof Date) {
          const hours = value.getHours();
          const minutes = value.getMinutes();
          const hoursStr = hours.toString().padStart(2, '0');
          const minutesStr = minutes.toString().padStart(2, '0');
          return `${hoursStr}:${minutesStr}`;
        }
        
        const str = value.toString().trim();
        if (!str) return null;
        
        // First, try standard hh:mm or h:mm format (accepts both 0:00-9:59 and 00:00-23:59)
        const timeRegex = /^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;
        if (timeRegex.test(str)) {
          // Normalize to hh:mm format (add leading zero if needed)
          const match = str.match(/^([0-9]):([0-5][0-9])$/);
          if (match) {
            return `0${match[1]}:${match[2]}`;
          }
          return str;
        }
        
        // Try to parse as number (Excel time format: decimal fraction of a day)
        const numValue = parseFloat(str);
        if (!isNaN(numValue)) {
          // Excel stores time as fraction of day (0.0 = 00:00:00, 0.5 = 12:00:00, 1.0 = 24:00:00)
          // Handle both pure time (0-1) and date-time serial numbers (integer part is date, fractional part is time)
          // For negative numbers, use absolute value and handle the fractional part
          const absValue = Math.abs(numValue);
          const timeFraction = absValue % 1; // Get fractional part (time portion)
          
          // Handle time fractions (0-1 range, representing 00:00:00 to 23:59:59)
          if (timeFraction >= 0 && timeFraction < 1) {
            const totalSeconds = Math.round(timeFraction * 24 * 60 * 60);
            const hours = Math.floor(totalSeconds / 3600) % 24; // Ensure hours stay in 0-23 range
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            
            // Format as hh:mm
            const hoursStr = hours.toString().padStart(2, '0');
            const minutesStr = minutes.toString().padStart(2, '0');
            return `${hoursStr}:${minutesStr}`;
          }
        }
        
        return null;
      };

      jsonData.forEach((row: any, index: number) => {
        const rowNum = index + 2; // +2 because Excel rows start at 1 and we have a header
        
        // Try to find ID field (optional - for updating existing positions)
        const idValue = row['ID'] || row['id'] || row['Id'] || null;
        let positionId: number | undefined = undefined;
        if (idValue !== null && idValue !== undefined && idValue !== '') {
          const parsedId = parseInt(idValue.toString().trim());
          if (!isNaN(parsedId) && parsedId > 0) {
            positionId = parsedId;
          }
        }
        
        // Try to find einsatzort field (case-insensitive)
        const einsatzortValue = row['Einsatzort'] || row['einsatzort'] || row['Einsatzort (Helferpunkt)'] || row['einsatzort (helferpunkt)'] || '';
        const einsatzdatumValue = row['Einsatzdatum'] || row['einsatzdatum'] || '';
        const einsatzuhrzeitValue = row['Einsatzuhrzeit (Von)'] || row['Einsatzuhrzeit'] || row['einsatzuhrzeit'] || '';
        const einsatzuhrzeitBisValue = row['Einsatzuhrzeit (Bis)'] || row['einsatzuhrzeit_bis'] || '';
        const requiredAssigneesValue = row['Anzahl benötigter Helfer'] || row['Anzahl benoetigter Helfer'] || row['required_assignees'] || row['Required Assignees'] || '';

        // If ID is provided but position doesn't exist, that's an error
        if (positionId !== undefined) {
          const existingById = helferpunkte.find(hp => hp.id === positionId);
          if (!existingById) {
            errors.push(`Zeile ${rowNum}: Position mit ID ${positionId} existiert nicht`);
            return;
          }
        }

        if (!einsatzortValue || !einsatzortValue.toString().trim()) {
          errors.push(`Zeile ${rowNum}: Einsatzort fehlt`);
          return;
        }

        if (einsatzortValue.toString().trim().length > 100) {
          errors.push(`Zeile ${rowNum}: Einsatzort ist zu lang (max. 100 Zeichen)`);
          return;
        }

        if (!einsatzuhrzeitValue) {
          errors.push(`Zeile ${rowNum}: Einsatzuhrzeit fehlt`);
          return;
        }

        const timeStr = parseTimeFromExcel(einsatzuhrzeitValue);
        if (!timeStr) {
          errors.push(`Zeile ${rowNum}: Ungültige Einsatzuhrzeit "${einsatzuhrzeitValue}" (Format: hh:mm oder Excel-Zeitformat)`);
          return;
        }

        // Validate mandatory end time
        if (!einsatzuhrzeitBisValue) {
          errors.push(`Zeile ${rowNum}: Einsatzuhrzeit (Bis) fehlt (Pflichtfeld)`);
          return;
        }
        
        const bisTimeStr = parseTimeFromExcel(einsatzuhrzeitBisValue);
        if (!bisTimeStr) {
          errors.push(`Zeile ${rowNum}: Ungültige Endzeit (Bis) "${einsatzuhrzeitBisValue}" (Format: hh:mm oder Excel-Zeitformat)`);
          return;
        }

        // Validate required assignees (mandatory field)
        if (!requiredAssigneesValue || !requiredAssigneesValue.toString().trim()) {
          errors.push(`Zeile ${rowNum}: Anzahl benötigter Helfer fehlt (Pflichtfeld)`);
          return;
        }
        
        const assigneesNum = parseInt(requiredAssigneesValue.toString().trim());
        if (isNaN(assigneesNum) || assigneesNum < 1) {
          errors.push(`Zeile ${rowNum}: Ungültige Anzahl benötigter Helfer "${requiredAssigneesValue}" (muss eine positive Zahl sein, mindestens 1)`);
          return;
        }

        // Parse einsatzdatum (optional field, default to race day if missing)
        let einsatzdatumStr: string;
        if (!einsatzdatumValue || einsatzdatumValue.toString().trim() === '') {
          // Default to race day date if not provided
          einsatzdatumStr = getDefaultRaceDayDate(year);
        } else {
          // Use flexible date parser to handle multiple formats
          const parsedDate = parseDateFromExcel(einsatzdatumValue);
          if (!parsedDate) {
            errors.push(`Zeile ${rowNum}: Ungültiges Datum "${einsatzdatumValue}" (Unterstützte Formate: YYYY-MM-DD, MM/DD/YYYY, DD.MM.YYYY, DD/MM/YYYY)`);
            return;
          }
          einsatzdatumStr = parsedDate;
        }

        // Parse einsatzinformationen (optional field, max 2000 chars)
        const einsatzinformationenValue = row['Einsatzinformationen'] || row['einsatzinformationen'] || row['Einsatzinformationen'] || null;
        let einsatzinformationenStr = null;
        if (einsatzinformationenValue !== null && einsatzinformationenValue !== undefined) {
          einsatzinformationenStr = einsatzinformationenValue.toString().trim();
          if (einsatzinformationenStr.length > 2000) {
            errors.push(`Zeile ${rowNum}: Einsatzinformationen ist zu lang (max. 2000 Zeichen)`);
            return;
          }
          if (einsatzinformationenStr === '') {
            einsatzinformationenStr = null;
          }
        }

        // Parse caretaker (Betreuer) - optional field
        const caretakerValue = row['Betreuer'] || row['betreuer'] || row['Caretaker'] || row['caretaker'] || '';
        let caretakerId: number | null = null;
        if (caretakerValue && caretakerValue.toString().trim()) {
          const caretakerName = caretakerValue.toString().trim();
          const foundCaretaker = caretakers.find(c => c.username === caretakerName);
          if (foundCaretaker) {
            caretakerId = foundCaretaker.id;
          } else if (caretakerName !== '') {
            // Warn but don't fail - caretaker might not exist or might be from another Fachbereich
            // We'll just skip setting the caretaker_id
          }
        }

        // Parse Erledigt (completed) - optional field: Ja/Yes/1/true = completed, Nein/No/0/false = not completed
        const erledigtValue = row['Erledigt'] || row['erledigt'] || row['Completed'] || row['completed'] || '';
        let completedValue: number | null = null;
        if (erledigtValue !== null && erledigtValue !== undefined && erledigtValue !== '') {
          const erledigtStr = erledigtValue.toString().trim().toLowerCase();
          if (['ja', 'yes', '1', 'true', 'x', 'j'].includes(erledigtStr)) {
            completedValue = 1;
          } else if (['nein', 'no', '0', 'false', 'n', ''].includes(erledigtStr)) {
            completedValue = 0;
          }
        }

        helferpunkteToImport.push({
          id: positionId,
          einsatzort: einsatzortValue.toString().trim(),
          einsatzdatum: einsatzdatumStr,
          einsatzuhrzeit: timeStr,
          einsatzuhrzeit_bis: bisTimeStr, // Now mandatory
          required_assignees: assigneesNum,
          einsatzinformationen: einsatzinformationenStr,
          caretaker_id: caretakerId,
          completed: completedValue
        });
      });

      if (errors.length > 0) {
        setImportMessage({
          type: 'error',
          text: `Fehler beim Import:\n${errors.slice(0, 10).join('\n')}${errors.length > 10 ? `\n... und ${errors.length - 10} weitere Fehler` : ''}`
        });
        setImporting(false);
        return;
      }

      if (helferpunkteToImport.length === 0) {
        setImportMessage({
          type: 'error',
          text: 'Keine gültigen Helferpunkte in der Datei gefunden'
        });
        setImporting(false);
        return;
      }

      // Import Helferpunkte (update existing or create new)
      let successCount = 0;
      let updateCount = 0;
      let createCount = 0;
      let failCount = 0;
      const failErrors: string[] = [];

      // First, fetch existing Helferpunkte to check for matches
      const existingHelferpunkte = helferpunkte;

      for (const helferpunkt of helferpunkteToImport) {
        try {
          let existing: Helferpunkt | undefined = undefined;
          
          // Priority 1: If ID is provided, use it to find existing position
          if (helferpunkt.id !== undefined && helferpunkt.id !== null) {
            existing = existingHelferpunkte.find(hp => hp.id === helferpunkt.id);
            if (!existing) {
              failCount++;
              failErrors.push(`${helferpunkt.einsatzort}: Position mit ID ${helferpunkt.id} existiert nicht`);
              continue;
            }
          } else {
            // Priority 2: Check if a Helferpunkt with the same einsatzort and einsatzuhrzeit already exists (fallback)
            existing = existingHelferpunkte.find(
              hp => hp.einsatzort === helferpunkt.einsatzort && 
                    hp.einsatzuhrzeit === helferpunkt.einsatzuhrzeit
            );
          }

          if (existing) {
            // Update existing Helferpunkt (assigned helpers are preserved by the backend)
            const { id, ...updateData } = helferpunkt; // Remove id from update data
            const response = await axios.put(
              `${API_BASE_URL}/api/helferpunkte/${existing.id}`,
              updateData,
              {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              }
            );
            if (response.data.success) {
              successCount++;
              updateCount++;
            } else {
              failCount++;
              failErrors.push(`${helferpunkt.einsatzort}: ${response.data.message || 'Unbekannter Fehler'}`);
            }
          } else {
            // Create new Helferpunkt
            const { id, ...createData } = helferpunkt; // Remove id from create data
            const response = await axios.post(
              `${API_BASE_URL}/api/fachbereich/${encodeURIComponent(fachbereich)}/helferpunkte`,
              createData,
              {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              }
            );
            if (response.data.success) {
              successCount++;
              createCount++;
            } else {
              failCount++;
              failErrors.push(`${helferpunkt.einsatzort}: ${response.data.message || 'Unbekannter Fehler'}`);
            }
          }
        } catch (err: any) {
          failCount++;
          const errorMsg = err.response?.data?.message || err.message || 'Unbekannter Fehler';
          failErrors.push(`${helferpunkt.einsatzort}: ${errorMsg}`);
        }
      }

      // Refresh the list
      await fetchHelferpunkte();

      // Show result message
      if (failCount === 0) {
        const details: string[] = [];
        if (updateCount > 0) {
          details.push(`${updateCount} aktualisiert`);
        }
        if (createCount > 0) {
          details.push(`${createCount} erstellt`);
        }
        const detailText = details.length > 0 ? ` (${details.join(', ')})` : '';
        setImportMessage({
          type: 'success',
          text: `${successCount} Helferpunkt${successCount !== 1 ? 'e' : ''} erfolgreich importiert${detailText}`
        });
      } else {
        const details: string[] = [];
        if (updateCount > 0) {
          details.push(`${updateCount} aktualisiert`);
        }
        if (createCount > 0) {
          details.push(`${createCount} erstellt`);
        }
        const detailText = details.length > 0 ? ` (${details.join(', ')})` : '';
        setImportMessage({
          type: 'error',
          text: `${successCount} erfolgreich${detailText}, ${failCount} fehlgeschlagen:\n${failErrors.slice(0, 5).join('\n')}${failErrors.length > 5 ? `\n... und ${failErrors.length - 5} weitere Fehler` : ''}`
        });
      }
    } catch (err: any) {
      setImportMessage({
        type: 'error',
        text: `Fehler beim Lesen der Datei: ${err.message || 'Unbekannter Fehler'}`
      });
    } finally {
      setImporting(false);
    }
  };

  // Calculate hours between two time strings (HH:MM format)
  const calculateHours = (timeFrom: string, timeTo: string | null): string => {
    if (!timeFrom || !timeTo) {
      return '';
    }
    
    try {
      const [hoursFrom, minutesFrom] = timeFrom.split(':').map(Number);
      const [hoursTo, minutesTo] = timeTo.split(':').map(Number);
      
      const fromMinutes = hoursFrom * 60 + minutesFrom;
      const toMinutes = hoursTo * 60 + minutesTo;
      
      // Handle case where timeTo is on the next day
      let diffMinutes = toMinutes - fromMinutes;
      if (diffMinutes < 0) {
        diffMinutes += 24 * 60; // Add 24 hours
      }
      
      const hours = diffMinutes / 60;
      return hours.toFixed(2).replace('.', ',');
    } catch (error) {
      return '';
    }
  };

  const exportToExcel = () => {
    if (helferpunkte.length === 0) return;

    const excelData: any[] = [];
    
    helferpunkte.forEach((hp) => {
      const helpers = helferpunktHelpers[hp.id] || [];
      const hoursPerEinsatz = calculateHours(hp.einsatzuhrzeit, hp.einsatzuhrzeit_bis || null);
      const caretakerName = hp.caretaker_id ? (caretakers.find(c => c.id === hp.caretaker_id)?.username || '') : '';
      const erledigtValue = (hp.completed === 1 || hp.completed === true) ? 'Ja' : 'Nein';
      
      if (helpers.length === 0) {
        excelData.push({
          'ID': hp.id,
          'Einsatzort': hp.einsatzort,
          'Einsatzdatum': hp.einsatzdatum || getDefaultRaceDayDate(year),
          'Einsatzuhrzeit (Von)': hp.einsatzuhrzeit,
          'Einsatzuhrzeit (Bis)': hp.einsatzuhrzeit_bis || '',
          'Anzahl benötigter Helfer': hp.required_assignees || '',
          'Einsatzinformationen': hp.einsatzinformationen || '',
          'Betreuer': caretakerName,
          'Erledigt': erledigtValue,
          'Stunden': hoursPerEinsatz,
          'Vorname': '',
          'Nachname': '',
          'E-Mail': '',
          'Telefon': ''
        });
      } else {
        helpers.forEach((helper) => {
          excelData.push({
            'ID': hp.id,
            'Einsatzort': hp.einsatzort,
            'Einsatzdatum': hp.einsatzdatum || getDefaultRaceDayDate(year),
            'Einsatzuhrzeit (Von)': hp.einsatzuhrzeit,
            'Einsatzuhrzeit (Bis)': hp.einsatzuhrzeit_bis || '',
            'Anzahl benötigter Helfer': hp.required_assignees || '',
            'Einsatzinformationen': hp.einsatzinformationen || '',
            'Betreuer': caretakerName,
            'Erledigt': erledigtValue,
            'Stunden': hoursPerEinsatz,
            'Vorname': helper.first_name,
            'Nachname': helper.surname,
            'E-Mail': helper.email || '',
            'Telefon': helper.phone || ''
          });
        });
      }
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    
    // Format phone number column as text to preserve leading zeros
    formatPhoneColumnAsText(worksheet, 'Telefon');
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Helferpunkte');

    const columnWidths = [
      { wch: 8 },   // ID
      { wch: 30 },  // Einsatzort
      { wch: 12 },  // Einsatzdatum
      { wch: 15 },  // Einsatzuhrzeit (Von)
      { wch: 15 },  // Einsatzuhrzeit (Bis)
      { wch: 25 },  // Anzahl benötigter Helfer
      { wch: 50 },  // Einsatzinformationen
      { wch: 20 },  // Betreuer
      { wch: 10 },  // Erledigt
      { wch: 12 },  // Stunden
      { wch: 15 },  // Vorname
      { wch: 25 },  // Nachname
      { wch: 25 },  // E-Mail
      { wch: 15 }   // Telefon
    ];
    worksheet['!cols'] = columnWidths;

    const today = new Date();
    const dateStr = today.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\//g, '-');
    const safeFachbereich = fachbereich.replace(/[^a-zA-Z0-9]/g, '_');
    const filename = `Roemerman_${year || 2026}_${safeFachbereich}_Helferpunkte_${dateStr}.xlsx`;

    XLSX.writeFile(workbook, filename);
  };

  return (
    <div className="fachbereich-overlay">
      <div className="fachbereich-container">
        <div className="fachbereich-header">
          <h2 className="fachbereich-title">FACHBEREICH POSITIONEN: {fachbereich}</h2>
          <button className="fachbereich-close" onClick={() => {
            if (hasFormChanges() || assigningHelpers !== null) {
              setShowCloseConfirmation({
                type: 'form',
                onSaveAndClose: async () => {
                  if (hasFormChanges()) {
                    const form = document.querySelector('.fachbereich-form form') as HTMLFormElement;
                    if (form) {
                      form.requestSubmit();
                      // Wait a bit for save to complete
                      await new Promise(resolve => setTimeout(resolve, 500));
                    }
                  }
                  setShowCloseConfirmation(null);
                  onClose();
                },
                onDiscardAndClose: () => {
                  cancelEdit();
                  setAssigningHelpers(null);
                  setShowCloseConfirmation(null);
                  onClose();
                }
              });
            } else {
              onClose();
            }
          }}>×</button>
        </div>
        {!loading && !error && (
          <>
            {/* Row 1: Action buttons - fixed (desktop: buttons, mobile when <=10: kebab) */}
            <div className="fachbereich-actions-row">
              <div className="fachbereich-actions-buttons">
              {!readOnly && (
              <button 
                ref={neuButtonRef}
                className="fachbereich-button" 
                onClick={async () => {
                  if (yearMaintenanceDisabled) {
                    return;
                  }
                  if (hasFormChanges()) {
                    setShowCloseConfirmation({
                      type: 'form',
                      onSaveAndClose: async () => {
                        const form = document.querySelector('.fachbereich-form form') as HTMLFormElement;
                        if (form) {
                          form.requestSubmit();
                        }
                        setShowCloseConfirmation(null);
                        // After save, open new form
                        let defaultDate = raceDayDate;
                        if (!defaultDate) {
                          const fetchedDate = await fetchRaceDayDate();
                          defaultDate = fetchedDate || getDefaultRaceDayDate(year);
                        } else {
                          defaultDate = getDefaultRaceDayDate(year);
                        }
                        setEinsatzort('');
                        setEinsatzdatum(defaultDate);
                        setEinsatzuhrzeit('');
                        setEinsatzuhrzeitBis('');
                        setRequiredAssignees('');
                        setEinsatzinformationen('');
                        setCaretakerId('');
                        setShowCreateForm(true);
                        setInitialFormValues({
                          einsatzort: '',
                          einsatzdatum: defaultDate,
                          einsatzuhrzeit: '',
                          einsatzuhrzeitBis: '',
                          requiredAssignees: '',
                          einsatzinformationen: '',
                          caretakerId: ''
                        });
                      },
                      onDiscardAndClose: async () => {
                        cancelEdit();
                        // Fetch race day date if not already loaded
                        let defaultDate = raceDayDate;
                        if (!defaultDate) {
                          const fetchedDate = await fetchRaceDayDate();
                          defaultDate = fetchedDate || getDefaultRaceDayDate(year);
                        } else {
                          defaultDate = getDefaultRaceDayDate(year);
                        }
                        setEinsatzort('');
                        setEinsatzdatum(defaultDate);
                        setEinsatzuhrzeit('');
                        setEinsatzuhrzeitBis('');
                        setRequiredAssignees('');
                        setEinsatzinformationen('');
                        setCaretakerId('');
                        setShowCreateForm(true);
                        setInitialFormValues({
                          einsatzort: '',
                          einsatzdatum: defaultDate,
                          einsatzuhrzeit: '',
                          einsatzuhrzeitBis: '',
                          requiredAssignees: '',
                          einsatzinformationen: '',
                          caretakerId: ''
                        });
                        setShowCloseConfirmation(null);
                      }
                    });
                  } else {
                  cancelEdit();
                  // Fetch race day date if not already loaded before opening form
                  let defaultDate = raceDayDate;
                  if (!defaultDate) {
                    const fetchedDate = await fetchRaceDayDate();
                    defaultDate = fetchedDate || getDefaultRaceDayDate(year);
                  } else {
                    defaultDate = getDefaultRaceDayDate(year);
                  }
                  setShowCreateForm(!showCreateForm);
                    if (!showCreateForm) {
                      setEinsatzort('');
                      setEinsatzdatum(defaultDate);
                      setEinsatzuhrzeit('');
                      setEinsatzuhrzeitBis('');
                      setRequiredAssignees('');
                      setEinsatzinformationen('');
                      setCaretakerId('');
                      setInitialFormValues({
                        einsatzort: '',
                        einsatzdatum: defaultDate,
                        einsatzuhrzeit: '',
                        einsatzuhrzeitBis: '',
                        requiredAssignees: '',
                        einsatzinformationen: '',
                        caretakerId: ''
                      });
                    }
                  }
                }}
                disabled={yearMaintenanceDisabled}
                title={yearMaintenanceDisabled ? `Das Jahr ${year} ist schreibgeschützt - Positionen können nicht erstellt werden` : "Neuen Helferpunkt erstellen"}
                style={{ opacity: yearMaintenanceDisabled ? 0.6 : 1, cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer' }}
              >
                📍 Neu
              </button>
              )}
              {!readOnly && (
              <button 
                ref={importButtonRef}
                className="fachbereich-button hide-import-on-mobile-phone" 
                onClick={handleImportClick}
                disabled={importing || yearMaintenanceDisabled}
                title={yearMaintenanceDisabled ? `Das Jahr ${year} ist schreibgeschützt - Import nicht möglich` : "Helferpunkte aus Excel-Datei importieren"}
                style={{ opacity: yearMaintenanceDisabled ? 0.6 : 1, cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer' }}
              >
                {importing ? '⏳' : '📍'} Import
              </button>
              )}
              {helferpunkte.length > 0 && (
                <button 
                  ref={exportButtonRef}
                  className="fachbereich-button" 
                  onClick={(e) => {
                    e.stopPropagation();
                    exportToExcel();
                  }}
                  title="Als Excel-Datei herunterladen"
                >
                  📥 Export
                </button>
              )}
              </div>
              {(!readOnly || helferpunkte.length > 0) && (
              <div className="fachbereich-actions-kebab-mobile">
                <button
                  className="fachbereich-actions-kebab"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowActionsKebabMenu(!showActionsKebabMenu);
                  }}
                  title="Positionen Aktionen"
                >
                  ⋮
                </button>
                {showActionsKebabMenu && (
                  <div className="fachbereich-actions-kebab-menu">
                    {!readOnly && (
                      <>
                        <button
                          className="helferpunkt-kebab-item"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowActionsKebabMenu(false);
                            neuButtonRef.current?.click();
                          }}
                          disabled={yearMaintenanceDisabled}
                        >
                          📍 Neu
                        </button>
                        <button
                          className="helferpunkt-kebab-item hide-import-on-mobile-phone"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowActionsKebabMenu(false);
                            importButtonRef.current?.click();
                          }}
                          disabled={importing || yearMaintenanceDisabled}
                        >
                          {importing ? '⏳' : '📍'} Import
                        </button>
                      </>
                    )}
                    {helferpunkte.length > 0 && (
                      <button
                        className="helferpunkt-kebab-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowActionsKebabMenu(false);
                          exportButtonRef.current?.click();
                        }}
                      >
                        📥 Export
                      </button>
                    )}
                  </div>
                )}
              </div>
              )}
            </div>
            {/* Row 2: Search + filters (only when > 10 positions). On mobile: search, expand/collapse, kebab in one row like Helferliste */}
            {helferpunkte.length > 10 && (
            <div className="fachbereich-positions-filter-container">
              <div className="fachbereich-positions-filter-inner">
              <div className="fachbereich-positions-filter-header">
                <input
                  type="text"
                  id="helferpunkt-search"
                  name="helferpunkt-search"
                  placeholder="🔍 Suche..."
                  value={helferpunktSearchQuery}
                  onChange={(e) => setHelferpunktSearchQuery(e.target.value)}
                  className="fachbereich-positions-search-input"
                />
                <button
                  className="fachbereich-positions-filter-toggle-mobile"
                  onClick={() => setPositionsFilterExpanded(!positionsFilterExpanded)}
                  aria-label={positionsFilterExpanded ? "Filter ausblenden" : "Filter anzeigen"}
                  title={positionsFilterExpanded ? "Filter ausblenden" : "Filter anzeigen"}
                >
                  {positionsFilterExpanded ? '▼' : '▲'}
                </button>
                <div className="fachbereich-positions-actions-kebab-container">
                  <button
                    className="fachbereich-positions-actions-kebab"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowActionsKebabMenu(!showActionsKebabMenu);
                    }}
                    title="Positionen Aktionen"
                  >
                    ⋮
                  </button>
                  {showActionsKebabMenu && (
                    <div className="fachbereich-positions-actions-kebab-menu">
                      {!readOnly && (
                        <>
                          <button
                            className="helferpunkt-kebab-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowActionsKebabMenu(false);
                              neuButtonRef.current?.click();
                            }}
                            disabled={yearMaintenanceDisabled}
                          >
                            📍 Neu
                          </button>
                          <button
                            className="helferpunkt-kebab-item hide-import-on-mobile-phone"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowActionsKebabMenu(false);
                              importButtonRef.current?.click();
                            }}
                            disabled={importing || yearMaintenanceDisabled}
                          >
                            {importing ? '⏳' : '📍'} Import
                          </button>
                        </>
                      )}
                      {helferpunkte.length > 0 && (
                        <button
                          className="helferpunkt-kebab-item"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowActionsKebabMenu(false);
                            exportButtonRef.current?.click();
                          }}
                        >
                          📥 Export
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className={`fachbereich-search-filters ${positionsFilterExpanded ? 'expanded' : 'collapsed'}`}>
              {/* Toggle: My Positions (ON) / All Positions (OFF) - right after search */}
              {betreuersEnabled && !isAdmin() && (() => {
                // Calculate count of all positions (respecting filters)
                const allPositionsCount = helferpunkte.filter((hp) => {
                  if (hideCompleted && (hp.completed === 1 || hp.completed === true)) {
                    return false;
                  }
                  if (!helferpunktSearchQuery.trim()) return true;
                  const query = helferpunktSearchQuery.trim().toLowerCase();
                  return hp.einsatzort.toLowerCase().includes(query);
                }).length;
                
                // Calculate count of positions assigned to current user
                const myPositionsCount = helferpunkte.filter((hp) => {
                  if (hideCompleted && (hp.completed === 1 || hp.completed === true)) {
                    return false;
                  }
                  if (!helferpunktSearchQuery.trim()) {
                    return hp.caretaker_id === user?.id;
                  }
                  const query = helferpunktSearchQuery.trim().toLowerCase();
                  return hp.caretaker_id === user?.id && hp.einsatzort.toLowerCase().includes(query);
                }).length;
                
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }}>
                      {showMyPositions ? `Meine betreuten Positionen (${myPositionsCount}/${allPositionsCount})` : `Positionen ungeachtet der Betreuer (${allPositionsCount})`}
                    </span>
                    <label className="switch-container">
                      <input
                        type="checkbox"
                        className="switch-input"
                        checked={showMyPositions}
                        onChange={(e) => setShowMyPositions(e.target.checked)}
                      />
                      <span className="switch-track switch-track-red">
                        <span className="switch-thumb" />
                      </span>
                    </label>
                  </div>
                );
              })()}
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                {/* Toggle: Erledigte Positionen ausgeblendet/angezeigt */}
                {(() => {
                  const baseForErledigt = helferpunkte.filter((hp) => {
                    if (helferpunktSearchQuery.trim()) {
                      const q = helferpunktSearchQuery.trim().toLowerCase();
                      if (!hp.einsatzort.toLowerCase().includes(q)) return false;
                    }
                    if (betreuersEnabled && !isAdmin() && helferpunkte.length > 10 && showMyPositions && user?.id) {
                      if (hp.caretaker_id !== user.id) return false;
                    }
                    return true;
                  });
                  const erledigtCount = baseForErledigt.filter(hp => hp.completed === 1 || hp.completed === true).length;
                  const erledigtTotal = baseForErledigt.length;
                  return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }}>
                    {hideCompleted ? `Erledigte Positionen ausgeblendet (${erledigtCount}/${erledigtTotal})` : `Erledigte Positionen angezeigt (${erledigtCount}/${erledigtTotal})`}
                  </span>
                  <label className="switch-container">
                    <input
                      type="checkbox"
                      className="switch-input"
                      checked={!hideCompleted}
                      onChange={(e) => setHideCompleted(!e.target.checked)}
                    />
                    <span className="switch-track switch-track-red">
                      <span className="switch-thumb" />
                    </span>
                  </label>
                </div>
                  );
                })()}

                {/* Toggle: Sortiert nach Zeit/Name */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }}>
                    {sortBy === 'time' ? 'Sortiert nach Zeit' : 'Sortiert nach Name'}
                  </span>
                  <label className="switch-container">
                    <input
                      type="checkbox"
                      className="switch-input"
                      checked={sortBy === 'name'}
                      onChange={(e) => setSortBy(e.target.checked ? 'name' : 'time')}
                    />
                    <span className="switch-track switch-track-red">
                      <span className="switch-thumb" />
                    </span>
                  </label>
                </div>

                {/* Toggle: Aufsteigend/Absteigend with arrow */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }}>
                    {sortOrder === 'asc' ? '↑' : '↓'} {sortOrder === 'asc' ? 'Aufsteigend' : 'Absteigend'}
                  </span>
                  <label className="switch-container">
                    <input
                      type="checkbox"
                      className="switch-input"
                      checked={sortOrder === 'desc'}
                      onChange={(e) => setSortOrder(e.target.checked ? 'desc' : 'asc')}
                    />
                    <span className="switch-track switch-track-red">
                      <span className="switch-thumb" />
                    </span>
                  </label>
                </div>
              </div>
              </div>
            </div>
            </div>
            )}
          </>
        )}
        <div className="fachbereich-scrollable-wrapper" ref={scrollableContainerRef}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileImport}
            style={{ display: 'none' }}
          />
          
          {yearMaintenanceDisabled && !readOnly && (
            <div className="fachbereich-error" style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107', color: '#856404', marginBottom: '16px' }}>
              ⚠️ Das Jahr {year} ist schreibgeschützt. Positionen können nicht erstellt, bearbeitet oder gelöscht werden. Helferzuordnungen und Erledigt-Status können nicht geändert werden.
            </div>
          )}
          {error && <div className="fachbereich-error">{error}</div>}
          {importMessage && (
            <div className={`fachbereich-import-message ${importMessage.type}`}>
              {importMessage.text.split('\n').map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i < importMessage.text.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </div>
          )}

          {loading ? (
            <div className="fachbereich-loading">Lädt...</div>
          ) : helferpunkte.length === 0 ? (
            <div className="fachbereich-empty">
              Noch keine Helferpunkte für diesen Fachbereich erstellt
            </div>
          ) : (
            <div className="fachbereich-content">
            <div className="helferpunkte-list">
              {[...helferpunkte]
                .filter((hp) => {
                  // Filter out completed positions if hideCompleted is true
                  if (hideCompleted && (hp.completed === 1 || hp.completed === true)) {
                    return false;
                  }
                  // Filter by search query
                  if (helferpunktSearchQuery.trim()) {
                    const query = helferpunktSearchQuery.trim().toLowerCase();
                    if (!hp.einsatzort.toLowerCase().includes(query)) {
                      return false;
                    }
                  }
                  // Filter by caretaker (My Positions vs All Positions) - only for non-admins
                  // IMPORTANT: Only apply filter if filters are shown (more than 10 positions)
                  // When filters are hidden, don't apply any default filtering
                  if (betreuersEnabled && !isAdmin() && helferpunkte.length > 10 && showMyPositions && user?.id) {
                    if (hp.caretaker_id !== user.id) {
                      return false;
                    }
                  }
                  return true;
                })
                .sort((a, b) => {
                let result = 0;
                if (sortBy === 'time') {
                  // Sort by date first, then by time, then by name
                  const dateA = a.einsatzdatum || '';
                  const dateB = b.einsatzdatum || '';
                  if (dateA !== dateB) {
                    result = dateA.localeCompare(dateB);
                  } else {
                    const timeA = a.einsatzuhrzeit || '';
                    const timeB = b.einsatzuhrzeit || '';
                    if (timeA !== timeB) {
                      result = timeA.localeCompare(timeB);
                    } else {
                      result = (a.einsatzort || '').localeCompare(b.einsatzort || '');
                    }
                  }
                } else {
                  // Sort by name (einsatzort)
                  result = (a.einsatzort || '').localeCompare(b.einsatzort || '');
                }
                // Apply sort order
                return sortOrder === 'asc' ? result : -result;
              }).map((hp) => (
                <div key={hp.id} data-helferpunkt-id={hp.id} className={`helferpunkt-item ${(hp.completed === 1 || hp.completed === true) ? 'helferpunkt-completed' : ''}`}>
                  <div className="helferpunkt-header">
                    <div className="helferpunkt-info">
                      <div className={`helferpunkt-time ${(hp.completed === 1 || hp.completed === true) ? 'helferpunkt-time-completed' : ''}`}>
                        {(() => {
                          const weekday = getWeekdayAbbreviation(hp.einsatzdatum);
                          const timeStr = `${hp.einsatzuhrzeit}${hp.einsatzuhrzeit_bis ? ` - ${hp.einsatzuhrzeit_bis}` : ''}`;
                          return weekday ? `${weekday} ${timeStr}` : timeStr;
                        })()}
                      </div>
                      <div className="helferpunkt-location">{hp.einsatzort}</div>
                      {betreuersEnabled && hp.caretaker_id && (
                        <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '2px' }}>
                          Betreuer: {caretakers.find(c => c.id === hp.caretaker_id)?.username || 'Unbekannt'}
                        </div>
                      )}
                    </div>
                    <div className="helferpunkt-actions">
                      <button
                        className={`helferpunkt-action-button ${
                          hp.required_assignees && 
                          (helferpunktHelpers[hp.id]?.length || 0) >= hp.required_assignees
                            ? 'helferpunkt-sufficient' 
                            : ''
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Don't allow collapsing if assignment dialog is open - user must use Save or Cancel
                          if (selectedHelferpunkt === hp.id && assigningHelpers === hp.id) {
                            return; // Block collapsing when assignment dialog is open
                          }
                          if (selectedHelferpunkt === hp.id) {
                            setSelectedHelferpunkt(null);
                          } else {
                            setSelectedHelferpunkt(hp.id);
                            fetchHelferpunktHelpers(hp.id);
                          }
                        }}
                        title="Helfer anzeigen/zuordnen"
                      >
                        {(() => {
                          // Check if any assigned helpers have time overlaps
                          const helpers = helferpunktHelpers[hp.id] || [];
                          const hasAnyTimeOverlap = helpers.some(helper => {
                            // Check conflicts across ALL Fachbereiche
                            const assignments = allHelperAssignments[helper.id] || [];
                            const otherAssignments = assignments.filter(a => a.id !== hp.id);
                            return otherAssignments.some(otherAssignment => 
                              checkDateTimeOverlap(
                                hp.einsatzdatum,
                                hp.einsatzuhrzeit,
                                hp.einsatzuhrzeit_bis || null,
                                otherAssignment.einsatzdatum,
                                otherAssignment.einsatzuhrzeit,
                                otherAssignment.einsatzuhrzeit_bis || null
                              )
                            );
                          });
                          
                          return (
                            <>
                              {hasAnyTimeOverlap && <span style={{ color: '#ed1c24', marginRight: '4px' }}>⚠️</span>}
                        👥 {helferpunktHelpers[hp.id]?.length || 0}
                        {hp.required_assignees && (
                          <span className="helferpunkt-required-count"> / {hp.required_assignees}</span>
                        )}
                            </>
                          );
                        })()}
                      </button>
                      {!readOnly && (
                      <button
                        className={`helferpunkt-action-button helferpunkt-completed-button ${
                          (hp.required_assignees && 
                           (helferpunktHelpers[hp.id]?.length || 0) >= hp.required_assignees)
                            ? 'sufficient' 
                            : ''
                        } ${(hp.completed === 1 || hp.completed === true) ? 'completed' : ''} ${
                          (hp.completed === 1 || hp.completed === true) && 
                          helferpunktHelpers[hp.id]?.some(helper => helper.rückmeldung !== 'Bestätigt')
                            ? 'completed-unconfirmed' 
                            : ''
                        }`}
                        disabled={(helferpunktHelpers[hp.id]?.length || 0) === 0 && !(hp.completed === 1 || hp.completed === true)}
                        onClick={(e) => {
                          // Prevent action if year is read-only
                          if (yearMaintenanceDisabled) {
                            return;
                          }
                          // Prevent action if no helpers assigned and not already completed
                          if ((helferpunktHelpers[hp.id]?.length || 0) === 0 && !(hp.completed === 1 || hp.completed === true)) {
                            return;
                          }
                          // Prevent default to avoid any scroll behavior
                          e.preventDefault();
                          e.stopPropagation();
                          
                          // Find the scrollable container (not window!)
                          const scrollableContainer = scrollableContainerRef.current || 
                            document.querySelector('.fachbereich-scrollable-wrapper') as HTMLElement;
                          
                          if (scrollableContainer) {
                            // Save scroll position on the actual scrollable container
                            const currentScroll = scrollableContainer.scrollTop;
                            scrollPositionRef.current = currentScroll;
                            clickedHelferpunktIdRef.current = hp.id;
                            
                            // Enable scroll lock
                            scrollLockRef.current = true;
                            
                            // Lock scroll position immediately on the container
                            scrollableContainer.style.scrollBehavior = 'auto';
                            scrollableContainer.scrollTop = currentScroll;
                            
                            // Set up a scroll lock interval to continuously maintain position
                            const lockInterval = setInterval(() => {
                              if (scrollLockRef.current && scrollableContainer) {
                                const currentPos = scrollableContainer.scrollTop;
                                if (Math.abs(currentPos - currentScroll) > 1) {
                                  scrollableContainer.scrollTop = currentScroll;
                                }
                              }
                            }, 16); // ~60fps
                            
                            // Clear lock after update completes
                            setTimeout(() => {
                              scrollLockRef.current = false;
                              clearInterval(lockInterval);
                              if (scrollableContainer) {
                                scrollableContainer.style.scrollBehavior = '';
                              }
                            }, 1500);
                          }
                          
                          handleToggleCompleted(hp.id, !(hp.completed === 1 || hp.completed === true));
                        }}
                        title={
                          yearMaintenanceDisabled
                            ? `Das Jahr ${year} ist schreibgeschützt`
                            : (helferpunktHelpers[hp.id]?.length || 0) === 0 && !(hp.completed === 1 || hp.completed === true)
                              ? 'Keine Helfer zugeordnet - Position kann nicht als erledigt markiert werden'
                              : hp.completed 
                                ? 'Als nicht erledigt markieren' 
                                : 'Als erledigt markieren'
                        }
                        style={{
                          opacity: yearMaintenanceDisabled || ((helferpunktHelpers[hp.id]?.length || 0) === 0 && !(hp.completed === 1 || hp.completed === true)) ? 0.5 : 1,
                          cursor: yearMaintenanceDisabled || ((helferpunktHelpers[hp.id]?.length || 0) === 0 && !(hp.completed === 1 || hp.completed === true)) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {(hp.completed === 1 || hp.completed === true) ? '✅' : '⬜'}
                      </button>
                      )}
                      {!readOnly && (
                      <div className="helferpunkt-kebab-container" style={{ position: 'relative' }}>
                        <button
                          className="helferpunkt-kebab"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenKebabMenuId(openKebabMenuId === hp.id ? null : hp.id);
                          }}
                          title="Optionen"
                        >
                          ⋮
                        </button>
                        {openKebabMenuId === hp.id && (
                          <div className="helferpunkt-kebab-menu">
                            <button
                              className="helferpunkt-kebab-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!yearMaintenanceDisabled) {
                                  startEdit(hp);
                                }
                                setOpenKebabMenuId(null);
                              }}
                              disabled={yearMaintenanceDisabled}
                              title={yearMaintenanceDisabled ? `Das Jahr ${year} ist schreibgeschützt` : ''}
                              style={{ opacity: yearMaintenanceDisabled ? 0.6 : 1, cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer' }}
                            >
                              ✏️ Bearbeiten
                            </button>
                            {(helferpunktHelpers[hp.id] || []).some(helper => helper.email && helper.email.trim() !== '') && (
                              <button
                                className="helferpunkt-kebab-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSendEmailToHelpers(hp);
                                  setOpenKebabMenuId(null);
                                }}
                              >
                                ✉️ Email schreiben
                              </button>
                            )}
                            <button
                              className="helferpunkt-kebab-item helferpunkt-kebab-item-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!yearMaintenanceDisabled) {
                                  handleDeleteHelferpunkt(hp.id);
                                }
                                setOpenKebabMenuId(null);
                              }}
                              disabled={yearMaintenanceDisabled}
                              title={yearMaintenanceDisabled ? `Das Jahr ${year} ist schreibgeschützt` : ''}
                              style={{ opacity: yearMaintenanceDisabled ? 0.6 : 1, cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer' }}
                            >
                              🗑️ Löschen
                            </button>
                          </div>
                        )}
                      </div>
                      )}
                    </div>
                  </div>
                  
                  {selectedHelferpunkt === hp.id && (
                    <>
                      {assigningHelpers === hp.id ? (
                        createPortal(
                          <div 
                            className="helper-assignment-overlay"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <div 
                              className="helper-assignment-overlay-inner"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <HelperAssignment
                          helferpunkt={hp}
                          allHelpers={allHelpers}
                          assignedHelpers={helferpunktHelpers[hp.id] || []}
                          helperAssignments={helperAssignments}
                          allHelperAssignments={allHelperAssignments}
                          onAssign={(helperIds) => {
                            if (!yearMaintenanceDisabled) {
                              handleAssignHelpers(hp.id, helperIds);
                            }
                          }}
                              onCancel={() => {
                                // HelperAssignment will handle change detection internally
                                // This is called when user explicitly cancels or when we need to force close
                                setAssigningHelpers(null);
                                setAssignmentHasChanges(false);
                              }}
                              onChangesDetected={(hasChanges) => {
                                setAssignmentHasChanges(hasChanges);
                              }}
                              parentShowCloseConfirmation={!!showCloseConfirmation}
                              betreuersEnabled={betreuersEnabled}
                              yearMaintenanceDisabled={yearMaintenanceDisabled}
                              raceDayDate={raceDayDate}
                              />
                            </div>
                          </div>,
                          document.body
                        )
                      ) : (
                        <div className="helferpunkt-helpers">
                          <div className="helpers-list">
                            {(helferpunktHelpers[hp.id] || []).length === 0 ? (
                              <div className="no-helpers">Noch keine Helfer zugeordnet</div>
                            ) : (
                              [...(helferpunktHelpers[hp.id] || [])]
                                .sort((a, b) => {
                                  // Sort alphabetically by surname, then first name
                                  const surnameA = (a.surname || '').toLowerCase();
                                  const surnameB = (b.surname || '').toLowerCase();
                                  if (surnameA !== surnameB) {
                                    return surnameA.localeCompare(surnameB, 'de');
                                  }
                                  const firstNameA = (a.first_name || '').toLowerCase();
                                  const firstNameB = (b.first_name || '').toLowerCase();
                                  return firstNameA.localeCompare(firstNameB, 'de');
                                })
                                .map((helper) => {
                                // Check for time overlaps with other assignments across ALL Fachbereiche
                                const assignments = allHelperAssignments[helper.id] || [];
                                const otherAssignments = assignments.filter(a => a.id !== hp.id);
                                const hasTimeOverlap = otherAssignments.some(otherAssignment => 
                                  checkDateTimeOverlap(
                                    hp.einsatzdatum,
                                    hp.einsatzuhrzeit,
                                    hp.einsatzuhrzeit_bis || null,
                                    otherAssignment.einsatzdatum,
                                    otherAssignment.einsatzuhrzeit,
                                    otherAssignment.einsatzuhrzeit_bis || null
                                  )
                                );
                                
                                const isPositionCompleted = hp.completed === 1 || hp.completed === true;
                                const fachbereichFeedback = helper.feedback?.find((f: HelperFeedback) => f.fachbereich === hp.fachbereich) as HelperFeedback | undefined;
                                const rueckmeldung = (fachbereichFeedback?.rückmeldung ?? helper.rückmeldung ?? null) || null;
                                const hasUnconfirmedRueckmeldung = rueckmeldung && rueckmeldung !== 'Bestätigt';
                                const hasMissingRueckmeldung = !rueckmeldung || String(rueckmeldung).trim() === '';
                                const caretakerId = fachbereichFeedback?.caretaker_id ?? helper.caretaker_id ?? null;
                                const caretakerName = caretakerId != null && caretakers.length > 0 ? (caretakers.find(c => c.id === caretakerId)?.username ?? '') : '';
                                
                                return (
                                <div key={helper.id} className="helper-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '1 1 auto', minWidth: 0 }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                                      {hasTimeOverlap && <span style={{ color: '#ed1c24', marginRight: '4px' }}>⚠️</span>}
                                  <strong>{formatHelperDisplayName(helper.first_name, helper.surname)}</strong>
                                  {helper.email && (
                                    <span className="helper-email">
                                            {' '}<a href={`mailto:${helper.email}`} className="helper-email-link" onClick={(e) => e.stopPropagation()}>{helper.email}</a>
                                    </span>
                                  )}
                                  {helper.phone && (
                                          <span className="helper-phone"> 📞 {helper.phone}</span>
                                        )}
                                      </div>
                                      {/* Always show Rückmeldung so user knows which helper to adjust */}
                                      <div style={{ 
                                        fontSize: '0.85rem', 
                                        color: hasMissingRueckmeldung ? '#ed1c24' : '#666',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '2px',
                                        marginTop: '2px'
                                      }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                          {(hasMissingRueckmeldung || hasUnconfirmedRueckmeldung) && (
                                            <span style={{ color: '#ed1c24', fontWeight: 'bold' }}>⚠️</span>
                                          )}
                                          <span style={{ fontStyle: 'italic' }}>
                                            {hasMissingRueckmeldung
                                              ? 'Rückmeldung fehlt'
                                              : `Rückmeldung: ${rueckmeldung}`
                                            }
                                          </span>
                                        </div>
                                        {betreuersEnabled && (caretakerName ? (
                                          <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '1px' }}>
                                            Betreuer: {caretakerName}
                                          </div>
                                        ) : (
                                          <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '1px', fontStyle: 'italic' }}>
                                            Kein Betreuer
                                          </div>
                                        ))}
                                        {((fachbereichFeedback?.bemerkung ?? helper.bemerkung) || '').trim() && (
                                          <div style={{ 
                                            fontSize: '0.85rem', 
                                            color: '#666',
                                            marginLeft: '0',
                                            fontStyle: 'italic'
                                          }}>
                                            Bemerkung: {(fachbereichFeedback?.bemerkung ?? helper.bemerkung) || ''}
                                          </div>
                                        )}
                                      </div>
                                </div>
                                    <div style={{ display: 'flex', gap: '4px', alignItems: 'flex-start' }}>
                                      {helper.email && !readOnly && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleSendEmailToHelper(helper, hp);
                                          }}
                                          style={{
                                            background: 'transparent',
                                            border: '1px solid #ed1c24',
                                            color: '#ed1c24',
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '0',
                                            fontSize: '1.2rem',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: 0,
                                            flexShrink: 0,
                                            transition: 'all 0.3s ease',
                                            alignSelf: 'flex-start',
                                            marginTop: '2px'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#ed1c24';
                                            e.currentTarget.style.color = 'white';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                            e.currentTarget.style.color = '#ed1c24';
                                          }}
                                          title="E-Mail an Helfer senden"
                                        >
                                          ✉️
                                        </button>
                                      )}
                                      {!isAdmin() && !readOnly && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (!yearMaintenanceDisabled) {
                                              const firstName = (helper.first_name || '').trim();
                                              const surname = (helper.surname || '').trim();
                                              const helperName = [firstName, surname].filter(Boolean).join(' ');
                                              setInitialHelperSearchQuery(helperName);
                                              // Always fetch fresh data before opening - this ensures rückmeldung/bemerkung are loaded
                                              fetchAllHelpers();
                                              // Open dialog - useEffect will handle displaying data when it arrives
                                              setShowHelperList(true);
                                            }
                                          }}
                                          disabled={yearMaintenanceDisabled}
                                          style={{
                                            background: 'transparent',
                                            border: '1px solid #0066cc',
                                            color: '#0066cc',
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '0',
                                            fontSize: '1.2rem',
                                            cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: 0,
                                            flexShrink: 0,
                                            transition: 'all 0.3s ease',
                                            alignSelf: 'flex-start',
                                            marginTop: '2px',
                                            opacity: yearMaintenanceDisabled ? 0.6 : 1
                                          }}
                                          onMouseEnter={(e) => {
                                            if (!yearMaintenanceDisabled) {
                                              e.currentTarget.style.backgroundColor = '#0066cc';
                                              e.currentTarget.style.color = 'white';
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            if (!yearMaintenanceDisabled) {
                                              e.currentTarget.style.backgroundColor = 'transparent';
                                              e.currentTarget.style.color = '#0066cc';
                                            }
                                          }}
                                          title={yearMaintenanceDisabled ? `Das Jahr ${year} ist schreibgeschützt - Rückmeldungen können nicht geändert werden` : "Rückmeldung verwalten"}
                                        >
                                          📝
                                        </button>
                                      )}
                                    </div>
                                </div>
                                );
                              })
                            )}
                          </div>
                          {!readOnly && (
                          <button
                            className="assign-helpers-button"
                            onClick={() => {
                              if (!yearMaintenanceDisabled) {
                                setAssigningHelpers(hp.id);
                              }
                            }}
                            disabled={yearMaintenanceDisabled}
                            title={yearMaintenanceDisabled ? `Das Jahr ${year} ist schreibgeschützt - Helferzuordnungen können nicht geändert werden` : 'Helfer zuordnen'}
                            style={{ opacity: yearMaintenanceDisabled ? 0.6 : 1, cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer' }}
                          >
                            Helfer zuordnen
                          </button>
                          )}
                        </div>
                      )}
                        </>
                  )}
                </div>
              ))}
            </div>
            </div>
          )}
        </div>

        {showHelperList && !isAdmin() && (
          <FachbereichHelperList
            fachbereich={fachbereich}
            helpers={allHelpers}
            initialSearchQuery={initialHelperSearchQuery}
            compactMode={!!initialHelperSearchQuery}
            allHelperAssignments={allHelperAssignments}
            defaultToNewHelperFilters={defaultToNewHelperFilters}
            onClose={() => {
              setShowHelperList(false);
              const wasCompactMode = !!initialHelperSearchQuery;
              setInitialHelperSearchQuery('');
              // Refresh helpers when closing
              fetchAllHelpers();
              if (wasCompactMode) {
                helferpunkte.forEach(hp => {
                  fetchHelferpunktHelpers(hp.id);
                });
              }
            }}
            onUpdate={fetchAllHelpers}
            yearMaintenanceDisabled={yearMaintenanceDisabled}
            raceDayDate={raceDayDate}
            readOnly={readOnly}
          />
        )}
      </div>
      
      {/* Admin Warning Dialog for Editing Position */}
      {showAdminWarningEdit && (
        <div className="fachbereich-confirmation-overlay" onClick={(e) => {
          // Don't close when clicking outside - user must choose an option
          e.stopPropagation();
        }}>
          <div className="fachbereich-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Hinweis für Administratoren</h3>
            <p>Die Bearbeitung von Positionen ist normalerweise Aufgabe der Fachbereich Manager oder Fachbereich Administratoren. Als Administrator solltest du diese Aufgabe den zuständigen Fachbereich Managern/Administratoren überlassen.</p>
            <p style={{ marginTop: '15px', fontWeight: 'bold' }}>Möchtest du trotzdem fortfahren?</p>
            <div className="fachbereich-confirmation-buttons">
              <button
                className="fachbereich-button submit-button"
                onClick={() => {
                  setShowAdminWarningEdit(false);
                  if (pendingEditAction) {
                    pendingEditAction();
                    setPendingEditAction(null);
                  }
                }}
              >
                Trotzdem fortfahren
              </button>
              <button
                className="fachbereich-button cancel-button"
                onClick={() => {
                  setShowAdminWarningEdit(false);
                  setPendingEditAction(null);
                }}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Admin Warning Dialog for Marking Position as Completed */}
      {showAdminWarningCompleted && pendingCompletedAction && (
        <div className="fachbereich-confirmation-overlay" onClick={(e) => {
          // Don't close when clicking outside - user must choose an option
          e.stopPropagation();
        }}>
          <div className="fachbereich-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Hinweis für Administratoren</h3>
            <p>Das Markieren von Positionen als erledigt ist normalerweise Aufgabe der Fachbereich Manager oder Fachbereich Administratoren. Als Administrator solltest du diese Aufgabe den zuständigen Fachbereich Managern/Administratoren überlassen.</p>
            <p style={{ marginTop: '15px', fontWeight: 'bold' }}>Möchtest du trotzdem fortfahren?</p>
            <div className="fachbereich-confirmation-buttons">
              <button
                className="fachbereich-button submit-button"
                onClick={() => {
                  setShowAdminWarningCompleted(false);
                  if (pendingCompletedAction) {
                    performToggleCompleted(pendingCompletedAction.id, pendingCompletedAction.completed);
                    setPendingCompletedAction(null);
                  }
                }}
              >
                Trotzdem fortfahren
              </button>
              <button
                className="fachbereich-button cancel-button"
                onClick={() => {
                  setShowAdminWarningCompleted(false);
                  setPendingCompletedAction(null);
                }}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Rückmeldung Warning Dialog for Marking Position as Completed */}
      {showRueckmeldungWarning && pendingCompletedAction && (
        <div className="fachbereich-confirmation-overlay" onClick={(e) => {
          // Don't close when clicking outside - user must choose an option
          e.stopPropagation();
        }}>
          <div className="fachbereich-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Rückmeldung nicht bestätigt</h3>
            {(() => {
              const helpers = helferpunktHelpers[pendingCompletedAction.id] || [];
              const helpersWithoutBestätigt = helpers.filter(helper => 
                !helper.rückmeldung || helper.rückmeldung !== 'Bestätigt'
              );
              const missingCount = helpers.filter(helper => 
                !helper.rückmeldung || helper.rückmeldung === ''
              ).length;
              const unconfirmedCount = helpersWithoutBestätigt.length - missingCount;
              
              return (
                <>
                  <p>
                    Nicht alle zugeordneten Helfer haben eine "Bestätigt" Rückmeldung:
                  </p>
                  <ul style={{ marginTop: '10px', marginBottom: '15px', paddingLeft: '20px' }}>
                    {missingCount > 0 && (
                      <li>{missingCount} {missingCount === 1 ? 'Helfer hat' : 'Helfer haben'} keine Rückmeldung</li>
                    )}
                    {unconfirmedCount > 0 && (
                      <li>{unconfirmedCount} {unconfirmedCount === 1 ? 'Helfer hat' : 'Helfer haben'} eine andere Rückmeldung als "Bestätigt"</li>
                    )}
                  </ul>
                  <p style={{ marginTop: '15px', fontWeight: 'bold' }}>
                    Möchtest du die Position trotzdem als erledigt markieren?
                  </p>
                </>
              );
            })()}
            <div className="fachbereich-confirmation-buttons">
              <button
                className="fachbereich-button submit-button"
                onClick={() => {
                  setShowRueckmeldungWarning(false);
                  if (pendingCompletedAction) {
                    performToggleCompleted(pendingCompletedAction.id, pendingCompletedAction.completed);
                    setPendingCompletedAction(null);
                  }
                }}
              >
                Trotzdem als erledigt
                <br />
                markieren
              </button>
              <button
                className="fachbereich-button cancel-button"
                onClick={() => {
                  setShowRueckmeldungWarning(false);
                  setPendingCompletedAction(null);
                }}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Confirmation Dialog - portaled to body so it appears above helferpunkt-form-overlay */}
      {showCloseConfirmation && createPortal(
        <div className="fachbereich-confirmation-overlay" onClick={(e) => {
          // Don't close when clicking outside - user must choose an option
          e.stopPropagation();
        }}>
          <div className="fachbereich-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Ungespeicherte Änderungen</h3>
            <p>Sie haben ungespeicherte Änderungen. Was möchten Sie tun?</p>
            <div className="fachbereich-confirmation-buttons">
              <button
                className="fachbereich-button submit-button"
                onClick={showCloseConfirmation.onSaveAndClose}
              >
                Speichern und schließen
              </button>
              <button
                className="fachbereich-button cancel-button"
                onClick={showCloseConfirmation.onDiscardAndClose}
              >
                Ohne Speichern schließen
              </button>
              <button
                className="fachbereich-button"
                onClick={() => setShowCloseConfirmation(null)}
                style={{ background: 'transparent', border: '2px solid #666', color: '#666' }}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Helferpunkt create/edit form in dedicated overlay dialog */}
      {(showCreateForm || editingHelferpunkt) && createPortal(
        <div className="helferpunkt-form-overlay">
          <div className="helferpunkt-form-overlay-inner" onClick={(e) => e.stopPropagation()}>
            <div className="helferpunkt-form-dialog-header">
              <h3>{editingHelferpunkt ? 'Helferpunkt bearbeiten' : 'Neuen Helferpunkt erstellen'}</h3>
              <button className="fachbereich-close" onClick={cancelEdit} aria-label="Schließen">×</button>
            </div>
            <div className="fachbereich-form">
              {yearMaintenanceDisabled && !readOnly && (
                <div className="fachbereich-error" style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107', color: '#856404', marginBottom: '16px' }}>
                  ⚠️ Das Jahr {year} ist schreibgeschützt. Änderungen können nicht gespeichert werden.
                </div>
              )}
              <form onSubmit={editingHelferpunkt ? handleUpdateHelferpunkt : handleCreateHelferpunkt}>
                <div className="form-group">
                  <label htmlFor="einsatzort">Einsatzort (Helferpunkt) *</label>
                  <input
                    id="einsatzort"
                    type="text"
                    value={einsatzort}
                    onChange={(e) => {
                      if (!yearMaintenanceDisabled) {
                        setEinsatzort(e.target.value);
                        e.target.setCustomValidity('');
                      }
                    }}
                    onInvalid={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.setCustomValidity('Bitte füllen Sie dieses Feld aus');
                    }}
                    maxLength={100}
                    required
                    disabled={yearMaintenanceDisabled}
                    placeholder="z.B. Start/Ziel, Wechselzone 1, etc."
                  />
                  <small style={{ display: 'block', marginTop: '-4px', color: '#666', fontSize: '0.85rem' }}>{einsatzort.length}/100 Zeichen</small>
                </div>
                <div className="form-group">
                  <label htmlFor="einsatzdatum">Einsatzdatum *</label>
                  <input
                    id="einsatzdatum"
                    type="date"
                    value={einsatzdatum}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setEinsatzdatum(newValue);
                      e.target.setCustomValidity('');
                    }}
                    onInvalid={(e) => {
                      const target = e.target as HTMLInputElement;
                      if (target.validity.valueMissing) {
                        target.setCustomValidity('Bitte füllen Sie dieses Feld aus');
                      }
                    }}
                    required
                  />
                  <small style={{ display: 'block', marginTop: '-4px', color: '#666', fontSize: '0.85rem' }}>Format: tt.mm.jjjj</small>
                </div>
                <div className="form-group">
                  <label htmlFor="einsatzuhrzeit">Einsatzuhrzeit (Von) *</label>
                  <input
                    id="einsatzuhrzeit"
                    type="text"
                    value={einsatzuhrzeit}
                    onChange={(e) => {
                      setEinsatzuhrzeit(e.target.value);
                      e.target.setCustomValidity('');
                    }}
                    onInvalid={(e) => {
                      const target = e.target as HTMLInputElement;
                      if (target.validity.valueMissing) {
                        target.setCustomValidity('Bitte füllen Sie dieses Feld aus');
                      } else if (target.validity.patternMismatch) {
                        target.setCustomValidity('Bitte geben Sie eine gültige Zeit ein (Format: hh:mm, z.B. 08:30)');
                      }
                    }}
                    required
                    placeholder="hh:mm (z.B. 08:30)"
                    pattern="([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]"
                  />
                  <small style={{ display: 'block', marginTop: '-4px', color: '#666', fontSize: '0.85rem' }}>Format: hh:mm (z.B. 08:30)</small>
                </div>
                <div className="form-group">
                  <label htmlFor="einsatzuhrzeitBis">Einsatzuhrzeit (Bis) *</label>
                  <input
                    id="einsatzuhrzeitBis"
                    type="text"
                    value={einsatzuhrzeitBis}
                    required
                    pattern="^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$"
                    onChange={(e) => {
                      setEinsatzuhrzeitBis(e.target.value);
                      e.target.setCustomValidity('');
                    }}
                    onInvalid={(e) => {
                      const target = e.target as HTMLInputElement;
                      if (target.validity.valueMissing) {
                        target.setCustomValidity('Bitte füllen Sie dieses Feld aus');
                      } else if (target.validity.patternMismatch) {
                        target.setCustomValidity('Bitte geben Sie eine gültige Zeit ein (Format: hh:mm, z.B. 17:00)');
                      }
                    }}
                    placeholder="hh:mm (z.B. 17:00)"
                  />
                  <small style={{ display: 'block', marginTop: '-4px', color: '#666', fontSize: '0.85rem' }}>Format: hh:mm (z.B. 17:00)</small>
                </div>
                <div className="form-group">
                  <label htmlFor="requiredAssignees">Anzahl benötigter Helfer *</label>
                  <input
                    id="requiredAssignees"
                    type="number"
                    min="1"
                    value={requiredAssignees}
                    onChange={(e) => {
                      setRequiredAssignees(e.target.value);
                      e.target.setCustomValidity('');
                    }}
                    onInvalid={(e) => {
                      const target = e.target as HTMLInputElement;
                      if (target.validity.valueMissing) {
                        target.setCustomValidity('Bitte füllen Sie dieses Feld aus');
                      } else if (target.validity.rangeUnderflow) {
                        target.setCustomValidity('Bitte geben Sie eine Zahl größer oder gleich 1 ein');
                      }
                    }}
                    required
                    placeholder="z.B. 3"
                  />
                  <small style={{ display: 'block', marginTop: '-4px', color: '#666', fontSize: '0.85rem' }}>Anzahl der Helfer, die für diesen Einsatzort benötigt werden (mindestens 1)</small>
                </div>
                <div className="form-group">
                  <label htmlFor="einsatzinformationen">Einsatzinformationen</label>
                  <textarea
                    id="einsatzinformationen"
                    value={einsatzinformationen}
                    onChange={(e) => {
                      if (e.target.value.length <= 2000) {
                        setEinsatzinformationen(e.target.value);
                      }
                    }}
                    maxLength={2000}
                    rows={5}
                    placeholder="Zusätzliche Informationen für diesen Einsatz (optional, max. 2000 Zeichen)"
                    style={{
                      width: '100%',
                      padding: '8px',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit',
                      fontSize: 'inherit',
                      resize: 'vertical',
                      minHeight: '100px'
                    }}
                  />
                  <small style={{ display: 'block', marginTop: '-4px', color: '#666' }}>
                    {einsatzinformationen.length}/2000 Zeichen
                  </small>
                </div>
                {betreuersEnabled && (
                  <div className="form-group">
                    <label htmlFor="caretakerId">Betreuer</label>
                    <select
                      id="caretakerId"
                      value={caretakerId}
                      onChange={(e) => setCaretakerId(e.target.value)}
                      style={{ padding: '8px', fontSize: '1rem', width: '100%' }}
                    >
                      <option value="">-- Keine Auswahl --</option>
                      {caretakers.map((caretaker) => (
                        <option key={caretaker.id} value={caretaker.id.toString()}>
                          {caretaker.username}
                        </option>
                      ))}
                    </select>
                    <small style={{ display: 'block', marginTop: '4px', color: '#666', fontSize: '0.85rem' }}>Optional: Betreuer für diese Position zuordnen</small>
                  </div>
                )}
                <div className="form-actions">
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={yearMaintenanceDisabled}
                    title={yearMaintenanceDisabled ? `Das Jahr ${year} ist schreibgeschützt` : ''}
                    style={{ opacity: yearMaintenanceDisabled ? 0.6 : 1, cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer' }}
                  >
                    {editingHelferpunkt ? 'Speichern' : 'Erstellen'}
                  </button>
                  <button type="button" className="cancel-button" onClick={cancelEdit}>
                    Abbrechen
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

interface HelperAssignmentProps {
  helferpunkt: Helferpunkt;
  allHelpers: Helper[];
  assignedHelpers: Helper[];
  helperAssignments: { [key: number]: Helferpunkt[] };
  onAssign: (helperIds: number[]) => void;
  onCancel: () => void;
  onChangesDetected?: (hasChanges: boolean) => void;
  parentShowCloseConfirmation?: boolean;
  betreuersEnabled?: boolean;
  yearMaintenanceDisabled?: boolean;
  raceDayDate?: string | null; // Race day date to filter helpers who don't help on race day
}

// Helper function to check if two date+time ranges overlap
// Only checks time overlap if dates are the same (or both undefined for backward compatibility)
const checkDateTimeOverlap = (date1: string | undefined, time1Start: string, time1End: string | null, date2: string | undefined, time2Start: string, time2End: string | null): boolean => {
  if (!time1Start || !time2Start) return false;
  
  // CRITICAL: If dates are provided and different, no overlap (positions on different days never conflict)
  if (date1 && date2 && date1 !== date2) return false;
  
  // If only one date is provided, we can't determine if they're on the same day
  // For safety, assume no overlap (conservative approach)
  if ((date1 && !date2) || (!date1 && date2)) {
    return false;
  }
  
  // If both dates are undefined (legacy data), check time overlap for backward compatibility
  // If both dates are the same, proceed to check time overlap
  
  try {
    // Convert time strings (hh:mm) to minutes since midnight
    const timeToMinutes = (time: string): number => {
      const [hours, minutes] = time.split(':').map(Number);
      return hours * 60 + minutes;
    };
    
    const start1 = timeToMinutes(time1Start);
    const end1 = time1End ? timeToMinutes(time1End) : start1 + 60; // Default to 1 hour if no end time
    const start2 = timeToMinutes(time2Start);
    const end2 = time2End ? timeToMinutes(time2End) : start2 + 60; // Default to 1 hour if no end time
    
    // Handle case where times span midnight (e.g., 22:00 to 02:00)
    // For simplicity, we'll check if ranges overlap within the same day
    // If end < start, it means it wraps around midnight
    const end1Adjusted = end1 < start1 ? end1 + 24 * 60 : end1;
    const end2Adjusted = end2 < start2 ? end2 + 24 * 60 : end2;
    
    // Check for overlap: ranges overlap if start1 < end2 AND start2 < end1
    return start1 < end2Adjusted && start2 < end1Adjusted;
  } catch (error) {
    return false;
  }
};

const HelperAssignment: React.FC<HelperAssignmentProps & { allHelperAssignments?: { [key: number]: Helferpunkt[] } }> = ({
  helferpunkt,
  allHelpers,
  assignedHelpers,
  helperAssignments,
  allHelperAssignments: globalHelperAssignments = {},
  onAssign,
  onCancel,
  onChangesDetected,
  parentShowCloseConfirmation = false,
  betreuersEnabled = false,
  yearMaintenanceDisabled = false,
  raceDayDate = null
}) => {
  const { isAdmin, user } = useAuth();
  const [selectedHelperIds, setSelectedHelperIds] = useState<number[]>(
    assignedHelpers.map(h => h.id)
  );
  const [timeConflictWarnings, setTimeConflictWarnings] = useState<{ [helperId: number]: string[] }>({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showAdminWarning, setShowAdminWarning] = useState(false);
  const [showAvailableHelpers, setShowAvailableHelpers] = useState<boolean>(true); // true = Zeitlich verfügbare Helfer, false = Zugeordnete Helfer
  const [hidePlannedHelpers, setHidePlannedHelpers] = useState<boolean>(false); // true = Verplante Helfer ausgeblendet, false = Verplante Helfer sichtbar
  const [showMyHelfer, setShowMyHelfer] = useState<boolean>(true); // true = Meine betreuten Helfer, false = Helfer ungeachtet der Betreuer (only when betreuersEnabled)
  const [helperSearchQuery, setHelperSearchQuery] = useState<string>('');
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(false); // Mobile: filters collapsed by default to show more helpers
  
  // Keep hidePlannedHelpers off when showAvailableHelpers is false (disabled state)
  useEffect(() => {
    if (!showAvailableHelpers) {
      setHidePlannedHelpers(false);
    }
  }, [showAvailableHelpers]);
  
  // Check if assignments have changed
  const hasChanges = (): boolean => {
    const initialIds = new Set(assignedHelpers.map(h => h.id));
    const currentIds = new Set(selectedHelperIds);
    if (initialIds.size !== currentIds.size) return true;
    // Convert Set to Array for iteration compatibility
    const initialIdsArray = Array.from(initialIds);
    for (const id of initialIdsArray) {
      if (!currentIds.has(id)) return true;
    }
    return false;
  };

  // Notify parent of change status
  useEffect(() => {
    if (onChangesDetected) {
      const hasChangesValue = hasChanges();
      onChangesDetected(hasChangesValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHelperIds, assignedHelpers]);
  
  const handleCancel = () => {
    if (hasChanges()) {
      setShowConfirmation(true);
    } else {
      onCancel();
    }
  };

  // Handle Escape key to check for changes
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // If parent's confirmation dialog is open, let parent handle it
        if (parentShowCloseConfirmation) {
          return; // Don't handle Escape here - let parent component handle it
        }
        // If admin warning dialog is open, close it (cancel)
        if (showAdminWarning) {
          setShowAdminWarning(false);
          return;
        }
        // If confirmation dialog is open, close it (cancel)
        if (showConfirmation) {
          setShowConfirmation(false);
          return;
        }
        // Check for changes before closing
        handleCancel();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- handleCancel is stable
  }, [showConfirmation, showAdminWarning, selectedHelperIds, assignedHelpers, onCancel, parentShowCloseConfirmation]);

  const checkForTimeConflicts = (helperId: number, currentSelectedIds: number[]): string[] => {
    const assignments = helperAssignments[helperId] || [];
    const conflicts: string[] = [];
    
    assignments.forEach((assignment) => {
      if (assignment.id !== helferpunkt.id) {
        if (checkDateTimeOverlap(
          helferpunkt.einsatzdatum,
          helferpunkt.einsatzuhrzeit,
          helferpunkt.einsatzuhrzeit_bis || null,
          assignment.einsatzdatum,
          assignment.einsatzuhrzeit,
          assignment.einsatzuhrzeit_bis || null
        )) {
          const weekday = getWeekdayAbbreviation(assignment.einsatzdatum);
          const timeStr = `${assignment.einsatzuhrzeit}${assignment.einsatzuhrzeit_bis ? ` - ${assignment.einsatzuhrzeit_bis}` : ''}`;
          const timeDisplay = weekday ? `${weekday} ${timeStr}` : timeStr;
          // Include fachbereich name if different from current fachbereich
          const fachbereichPrefix = assignment.fachbereich && assignment.fachbereich !== helferpunkt.fachbereich 
            ? `${assignment.fachbereich} - ` 
            : '';
          conflicts.push(`${fachbereichPrefix}${assignment.einsatzort} (${timeDisplay})`);
        }
      }
    });
    
    return conflicts;
  };

  const handleToggleHelper = (helperId: number) => {
    setSelectedHelperIds(prev => {
      const isAdding = !prev.includes(helperId);
      
      // If adding a helper, check if they already have assignments and are not willing to do multiple Einsätze - show warning but allow
      if (isAdding) {
        const helper = allHelpers.find(h => h.id === helperId);
        const existingAssignments = helperAssignments[helperId] || [];
        const otherAssignments = existingAssignments.filter(a => a.id !== helferpunkt.id);
        
        if (helper && otherAssignments.length > 0 && convertBereitFuerMehrereEinsaetze(helper.bereit_fuer_mehrere_einsaetze) !== 'Ja') {
          const assignmentText = otherAssignments
            .map(a => {
              const weekday = getWeekdayAbbreviation(a.einsatzdatum);
              const timeStr = `${a.einsatzuhrzeit}${a.einsatzuhrzeit_bis ? ` - ${a.einsatzuhrzeit_bis}` : ''}`;
              const timeDisplay = weekday ? `${weekday} ${timeStr}` : timeStr;
              return `${a.einsatzort} (${timeDisplay})`;
            })
            .join(', ');
          const confirmMessage = `⚠️ Warnung: Dieser Helfer ist bereits anderen Positionen zugeordnet (${assignmentText}) und nicht bereit für mehrere Einsätze.\n\nMöchtest du trotzdem fortfahren?`;
          if (!window.confirm(confirmMessage)) {
            return prev; // Don't add the helper
          }
        }
      }
      
      const newSelectedIds = isAdding
        ? [...prev, helperId]
        : prev.filter(id => id !== helperId);
      
      // Check for time conflicts when adding a helper
      if (isAdding) {
        const conflicts = checkForTimeConflicts(helperId, newSelectedIds);
        if (conflicts.length > 0) {
          setTimeConflictWarnings(prevWarnings => ({
            ...prevWarnings,
            [helperId]: conflicts
          }));
        } else {
          setTimeConflictWarnings(prevWarnings => {
            const newWarnings = { ...prevWarnings };
            delete newWarnings[helperId];
            return newWarnings;
          });
        }
      } else {
        // Remove warning when unchecking
        setTimeConflictWarnings(prevWarnings => {
          const newWarnings = { ...prevWarnings };
          delete newWarnings[helperId];
          return newWarnings;
        });
      }
      
      return newSelectedIds;
    });
  };

  const handleSave = () => {
    // Check if user is Admin (not Fachbereich Manager or Fachbereich Administrator)
    // Show warning that this is typically the work of Fachbereich Managers/Administrators
    if (isAdmin() && user?.role === 'Administrator') {
      setShowAdminWarning(true);
      return;
    }
    
    // Check if there are any warnings and confirm with user
    const hasWarnings = Object.keys(timeConflictWarnings).length > 0;
    if (hasWarnings) {
      const warningCount = Object.keys(timeConflictWarnings).length;
      const confirmMessage = `Warnung: ${warningCount} Helfer ${warningCount === 1 ? 'ist' : 'sind'} bereits zeitgleich anderen Positionen zugeordnet. Möchtest du trotzdem fortfahren?`;
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }
    onAssign(selectedHelperIds);
  };

  const handleAdminWarningConfirm = () => {
    setShowAdminWarning(false);
    // Check if there are any warnings and confirm with user
    const hasWarnings = Object.keys(timeConflictWarnings).length > 0;
    if (hasWarnings) {
      const warningCount = Object.keys(timeConflictWarnings).length;
      const confirmMessage = `Warnung: ${warningCount} Helfer ${warningCount === 1 ? 'ist' : 'sind'} bereits zeitgleich anderen Positionen zugeordnet. Möchtest du trotzdem fortfahren?`;
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }
    onAssign(selectedHelperIds);
  };

  // Filter and sort helpers based on filter switches
  const assignedHelperIds = new Set(assignedHelpers.map(h => h.id));
  const selectedHelperIdsSet = new Set(selectedHelperIds);
  
  // First filter helpers based on switches
  let filteredHelpers = [...allHelpers].filter((helper) => {
    // CRITICAL: Only show helpers assigned to the current Fachbereich
    if (helper.fachbereiche && Array.isArray(helper.fachbereiche)) {
      if (!helper.fachbereiche.includes(helferpunkt.fachbereich)) {
        return false; // Filter out helpers not assigned to this Fachbereich
      }
    } else {
      return false; // No fachbereiche array means helper is not assigned to any Fachbereich
    }
    
    // Exclude helpers with "Passt nicht" for this Fachbereich - they shall not be shown at all
    const fachbereichFeedback = helper.feedback?.find((fb: { fachbereich?: string; rückmeldung?: string | null }) => fb.fachbereich === helferpunkt.fachbereich);
    const rueckmeldung = fachbereichFeedback?.rückmeldung ?? helper.rückmeldung ?? null;
    if (rueckmeldung === 'Passt nicht') {
      return false; // Never show Passt nicht helpers in position assignment
    }
    
    const isAssigned = assignedHelperIds.has(helper.id);
    const isCurrentlySelected = selectedHelperIdsSet.has(helper.id);
    const hasOtherAssignments = (helperAssignments[helper.id] || []).filter(a => a.id !== helferpunkt.id).length > 0;
    
    // Filter out helpers who don't help on race day if the position is on the race day
    // Only filter if helper is not already assigned (allow keeping existing assignments)
    if (!isAssigned && !isCurrentlySelected && helferpunkt.einsatzdatum && raceDayDate) {
      // Check if position date matches race day date
      if (helferpunkt.einsatzdatum === raceDayDate) {
        // Position is on race day - filter out helpers who don't help on race day
        if (helper.hilft_nicht_am_wettkampftag === 1) {
          return false; // Filter out helpers who don't help on race day
        }
      }
    }
    
    // Filter by Betreuer (Meine betreuten Helfer vs. Helfer ungeachtet der Betreuer) - only if betreuersEnabled and not admin
    if (betreuersEnabled && !isAdmin() && showMyHelfer && user?.id) {
      // Get caretaker_id from helper.feedback for this Fachbereich (API returns it in feedback array)
      const fachbereichFeedback = helper.feedback?.find((fb: { fachbereich?: string }) => fb.fachbereich === helferpunkt.fachbereich);
      const helperCaretakerId = fachbereichFeedback?.caretaker_id ?? helper.caretaker_id;
      if (helperCaretakerId !== user.id) {
        return false; // Filter out helpers not assigned to current user
      }
    }
    
    // Filter by Zeitlich verfügbare Helfer vs. Zugeordnete Helfer
    if (showAvailableHelpers) {
      // Check if helper has time conflicts with the position being assigned
      // Use ALL assignments across ALL Fachbereiche for global conflict detection
      const assignments = globalHelperAssignments[helper.id] || [];
      const hasTimeConflict = assignments.some((assignment) => {
        if (assignment.id === helferpunkt.id) return false; // Don't count the current position
        return checkDateTimeOverlap(
          helferpunkt.einsatzdatum,
          helferpunkt.einsatzuhrzeit,
          helferpunkt.einsatzuhrzeit_bis || null,
          assignment.einsatzdatum,
          assignment.einsatzuhrzeit,
          assignment.einsatzuhrzeit_bis || null
        );
      });
      
      // Show only helpers without time conflicts (or already assigned/selected helpers)
      // Always show helpers that are already assigned or currently selected, even if they have conflicts
      if (!isAssigned && !isCurrentlySelected && hasTimeConflict) {
        return false; // Hide helpers with time conflicts
      }
      
      // Filter by Verplante Helfer ausgeblendet/sichtbar (only applies to available helpers)
      if (hidePlannedHelpers && hasOtherAssignments && !isAssigned && !isCurrentlySelected) {
        return false; // Hide available helpers with other assignments, but keep assigned/selected helpers visible
      }
    } else {
      // Show only helpers that are assigned to this position (including currently selected but not yet saved)
      // When showing "Zugeordnete Helfer", show ALL assigned helpers regardless of other assignments
      // Also include helpers that are currently selected in the frontend but not yet saved
      if (!isAssigned && !isCurrentlySelected) return false;
    }
    
    // Apply search filter
    if (helperSearchQuery.trim()) {
      if (!helperMatchesSearch(helperSearchQuery, helper.first_name, helper.surname, helper.email)) {
        return false;
      }
    }
    
    return true;
  });
  
  // Then sort: assigned/selected helpers first, then alphabetically by surname, then first name
  const sortedHelpers = filteredHelpers.sort((a, b) => {
    const aIsAssigned = assignedHelperIds.has(a.id);
    const bIsAssigned = assignedHelperIds.has(b.id);
    const aIsSelected = selectedHelperIdsSet.has(a.id);
    const bIsSelected = selectedHelperIdsSet.has(b.id);
    
    // Assigned or currently selected helpers come first
    const aIsRelevant = aIsAssigned || aIsSelected;
    const bIsRelevant = bIsAssigned || bIsSelected;
    
    if (aIsRelevant && !bIsRelevant) return -1;
    if (!aIsRelevant && bIsRelevant) return 1;
    
    // Within each group, sort alphabetically by surname, then first name
    const surnameA = (a.surname || '').toLowerCase();
    const surnameB = (b.surname || '').toLowerCase();
    if (surnameA !== surnameB) {
      return surnameA.localeCompare(surnameB, 'de');
    }
    const firstNameA = (a.first_name || '').toLowerCase();
    const firstNameB = (b.first_name || '').toLowerCase();
    return firstNameA.localeCompare(firstNameB, 'de');
  });

  return (
    <div className="helper-assignment">
      <div className="helper-assignment-header">
      <h4>Helfer zuordnen: {helferpunkt.einsatzort} ({(() => {
        const weekday = getWeekdayAbbreviation(helferpunkt.einsatzdatum);
        const timeStr = `${helferpunkt.einsatzuhrzeit}${helferpunkt.einsatzuhrzeit_bis ? ` - ${helferpunkt.einsatzuhrzeit_bis}` : ''}`;
        return weekday ? `${weekday} ${timeStr}` : timeStr;
      })()})</h4>
      
      {/* Search + expand/collapse (mobile): same row */}
      <div className="helper-assignment-search-row">
        <input
          type="text"
          placeholder="Helfername suchen..."
          value={helperSearchQuery}
          onChange={(e) => setHelperSearchQuery(e.target.value)}
          className="helper-assignment-search-input"
          onFocus={(e) => {
            e.target.style.outline = 'none';
            e.target.style.borderColor = '#ed1c24';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#ebebeb';
          }}
        />
        <button
          type="button"
          className="helper-assignment-filter-toggle-mobile"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
          aria-label={filtersExpanded ? 'Filter ausblenden' : 'Filter anzeigen'}
          title={filtersExpanded ? 'Filter ausblenden' : 'Filter anzeigen'}
        >
          {filtersExpanded ? '▼' : '▲'}
        </button>
      </div>
      
      {/* Filter Toggles */}
      <div className={`helper-assignment-filters ${filtersExpanded ? 'expanded' : 'collapsed'}`}>
        {/* Toggle: Meine betreuten Helfer vs. Helfer ungeachtet der Betreuer (only if betreuersEnabled and not admin) */}
        {betreuersEnabled && !isAdmin() && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }}>
              {showMyHelfer ? 'Meine betreuten Helfer' : 'Helfer ungeachtet der Betreuer'}
            </span>
            <label className="switch-container">
              <input
                type="checkbox"
                className="switch-input"
                checked={showMyHelfer}
                onChange={(e) => setShowMyHelfer(e.target.checked)}
              />
              <span className="switch-track switch-track-red">
                <span className="switch-thumb" />
              </span>
            </label>
          </div>
        )}
        
        {/* Toggle: Zeitlich verfügbare Helfer vs. Zugeordnete Helfer */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }}>
            {showAvailableHelpers ? 'Zeitlich verfügbare Helfer' : 'Zugeordnete Helfer'}
          </span>
          <label className="switch-container">
            <input
              type="checkbox"
              className="switch-input"
              checked={!showAvailableHelpers}
              onChange={(e) => {
                const newValue = !e.target.checked;
                setShowAvailableHelpers(newValue);
                // When switching to "Zugeordnete Helfer", disable the second switch and keep it off
                if (!newValue) {
                  setHidePlannedHelpers(false);
                }
              }}
            />
            <span className="switch-track switch-track-red">
              <span className="switch-thumb" />
            </span>
          </label>
        </div>

        {/* Toggle: Verplante Helfer ausgeblendet vs. Verplante Helfer sichtbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: showAvailableHelpers ? 1 : 0.5 }}>
          <span style={{ fontSize: '0.85rem', color: showAvailableHelpers ? '#666' : '#999', whiteSpace: 'nowrap' }}>
            {showAvailableHelpers 
              ? (hidePlannedHelpers ? 'Verplante Helfer ausgeblendet' : 'Verplante Helfer sichtbar')
              : 'Verplante Helfer'}
          </span>
          <label className={`switch-container ${!showAvailableHelpers ? 'disabled' : ''}`} style={{ opacity: showAvailableHelpers ? 1 : 0.6 }}>
            <input
              type="checkbox"
              className="switch-input"
              checked={hidePlannedHelpers}
              disabled={!showAvailableHelpers}
              onChange={(e) => setHidePlannedHelpers(e.target.checked)}
            />
            <span className="switch-track switch-track-red">
              <span className="switch-thumb" />
            </span>
          </label>
        </div>
      </div>
      </div>
      
      <div className="helpers-checkbox-list">
        {sortedHelpers.length === 0 ? (
          <div className="no-helpers">
            {showAvailableHelpers ? 'Keine Helfer verfügbar' : 'Noch keine Helfer zugeordnet und gespeichert'}
          </div>
        ) : (
          <>
            {/* Show warning if there are helpers who aren't willing to do multiple Einsätze but already have assignments */}
            {(() => {
              const helpersWithWarning = sortedHelpers.filter(helper => {
                const assignments = helperAssignments[helper.id] || [];
                const otherAssignments = assignments.filter(a => a.id !== helferpunkt.id);
                const hasOtherAssignments = otherAssignments.length > 0;
                const isNotWillingForMultiple = convertBereitFuerMehrereEinsaetze(helper.bereit_fuer_mehrere_einsaetze) !== 'Ja';
                const isCurrentlySelected = selectedHelperIds.includes(helper.id);
                return !isCurrentlySelected && hasOtherAssignments && isNotWillingForMultiple;
              });
              
              if (helpersWithWarning.length > 0) {
                return (
                  <div style={{ fontSize: '0.8rem', color: '#ed1c24', marginBottom: '12px', padding: '8px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px' }}>
                    ⚠️ Warnung: Einige Helfer sind bereits anderen Positionen zugeordnet und nicht bereit für mehrere Einsätze. Du kannst trotzdem weitere Positionen zuordnen.
                  </div>
                );
              }
              return null;
            })()}
            {sortedHelpers.map((helper) => {
            // Use ALL assignments across ALL Fachbereiche for global conflict detection
            const assignments = globalHelperAssignments[helper.id] || [];
            const otherAssignments = assignments.filter(a => a.id !== helferpunkt.id);
            
            // Check for time overlaps with other assignments across ALL Fachbereiche (reserved for future conflict UI)
            void otherAssignments.some(otherAssignment => 
              checkDateTimeOverlap(
                helferpunkt.einsatzdatum,
                helferpunkt.einsatzuhrzeit,
                helferpunkt.einsatzuhrzeit_bis || null,
                otherAssignment.einsatzdatum,
                otherAssignment.einsatzuhrzeit,
                otherAssignment.einsatzuhrzeit_bis || null
              )
            );
            // Check if helper already has assignments and is not willing to do multiple Einsätze
            const hasOtherAssignments = otherAssignments.length > 0;
            const isNotWillingForMultiple = convertBereitFuerMehrereEinsaetze(helper.bereit_fuer_mehrere_einsaetze) !== 'Ja';
            const isCurrentlySelected = selectedHelperIds.includes(helper.id);
            const shouldShowWarning = !isCurrentlySelected && hasOtherAssignments && isNotWillingForMultiple;
            
            return (
              <label key={helper.id} className="helper-checkbox-item" style={{ opacity: yearMaintenanceDisabled ? 0.6 : 1 }}>
                <input
                  type="checkbox"
                  id={`helper-checkbox-${helper.id}`}
                  name={`helper-${helper.id}`}
                  checked={selectedHelperIds.includes(helper.id)}
                  onChange={() => {
                    if (!yearMaintenanceDisabled) {
                      handleToggleHelper(helper.id);
                    }
                  }}
                  disabled={yearMaintenanceDisabled}
                  title={yearMaintenanceDisabled ? 'Das Jahr ist schreibgeschützt' : ''}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', lineHeight: '1.6' }}>
                  {/* Row 1: Name, Email, Phone */}
                  <div>
                    <strong>{formatHelperDisplayName(helper.first_name, helper.surname)}</strong>
                  {helper.email && (
                    <span className="helper-email">
                        {' '}<a href={`mailto:${helper.email}`} className="helper-email-link" onClick={(e) => e.stopPropagation()}>{helper.email}</a>
                    </span>
                  )}
                    {helper.phone && <span className="helper-phone"> 📞 {helper.phone}</span>}
                  </div>
                  
                  {/* Row 2: Wunscheinsatzort/Einsatzzeit/Ist bereit für mehrere Einsätze OR "Hilft nicht am Wettkampftag" */}
                  {(helper.hilft_nicht_am_wettkampftag === 1 || helper.wunscheinsatzort || helper.einsatzzeit || (() => {
                    const bereitValue = helper.bereit_fuer_mehrere_einsaetze;
                    return bereitValue === true || bereitValue === 'Ja' || bereitValue === '1' || (typeof bereitValue === 'number' && bereitValue === 1);
                  })()) && (
                    <div className="helper-assignments-info" style={{ fontSize: '0.85rem', color: '#666' }}>
                      {helper.hilft_nicht_am_wettkampftag === 1 ? (
                        <span><strong>Hilft nicht am Wettkampftag</strong></span>
                      ) : (
                        <>
                          {helper.wunscheinsatzort && (() => {
                            let value = String(helper.wunscheinsatzort || '').trim();
                            value = value.replace(/\s*\|\s*$/g, '').replace(/\|\s*$/g, '').replace(/\s*\|$/g, '');
                            return <span><strong>Wunscheinsatzort:</strong> {value}</span>;
                          })()}
                          {helper.wunscheinsatzort && helper.einsatzzeit && <span> | </span>}
                          {helper.einsatzzeit && (() => {
                            let value = String(helper.einsatzzeit || '').trim();
                            value = value.replace(/\s*\|\s*$/g, '').replace(/\|\s*$/g, '').replace(/\s*\|$/g, '');
                            return <span><strong>Einsatzzeit:</strong> {value}</span>;
                          })()}
                          {(() => {
                            const bereitValue = helper.bereit_fuer_mehrere_einsaetze;
                            const isBereit = bereitValue === true || bereitValue === 'Ja' || bereitValue === '1' || (typeof bereitValue === 'number' && bereitValue === 1);
                            const showSep = (helper.wunscheinsatzort || helper.einsatzzeit) && isBereit;
                            return showSep ? <span> | </span> : null;
                          })()}
                          {(() => {
                            const bereitValue = helper.bereit_fuer_mehrere_einsaetze;
                            const isBereit = bereitValue === true || bereitValue === 'Ja' || bereitValue === '1' || (typeof bereitValue === 'number' && bereitValue === 1);
                            return isBereit ? <span><strong>Ist bereit für mehrere Einsätze</strong></span> : null;
                          })()}
                        </>
                      )}
                    </div>
                  )}
                  {/* Row 2a: Anmerkung (dedicated row) */}
                  {helper.anmerkung && String(helper.anmerkung).trim() !== '' && (
                    <div className="helper-assignments-info" style={{ marginTop: '4px', fontSize: '0.85rem', color: '#666' }}>
                      <span><strong>Anmerkung:</strong> {String(helper.anmerkung).trim()}</span>
                    </div>
                  )}
                  
                  
                  
                  {/* Row 2b: Aufbauhilfe (below Anmerkung if present) */}
                  {(() => {
                    const aufbauhilfeValue = helper.aufbauhilfe;
                    const hasAufbauhilfe = aufbauhilfeValue !== null && aufbauhilfeValue !== undefined && String(aufbauhilfeValue).trim() !== '';
                    return hasAufbauhilfe ? (
                      <div className="helper-assignments-info" style={{ marginTop: '4px', fontSize: '0.85rem', color: '#666' }}>
                        <span><strong>Aufbauhilfe:</strong> {String(aufbauhilfeValue).trim()}</span>
                      </div>
                    ) : null;
                  })()}
                  
                  {/* Row 3: Bereits zugeordnet */}
                  {otherAssignments.length > 0 && (() => {
                    // Sort assignments by date and time
                    const sortedOtherAssignments = [...otherAssignments].sort((a, b) => {
                      // First sort by date
                      if (a.einsatzdatum && b.einsatzdatum) {
                        const dateCompare = a.einsatzdatum.localeCompare(b.einsatzdatum);
                        if (dateCompare !== 0) return dateCompare;
                      } else if (a.einsatzdatum && !b.einsatzdatum) return -1;
                      else if (!a.einsatzdatum && b.einsatzdatum) return 1;
                      
                      // Then sort by time
                      if (a.einsatzuhrzeit && b.einsatzuhrzeit) {
                        return a.einsatzuhrzeit.localeCompare(b.einsatzuhrzeit);
                      }
                      return 0;
                    });
                    
                    // Check for time conflicts between all assignments (including current position)
                    const allAssignments = [helferpunkt, ...sortedOtherAssignments];
                    const conflicts: Array<{ pos1: Helferpunkt; pos2: Helferpunkt }> = [];
                    for (let i = 0; i < allAssignments.length; i++) {
                      for (let j = i + 1; j < allAssignments.length; j++) {
                        const pos1 = allAssignments[i];
                        const pos2 = allAssignments[j];
                        if (checkDateTimeOverlap(
                          pos1.einsatzdatum,
                          pos1.einsatzuhrzeit,
                          pos1.einsatzuhrzeit_bis || null,
                          pos2.einsatzdatum,
                          pos2.einsatzuhrzeit,
                          pos2.einsatzuhrzeit_bis || null
                        )) {
                          conflicts.push({ pos1, pos2 });
                        }
                      }
                    }
                    
                    const hasAnyConflict = conflicts.length > 0;
                    
                    return (
                      <div className="helper-assignments-info" style={{ fontSize: '0.85rem' }}>
                        {hasAnyConflict && <span style={{ color: '#ed1c24', marginRight: '4px' }}>⚠️</span>}
                        {shouldShowWarning && <span style={{ color: '#ed1c24', marginRight: '4px' }}>⚠️</span>}
                        <strong style={{ color: hasAnyConflict || shouldShowWarning ? '#ed1c24' : '#000000' }}>Bereits zugeordnet:</strong>{' '}
                        {shouldShowWarning && <span style={{ color: '#ed1c24', fontWeight: 'bold' }}>(Nicht bereit für mehrere Einsätze) </span>}
                        {hasAnyConflict && (
                          <span style={{ color: '#ed1c24', fontWeight: 'bold', fontSize: '0.8rem', display: 'block', marginTop: '4px', marginBottom: '4px' }}>
                            ⚠️ Zeitkonflikt: Ein Helfer kann nur zu einer Zeit an einer Position sein!
                          </span>
                        )}
                        {sortedOtherAssignments.map((a, index) => {
                          // Check if this assignment conflicts with current position or other assignments
                          const assignmentHasConflict = conflicts.some(c => 
                            (c.pos1.id === a.id || c.pos2.id === a.id) && 
                            (c.pos1.id === helferpunkt.id || c.pos2.id === helferpunkt.id)
                          );
                          const weekday = getWeekdayAbbreviation(a.einsatzdatum);
                          const timeStr = `${a.einsatzuhrzeit}${a.einsatzuhrzeit_bis ? ` - ${a.einsatzuhrzeit_bis}` : ''}`;
                          const timeDisplay = weekday ? `${weekday} ${timeStr}` : timeStr;
                          // Include fachbereich name if different from current fachbereich
                          const fachbereichPrefix = a.fachbereich && a.fachbereich !== helferpunkt.fachbereich 
                            ? `${a.fachbereich} - ` 
                            : '';
                          const assignmentText = `${fachbereichPrefix}${a.einsatzort} (${timeDisplay})`;
                          return (
                            <span key={a.id}>
                              {index > 0 && <span style={{ color: '#000000' }}>, </span>}
                              <span style={{ color: assignmentHasConflict ? '#ed1c24' : '#000000' }}>
                                {assignmentHasConflict && <span style={{ color: '#ed1c24', marginRight: '2px' }}>⚠️</span>}
                                {assignmentText}
                              </span>
                            </span>
                          );
                        })}
                      </div>
                    );
                  })()}
                  
                  {/* Time conflict warning */}
                  {timeConflictWarnings[helper.id] && timeConflictWarnings[helper.id].length > 0 && (
                    <div className="helper-assignments-info" style={{ fontSize: '0.85rem', color: '#ed1c24', fontWeight: 'bold' }}>
                      ⚠️ Warnung: Zeitgleich mit {timeConflictWarnings[helper.id].join(', ')}
                    </div>
                  )}
                </div>
              </label>
            );
          })}
          </>
        )}
      </div>
      {yearMaintenanceDisabled && (
        <div className="fachbereich-error" style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107', color: '#856404', marginBottom: '16px', padding: '8px 12px' }}>
          ⚠️ Das Jahr ist schreibgeschützt. Helferzuordnungen können nicht geändert werden.
        </div>
      )}
      <div className="assignment-actions">
        <button 
          className="submit-button" 
          onClick={handleSave}
          disabled={yearMaintenanceDisabled}
          title={yearMaintenanceDisabled ? 'Das Jahr ist schreibgeschützt' : ''}
          style={{ opacity: yearMaintenanceDisabled ? 0.6 : 1, cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer' }}
        >
          Speichern
        </button>
        <button className="cancel-button" onClick={handleCancel}>
          Abbrechen
        </button>
      </div>
      
      {/* Admin Warning Dialog */}
      {showAdminWarning && (
        <div className="fachbereich-confirmation-overlay" onClick={(e) => {
          // Don't close when clicking outside - user must choose an option
          e.stopPropagation();
        }}>
          <div className="fachbereich-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Hinweis für Administratoren</h3>
            <p>Die Zuordnung von Helfern zu Positionen ist normalerweise Aufgabe der Fachbereich Manager oder Fachbereich Administratoren. Als Administrator solltest du diese Aufgabe den zuständigen Fachbereich Managern/Administratoren überlassen.</p>
            <p style={{ marginTop: '15px', fontWeight: 'bold' }}>Möchtest du trotzdem fortfahren?</p>
            <div className="fachbereich-confirmation-buttons">
              <button
                className="fachbereich-button submit-button"
                onClick={handleAdminWarningConfirm}
              >
                Trotzdem fortfahren
              </button>
              <button
                className="fachbereich-button cancel-button"
                onClick={() => setShowAdminWarning(false)}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Confirmation Dialog */}
      {showConfirmation && (
        <div className="fachbereich-confirmation-overlay" onClick={(e) => {
          // Don't close when clicking outside - user must choose an option
          e.stopPropagation();
        }}>
          <div className="fachbereich-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Ungespeicherte Änderungen</h3>
            <p>Sie haben ungespeicherte Änderungen an den Helferzuordnungen. Was möchten Sie tun?</p>
            <div className="fachbereich-confirmation-buttons">
              <button
                className="fachbereich-button submit-button"
                onClick={() => {
                  handleSave();
                  setShowConfirmation(false);
                }}
              >
                Speichern und schließen
              </button>
              <button
                className="fachbereich-button cancel-button"
                onClick={() => {
                  setShowConfirmation(false);
                  onCancel();
                }}
              >
                Ohne Speichern schließen
              </button>
              <button
                className="fachbereich-button"
                onClick={() => setShowConfirmation(false)}
                style={{ background: 'transparent', border: '2px solid #666', color: '#666' }}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface FachbereichHelperListProps {
  fachbereich: string;
  helpers: Helper[];
  onClose: () => void;
  onUpdate: () => void | Promise<void>; // Can be async
  onEditFachbereich?: (fachbereich: string) => void;
  initialSearchQuery?: string;
  compactMode?: boolean;
  allHelperAssignments?: { [key: number]: Helferpunkt[] };
  yearMaintenanceDisabled?: boolean;
  raceDayDate?: string | null; // Race day date to filter positions for helpers who don't help on race day
  /** When true, default filters to "Keine Rückmeldung" + "Ohne Position" to show new helpers */
  defaultToNewHelperFilters?: boolean;
  /** When true, user has read-only access by role (e.g. Beobachter) - don't show year-lock banners */
  readOnly?: boolean;
}

const RÜCKMELDUNG_OPTIONS = [
  'Zur Kenntnis genommen',
  'In Bearbeitung',
  'Warte auf Helferrückmeldung',
  'Bestätigt',
  'Passt nicht'
];

const FachbereichHelperList: React.FC<FachbereichHelperListProps> = ({ fachbereich, helpers, onClose, onUpdate, initialSearchQuery = '', compactMode = false, allHelperAssignments: globalHelperAssignments = {}, yearMaintenanceDisabled = false, raceDayDate: propRaceDayDate = null, defaultToNewHelperFilters = false, readOnly = false }) => {
  const { token, isAdmin, user, year, canRegisterHelpers, canEmailVisibleHelpers } = useAuth();
  // Fetch raceDayDate if not provided as prop
  const [raceDayDate, setRaceDayDate] = useState<string | null>(propRaceDayDate);
  
  useEffect(() => {
    // If prop is provided, use it
    if (propRaceDayDate) {
      setRaceDayDate(propRaceDayDate);
      return;
    }
    
    // Otherwise fetch it
    const fetchRaceDayDate = async () => {
      if (!year || !token) return;
      try {
        const response = await axios.get(`${API_BASE_URL}/api/admin/years/maintenance`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.data.success && response.data.years) {
          const currentYearData = response.data.years.find((y: { year: number; race_day_date?: string | null }) => y.year === year);
          if (currentYearData?.race_day_date) {
            setRaceDayDate(currentYearData.race_day_date);
          }
        }
      } catch (err) {
        console.error('Error fetching race day date:', err);
      }
    };
    
    fetchRaceDayDate();
  }, [propRaceDayDate, year, token]);
  
  const [saving, setSaving] = React.useState(false);
  const [helperData, setHelperData] = useState<{ [key: number]: { rückmeldung: string; bemerkung: string; caretaker_id?: number | null } }>({});
  const [helperAssignments, setHelperAssignments] = useState<{ [key: number]: Helferpunkt[] }>({});
  const [allHelperAssignmentsLocal, setAllHelperAssignmentsLocal] = useState<{ [key: number]: Helferpunkt[] }>(globalHelperAssignments);
  const [caretakers, setCaretakers] = useState<Caretaker[]>([]);
  const [helferpunkte, setHelferpunkte] = useState<Helferpunkt[]>([]);
  const [assigningHelperId, setAssigningHelperId] = useState<number | null>(null);
  const [helferpunktHelpersMap, setHelferpunktHelpersMap] = useState<{ [helferpunktId: number]: number[] }>({});
  // Track pending position assignment changes (helferpunktId -> helperIds[]) - for manual save
  const [pendingPositionAssignments, setPendingPositionAssignments] = useState<{ [helferpunktId: number]: number[] }>({});
  const pendingPositionAssignmentsRef = React.useRef<{ [helferpunktId: number]: number[] }>({});
  const initialPositionAssignmentsRef = React.useRef<{ [helferpunktId: number]: number[] }>({});
  // When compactMode (editing Rückmeldung for a helper from a position): that helper HAS a position and may have any Rückmeldung.
  // Don't apply defaultToNewHelperFilters - it would hide them (filterRueckmeldung='none' + showWithPosition=false).
  // Also default showMyHelfer to false so the helper is visible even if assigned to a different Betreuer.
  const [filterRueckmeldung, setFilterRueckmeldung] = useState<string>(compactMode ? '' : (defaultToNewHelperFilters ? 'none' : ''));
  const [showWithoutPosition, setShowWithoutPosition] = useState<boolean>(true);
  const [showWithPosition, setShowWithPosition] = useState<boolean>(compactMode ? true : !defaultToNewHelperFilters);
  const [showMyHelfer, setShowMyHelfer] = useState<boolean>(compactMode ? false : true);
  const [showOnlyReadyForMultiple, setShowOnlyReadyForMultiple] = useState<boolean>(false); // Default off: show all (Einzel- und Mehrfacheinsätze)
  const [showWithoutCaretaker, setShowWithoutCaretaker] = useState<boolean>(false); // Default off: show only helpers without Betreuer (visible when betreuersEnabled)
  const [hideConflictingPositionsInAssignment, setHideConflictingPositionsInAssignment] = useState<boolean>(true); // In Positionen zuordnen: hide positions with time conflicts (default: ON = Konfliktfreie Positionen)
  const [searchQuery, setSearchQuery] = useState<string>(initialSearchQuery);
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(false); // Mobile: filters collapsed by default
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showHeaderKebabMenu, setShowHeaderKebabMenu] = useState(false);
  
  // DEAD SIMPLE: Fetch fresh data when dialog opens
  useEffect(() => {
    if (onUpdate) {
      onUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update search query when initialSearchQuery prop changes
  useEffect(() => {
    if (initialSearchQuery) {
      setSearchQuery(initialSearchQuery);
    }
  }, [initialSearchQuery]);

  const [error, setError] = useState('');
  const [saveTimeout, setSaveTimeout] = useState<{ [key: number]: NodeJS.Timeout }>({});
  const [betreuersEnabled, setBetreuersEnabled] = useState<boolean>(true);
  const [showAdminWarningFeedback, setShowAdminWarningFeedback] = useState(false);
  const [showAdminWarningAssignment, setShowAdminWarningAssignment] = useState(false);
  const [showRefreshConfirmModal, setShowRefreshConfirmModal] = useState(false);
  const [refreshConfirmMode, setRefreshConfirmMode] = useState<'refresh' | 'filter'>('refresh');
  const [pendingFilterChange, setPendingFilterChange] = useState<{ [key: string]: unknown } | null>(null);
  const [pendingFeedbackAction, setPendingFeedbackAction] = useState<(() => void) | null>(null);
  const [pendingAssignmentAction, setPendingAssignmentAction] = useState<(() => void) | null>(null);
  // Use refs to access latest values in cleanup
  const helperDataRef = useRef(helperData);
  const saveTimeoutRef = useRef(saveTimeout);
  const isInitializedRef = useRef(false);
  // Store initial values to track changes - USE REF so useEffect can't overwrite it after save
  const initialHelperDataRef = useRef<{ [key: number]: { rückmeldung: string; bemerkung: string; caretaker_id?: number | null } }>({});
  const [initialHelperData, setInitialHelperData] = useState<{ [key: number]: { rückmeldung: string; bemerkung: string; caretaker_id?: number | null } }>({});
  const [, setInitialHelperDataVersion] = useState(0); // Version counter to force memo recalculation
  const lastHelperIdsRef = useRef<string>('');
  const isSavingRef = useRef(false); // Track if we're currently saving
  const lastSaveTimeRef = useRef<number>(0); // Track when we last saved (timestamp)
  const initialCaretakerFilterAppliedRef = useRef(false);
  
  // Keep refs in sync with state
  useEffect(() => {
    helperDataRef.current = helperData;
  }, [helperData]);
  
  useEffect(() => {
    saveTimeoutRef.current = saveTimeout;
  }, [saveTimeout]);

  useEffect(() => {
    pendingPositionAssignmentsRef.current = pendingPositionAssignments;
  }, [pendingPositionAssignments]);

  // Check if there are unsaved changes (helper data or position assignments)
  const hasUnsavedChanges = React.useCallback((): boolean => {
    const currentData = helperDataRef.current;
    const initialData = initialHelperDataRef.current;
    for (const helperIdStr of Object.keys(currentData)) {
      const helperId = parseInt(helperIdStr);
      const cur = currentData[helperId];
      const init = initialData[helperId];
      if (!cur) continue;
      if (!init) return true;
      if ((cur.rückmeldung || '') !== (init.rückmeldung || '')) return true;
      if ((cur.bemerkung || '').trim() !== (init.bemerkung || '').trim()) return true;
      if ((cur.caretaker_id ?? null) !== (init.caretaker_id ?? null)) return true;
    }
    const pending = pendingPositionAssignmentsRef.current;
    const initial = initialPositionAssignmentsRef.current;
    const allHpIds = Array.from(new Set([...Object.keys(pending).map(Number), ...Object.keys(initial).map(Number)]));
    for (const hpId of allHpIds) {
      const a = (pending[hpId] || []).slice().sort((x, y) => x - y);
      const b = (initial[hpId] || []).slice().sort((x, y) => x - y);
      if (a.length !== b.length) return true;
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return true;
    }
    return false;
  }, []);

  // Apply pending filter change (after save/discard when user changed filter with unsaved changes)
  const applyPendingFilterChange = React.useCallback(() => {
    if (!pendingFilterChange) return;
    if (pendingFilterChange.filterRueckmeldung !== undefined) setFilterRueckmeldung(String(pendingFilterChange.filterRueckmeldung));
    if (pendingFilterChange.showMyHelfer !== undefined) setShowMyHelfer(Boolean(pendingFilterChange.showMyHelfer));
    if (pendingFilterChange.showWithoutCaretaker !== undefined) setShowWithoutCaretaker(Boolean(pendingFilterChange.showWithoutCaretaker));
    if (pendingFilterChange.showWithoutPosition !== undefined) setShowWithoutPosition(Boolean(pendingFilterChange.showWithoutPosition));
    if (pendingFilterChange.showWithPosition !== undefined) setShowWithPosition(Boolean(pendingFilterChange.showWithPosition));
    if (pendingFilterChange.showOnlyReadyForMultiple !== undefined) setShowOnlyReadyForMultiple(Boolean(pendingFilterChange.showOnlyReadyForMultiple));
    setPendingFilterChange(null);
  }, [pendingFilterChange]);

  // Intercept filter changes: if unsaved changes, show confirm modal; otherwise apply directly
  const handleFilterChange = React.useCallback((key: string, value: unknown) => {
    if (!hasUnsavedChanges()) {
      if (key === 'filterRueckmeldung') setFilterRueckmeldung(String(value));
      else if (key === 'showMyHelfer') setShowMyHelfer(Boolean(value));
      else if (key === 'showWithoutCaretaker') setShowWithoutCaretaker(Boolean(value));
      else if (key === 'showWithoutPosition') setShowWithoutPosition(Boolean(value));
      else if (key === 'showWithPosition') setShowWithPosition(Boolean(value));
      else if (key === 'showOnlyReadyForMultiple') setShowOnlyReadyForMultiple(Boolean(value));
      return;
    }
    setPendingFilterChange(prev => ({ ...prev, [key]: value }));
    setRefreshConfirmMode('filter');
    setShowRefreshConfirmModal(true);
  }, [hasUnsavedChanges]);

  // Close kebab menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.fachbereich-helper-list-header-kebab') && !target.closest('.fachbereich-helper-list-header-kebab-menu')) {
        setShowHeaderKebabMenu(false);
      }
    };
    if (showHeaderKebabMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showHeaderKebabMenu]);

  // Filter basis: use initialHelperData (saved/loaded state) for filtering, NOT helperData (live edits).
  // This prevents the list from changing when editing fields - list only updates when filter conditions change.
  // Fall back to helperData when initialHelperData isn't populated yet (first render).
  const filterBasis = Object.keys(initialHelperData).length > 0 ? initialHelperData : helperData;

  // Filter helpers to only show those assigned to this Fachbereich
  // This is a safeguard in case the backend returns helpers from other Fachbereiche
  let filteredHelpers = helpers.filter(helper => {
    // Check if helper has fachbereiche array and it includes the current Fachbereich
    if (helper.fachbereiche && Array.isArray(helper.fachbereiche)) {
      return helper.fachbereiche.includes(fachbereich);
    }
    // If no fachbereiche array, check if the helper was returned from the Fachbereich endpoint
    // (all helpers from the endpoint should be for this Fachbereich, but we filter anyway)
    return true; // Keep all if we can't determine - backend should have filtered already
  });

  // Sort helpers by surname, then first name, then email
  filteredHelpers = filteredHelpers.sort((a, b) => {
    const surnameA = (a.surname || '').toLowerCase();
    const surnameB = (b.surname || '').toLowerCase();
    if (surnameA !== surnameB) {
      return surnameA.localeCompare(surnameB, 'de');
    }
    const firstNameA = (a.first_name || '').toLowerCase();
    const firstNameB = (b.first_name || '').toLowerCase();
    if (firstNameA !== firstNameB) {
      return firstNameA.localeCompare(firstNameB, 'de');
    }
    const emailA = (a.email || '').toLowerCase();
    const emailB = (b.email || '').toLowerCase();
    return emailA.localeCompare(emailB, 'de');
  });

  // Apply search filter if search query is provided
  if (searchQuery.trim()) {
    filteredHelpers = filteredHelpers.filter(helper =>
      helperMatchesSearch(searchQuery, helper.first_name, helper.surname, helper.email)
    );
  }


  // Calculate Rückmeldung counts for filter options (x of all format) - use filterBasis (stable)
  const rueckmeldungCounts = useMemo((): Record<string, number> => {
    const counts: Record<string, number> = { none: 0, 'not-confirmed': 0 };
    const allCount = filteredHelpers.length;
    
    // Count helpers without Rückmeldung
    filteredHelpers.forEach(helper => {
      const rueckmeldung = filterBasis[helper.id]?.rückmeldung;
      if (!rueckmeldung || rueckmeldung === '') {
        counts.none++;
      }
    });
    
    // Count helpers per Rückmeldung option
    RÜCKMELDUNG_OPTIONS.forEach(option => {
      counts[option] = 0;
      filteredHelpers.forEach(helper => {
        const rueckmeldung = filterBasis[helper.id]?.rückmeldung;
        if (rueckmeldung === option) {
          counts[option]++;
        }
      });
    });
    
    // Count helpers with Rückmeldung that is NOT "Bestätigt" (including no Rückmeldung)
    filteredHelpers.forEach(helper => {
      const rueckmeldung = filterBasis[helper.id]?.rückmeldung;
      if (!rueckmeldung || rueckmeldung === '' || rueckmeldung !== 'Bestätigt') {
        counts['not-confirmed']++;
      }
    });
    
    counts.all = allCount;
    return counts;
  }, [filteredHelpers, filterBasis]);

  // Helpers after Rückmeldung and caretaker filters - used for position/readyForMultiple counts so "all" reflects current view - use filterBasis (stable)
  const helpersAfterRueckmeldungAndCaretaker = useMemo(() => {
    let list = [...filteredHelpers];
    if (betreuersEnabled && !isAdmin() && showMyHelfer && user?.id) {
      list = list.filter(helper => filterBasis[helper.id]?.caretaker_id === user.id);
    }
    if (filterRueckmeldung) {
      if (filterRueckmeldung === 'none') {
        list = list.filter(helper => !filterBasis[helper.id]?.rückmeldung || filterBasis[helper.id]?.rückmeldung === '');
      } else if (filterRueckmeldung === 'not-confirmed') {
        list = list.filter(helper => {
          const r = filterBasis[helper.id]?.rückmeldung;
          return !r || r === '' || r !== 'Bestätigt';
        });
      } else {
        list = list.filter(helper => filterBasis[helper.id]?.rückmeldung === filterRueckmeldung);
      }
    } else {
      // Default "Alle Rückmeldungen (außer Passt nicht)": exclude Passt nicht
      list = list.filter(helper => filterBasis[helper.id]?.rückmeldung !== 'Passt nicht');
    }
    return list;
  }, [filteredHelpers, filterRueckmeldung, showMyHelfer, betreuersEnabled, isAdmin, user, filterBasis]);

  // Calculate position assignment counts for filter options (x of all format) - "all" = helpers in current view
  const positionCounts = useMemo(() => {
    const counts: { assigned: number; unassigned: number; all: number } = { assigned: 0, unassigned: 0, all: helpersAfterRueckmeldungAndCaretaker.length };
    
    helpersAfterRueckmeldungAndCaretaker.forEach(helper => {
      const assignments = helperAssignments[helper.id];
      if (assignments && assignments.length > 0) {
        counts.assigned++;
      } else {
        counts.unassigned++;
      }
    });
    
    return counts;
  }, [helpersAfterRueckmeldungAndCaretaker, helperAssignments]);

  // Count helpers ready for multiple positions (for "Helfer bereit für mehr Einsätze" filter) - within current view
  const readyForMultipleCount = useMemo(() => {
    return helpersAfterRueckmeldungAndCaretaker.filter(helper =>
      convertBereitFuerMehrereEinsaetze(helper.bereit_fuer_mehrere_einsaetze) === 'Ja'
    ).length;
  }, [helpersAfterRueckmeldungAndCaretaker]);

  // Count helpers without caretaker (for "Helfer ohne Betreuer" filter) - within current view - use filterBasis (stable)
  const withoutCaretakerCount = useMemo(() => {
    return helpersAfterRueckmeldungAndCaretaker.filter(helper => {
      const caretakerId = filterBasis[helper.id]?.caretaker_id;
      return !caretakerId;
    }).length;
  }, [helpersAfterRueckmeldungAndCaretaker, filterBasis]);

  // Calculate helper counts for "Meine betreuten Helfer" filter display
  const helferCounts = useMemo(() => {
    if (!betreuersEnabled || isAdmin() || !user?.id) {
      return { myHelfer: 0, allHelfer: 0 };
    }

    // Start with helpers filtered by Fachbereich, sorted, and search query
    let helpersForCount = helpers.filter(helper => {
      if (helper.fachbereiche && Array.isArray(helper.fachbereiche)) {
        return helper.fachbereiche.includes(fachbereich);
      }
      return true;
    });

    // Apply search filter if present
    if (searchQuery.trim()) {
      helpersForCount = helpersForCount.filter(helper =>
        helperMatchesSearch(searchQuery, helper.first_name, helper.surname, helper.email)
      );
    }

    // Apply Rückmeldung filter if present - use filterBasis (stable)
    if (filterRueckmeldung) {
      if (filterRueckmeldung === 'none') {
        helpersForCount = helpersForCount.filter(helper => {
          const rueckmeldung = filterBasis[helper.id]?.rückmeldung;
          return !rueckmeldung || rueckmeldung === '';
        });
      } else if (filterRueckmeldung === 'not-confirmed') {
        helpersForCount = helpersForCount.filter(helper => {
          const rueckmeldung = filterBasis[helper.id]?.rückmeldung;
          return !rueckmeldung || rueckmeldung === '' || rueckmeldung !== 'Bestätigt';
        });
      } else {
        helpersForCount = helpersForCount.filter(helper => {
          const rueckmeldung = filterBasis[helper.id]?.rückmeldung;
          return rueckmeldung === filterRueckmeldung;
        });
      }
    } else {
      // Default "Alle Rückmeldungen (außer Passt nicht)": exclude Passt nicht
      helpersForCount = helpersForCount.filter(helper => filterBasis[helper.id]?.rückmeldung !== 'Passt nicht');
    }

    // Apply position filters
    if (!showWithoutPosition && !showWithPosition) {
      helpersForCount = [];
    } else if (!showWithoutPosition && showWithPosition) {
      helpersForCount = helpersForCount.filter(helper => {
        const assignments = helperAssignments[helper.id];
        return assignments && assignments.length > 0;
      });
    } else if (showWithoutPosition && !showWithPosition) {
      helpersForCount = helpersForCount.filter(helper => {
        const assignments = helperAssignments[helper.id];
        return !assignments || assignments.length === 0;
      });
    }

    // Apply "bereit für mehr Einsätze" filter if on
    if (showOnlyReadyForMultiple) {
      helpersForCount = helpersForCount.filter(helper =>
        convertBereitFuerMehrereEinsaetze(helper.bereit_fuer_mehrere_einsaetze) === 'Ja'
      );
    }

    // Calculate all helfer count (before caretaker filter)
    const allHelfer = helpersForCount.length;

    // Calculate my helfer count (with caretaker filter) - use filterBasis (stable)
    const myHelfer = helpersForCount.filter(helper => {
      const helperCaretakerId = filterBasis[helper.id]?.caretaker_id;
      return helperCaretakerId === user.id;
    }).length;

    return { myHelfer, allHelfer };
  }, [helpers, fachbereich, searchQuery, filterRueckmeldung, showWithoutPosition, showWithPosition, showOnlyReadyForMultiple, filterBasis, helperAssignments, betreuersEnabled, isAdmin, user]);

  // Apply caretaker filter (My Helfer vs All Helfer) if Betreuer is enabled - only for non-admins - use filterBasis (stable)
  if (betreuersEnabled && !isAdmin() && showMyHelfer && user?.id) {
    filteredHelpers = filteredHelpers.filter(helper => {
      const helperCaretakerId = filterBasis[helper.id]?.caretaker_id;
      return helperCaretakerId === user.id;
    });
  }

  // Apply Rückmeldung filter - use filterBasis (stable)
  if (filterRueckmeldung) {
    if (filterRueckmeldung === 'none') {
      // Filter for helpers without a Rückmeldung
      filteredHelpers = filteredHelpers.filter(helper => {
        const rueckmeldung = filterBasis[helper.id]?.rückmeldung;
        return !rueckmeldung || rueckmeldung === '';
      });
    } else if (filterRueckmeldung === 'not-confirmed') {
      // Filter for helpers with Rückmeldung that is NOT "Bestätigt" (including no Rückmeldung)
      filteredHelpers = filteredHelpers.filter(helper => {
        const rueckmeldung = filterBasis[helper.id]?.rückmeldung;
        return !rueckmeldung || rueckmeldung === '' || rueckmeldung !== 'Bestätigt';
      });
    } else {
      // Filter for specific Rückmeldung
      filteredHelpers = filteredHelpers.filter(helper => {
        const rueckmeldung = filterBasis[helper.id]?.rückmeldung;
        return rueckmeldung === filterRueckmeldung;
      });
    }
  } else {
    // Default "Alle Rückmeldungen (außer Passt nicht)": exclude Passt nicht
    filteredHelpers = filteredHelpers.filter(helper => filterBasis[helper.id]?.rückmeldung !== 'Passt nicht');
  }

  // Apply position assignment filters
  // CRITICAL: Always keep the helper being edited (assigningHelperId) visible - don't hide them when they get a position
  if (!showWithoutPosition && !showWithPosition) {
    // Both filters are off - show empty list (handled in empty state message)
    filteredHelpers = [];
  } else if (!showWithoutPosition && showWithPosition) {
    // Only show helpers with positions
    filteredHelpers = filteredHelpers.filter(helper => {
      if (assigningHelperId === helper.id) return true; // Keep current assignment panel helper visible
      const assignments = helperAssignments[helper.id];
      return assignments && assignments.length > 0;
    });
  } else if (showWithoutPosition && !showWithPosition) {
    // Only show helpers without positions - but keep the one being edited visible even after assigning
    filteredHelpers = filteredHelpers.filter(helper => {
      if (assigningHelperId === helper.id) return true; // Keep current assignment panel helper visible
      const assignments = helperAssignments[helper.id];
      return !assignments || assignments.length === 0;
    });
  }
  // If both are true (default), show all helpers (no filtering needed)

  // Apply "Helfer bereit für mehr Einsätze" filter (default off = show all)
  if (showOnlyReadyForMultiple) {
    filteredHelpers = filteredHelpers.filter(helper =>
      convertBereitFuerMehrereEinsaetze(helper.bereit_fuer_mehrere_einsaetze) === 'Ja'
    );
  }

  // Apply "Helfer ohne Betreuer" filter (default off = show all) - only when betreuersEnabled - use filterBasis (stable)
  if (betreuersEnabled && showWithoutCaretaker) {
    filteredHelpers = filteredHelpers.filter(helper => {
      const caretakerId = filterBasis[helper.id]?.caretaker_id;
      return !caretakerId;
    });
  }

  const fetchHelperAssignments = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/fachbereich/${encodeURIComponent(fachbereich)}/helpers/assignments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.success) {
        setHelperAssignments(response.data.assignments || {});
      }
    } catch (err) {
      // Error fetching helper assignments
    }
  };

  // Fetch ALL helper assignments across ALL Fachbereiche for global conflict detection
  const fetchAllHelperAssignments = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/helpers/assignments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.success) {
        setAllHelperAssignmentsLocal(response.data.assignments || {});
      }
    } catch (err) {
      // Error fetching all helper assignments
    }
  };

  // Fetch betreuers_enabled setting for this Fachbereich
  useEffect(() => {
    const fetchBetreuersEnabled = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API_BASE_URL}/api/fachbereiche`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.data.success) {
          const fachbereichData = response.data.fachbereiche.find((fb: any) => fb.name === fachbereich);
          if (fachbereichData) {
            setBetreuersEnabled(fachbereichData.betreuers_enabled !== undefined && fachbereichData.betreuers_enabled !== 0 && fachbereichData.betreuers_enabled !== false);
          }
        }
      } catch (err) {
        // Default to enabled if fetch fails
        setBetreuersEnabled(true);
      }
    };
    fetchBetreuersEnabled();
  }, [fachbereich, token]);

  // Fetch available caretakers for this Fachbereich (only if Betreuer is enabled)
  useEffect(() => {
    if (!betreuersEnabled) {
      setCaretakers([]);
      return;
    }
    const fetchCaretakers = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/fachbereich/${encodeURIComponent(fachbereich)}/caretakers`);
        if (response.data.success) {
          setCaretakers(response.data.caretakers || []);
        }
      } catch (err) {
        // Error fetching caretakers
      }
    };
    fetchCaretakers();
    // Reset initialization flag and initial data when Fachbereich changes (new popup instance)
    isInitializedRef.current = false;
    initialHelperDataRef.current = {};
    setInitialHelperData({});
    setInitialHelperDataVersion(prev => prev + 1);
  }, [fachbereich, betreuersEnabled]);

  // Reset initial caretaker filter when Fachbereich changes
  useEffect(() => {
    initialCaretakerFilterAppliedRef.current = false;
  }, [fachbereich]);

  // When opening: if caretaker enabled, user has no helpers assigned to them, and there are helpers without caretaker,
  // don't filter for "my helpers" (show all) and instead filter for helpers without caretaker.
  // Apply when: (no helpers assigned to user) OR (helpers without caretaker exist) - latter ensures we show useful default
  useEffect(() => {
    if (!betreuersEnabled || isAdmin() || !user?.id || initialCaretakerFilterAppliedRef.current) return;
    if (!helpers || helpers.length === 0) return;
    const fachbereichHelpers = helpers.filter(h =>
      h.fachbereiche && Array.isArray(h.fachbereiche) ? h.fachbereiche.includes(fachbereich) : true
    );
    if (fachbereichHelpers.length === 0) return;
    const firstId = fachbereichHelpers[0].id;
    if (helperData[firstId] === undefined) return;

    const withoutCaretakerCount = fachbereichHelpers.filter(h => !helperData[h.id]?.caretaker_id).length;

    // Apply when there are helpers without caretaker: show "Helfer ungeachtet der Betreuer" (off) + "Helfer ohne Betreuer" (on)
    if (withoutCaretakerCount > 0) {
      setShowMyHelfer(false);
      setShowWithoutCaretaker(true);
    }
    initialCaretakerFilterAppliedRef.current = true;
  }, [helpers, helperData, fachbereich, betreuersEnabled, isAdmin, user?.id]);

  // Fetch helferpunkte for this Fachbereich
  useEffect(() => {
    const fetchHelferpunkte = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/fachbereich/${encodeURIComponent(fachbereich)}/helferpunkte`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.data.success) {
          const helferpunkteData = response.data.helferpunkte || [];
          setHelferpunkte(helferpunkteData);
          
          // Initialize initialPositionAssignmentsRef with current position assignments
          // This prevents false positives when checking for unsaved changes
          const helpersMap: { [helferpunktId: number]: number[] } = {};
          await Promise.all(
            helferpunkteData.map(async (hp: Helferpunkt) => {
              try {
                const helpersResponse = await axios.get(`${API_BASE_URL}/api/helferpunkte/${hp.id}/helpers`, {
                  headers: {
                    'Authorization': `Bearer ${token}`
                  }
                });
                if (helpersResponse.data.success) {
                  const helperIds = helpersResponse.data.helpers.map((h: Helper) => h.id);
                  helpersMap[hp.id] = helperIds;
                }
              } catch (err) {
                // Error fetching helpers for helferpunkt - initialize with empty array
                helpersMap[hp.id] = [];
              }
            })
          );
          initialPositionAssignmentsRef.current = { ...helpersMap };
          setPendingPositionAssignments({ ...helpersMap });
        }
      } catch (err) {
        // Error fetching helferpunkte
        setError('Fehler beim Laden der Positionen');
      }
    };
    if (token) {
    fetchHelferpunkte();
    }
  }, [fachbereich, token]);

  // DEAD SIMPLE: When helpers prop has data, update helperData - NO CONDITIONS
  useEffect(() => {
    // If no helpers, wait for data - don't clear existing data
    if (!helpers || helpers.length === 0) {
      return;
    }
    
    // Build helperData from ALL helpers - SIMPLE
    const updatedData: { [key: number]: { rückmeldung: string; bemerkung: string; caretaker_id?: number | null } } = {};
    
    helpers.forEach(helper => {
      // Extract Fachbereich-specific feedback from feedback array
      // API now returns feedback in an array: [{ fachbereich: "...", rückmeldung: "...", bemerkung: "...", caretaker_id: ... }]
      let fachbereichFeedback = null;
      if (helper.feedback && Array.isArray(helper.feedback) && helper.feedback.length > 0) {
        fachbereichFeedback = helper.feedback.find(fb => fb.fachbereich === fachbereich);
      }
      
      // Use feedback array data if available, otherwise fall back to direct properties (backward compatibility)
      const rückmeldung = fachbereichFeedback?.rückmeldung ?? helper.rückmeldung ?? null;
      const bemerkung = fachbereichFeedback?.bemerkung ?? helper.bemerkung ?? null;
      const caretaker_id = fachbereichFeedback?.caretaker_id ?? helper.caretaker_id ?? null;
      
      // CRITICAL: Normalize EXACTLY as save functions do:
      // - Backend returns null for empty rückmeldung/bemerkung, or trimmed strings
      // - Convert null to '' for both
      // - Trim bemerkung (backend trims before saving, but normalize here for consistency)
      // - This ensures useEffect produces the same normalized values as save functions
      const normalizedRückmeldung = rückmeldung || '';
      const normalizedBemerkung = (bemerkung || '').trim();
      const normalizedCaretakerId = caretaker_id || null;
      
      
      updatedData[helper.id] = {
        rückmeldung: normalizedRückmeldung,
        bemerkung: normalizedBemerkung,
        caretaker_id: normalizedCaretakerId
      };
    });
    
    // ALWAYS update helperData for display
    helperDataRef.current = updatedData;
    setHelperData(updatedData);
    
    // ONLY update initialHelperData if we're NOT currently saving
    // This prevents the useEffect from overwriting our manual update after save
    if (!isSavingRef.current) {
      const now = Date.now();
      const timeSinceLastSave = now - lastSaveTimeRef.current;
      const recentSaveThreshold = 3000; // 3 seconds
      
      // If we recently saved, check if backend data matches what we saved
      // If it doesn't match, skip update to avoid overwriting with stale data
      if (timeSinceLastSave < recentSaveThreshold) {
        // Normalize function for string comparison
        const normalize = (value: string | null | undefined): string => {
          return (value || '').trim();
        };
        const normalizeId = (value: number | null | undefined): number | null => {
          return value || null;
        };
        
        let backendMatchesSaved = true;
        for (const helperId in updatedData) {
          const backendData = updatedData[helperId];
          const savedData = initialHelperDataRef.current[helperId];
          if (savedData) {
            const backendRückmeldung = normalize(backendData.rückmeldung);
            const savedRückmeldung = normalize(savedData.rückmeldung);
            const backendBemerkung = normalize(backendData.bemerkung);
            const savedBemerkung = normalize(savedData.bemerkung);
            const backendCaretakerId = normalizeId(backendData.caretaker_id);
            const savedCaretakerId = normalizeId(savedData.caretaker_id);
            
            if (backendRückmeldung !== savedRückmeldung || 
                backendBemerkung !== savedBemerkung || 
                backendCaretakerId !== savedCaretakerId) {
              backendMatchesSaved = false;
              break;
            }
          }
        }
        
        if (!backendMatchesSaved) {
          return; // Don't update initialHelperDataRef with stale data
        }
      }
      
      initialHelperDataRef.current = { ...updatedData };
      // Create deep copy to ensure React detects the change
      const newInitialData = JSON.parse(JSON.stringify(updatedData));
      setInitialHelperData(newInitialData);
      setInitialHelperDataVersion(prev => prev + 1);
    }
    
    fetchHelperAssignments();
    fetchAllHelperAssignments(); // Fetch all assignments for global conflict detection
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [helpers, fachbereich]);
  
  // REMOVED: Complex reset logic - not needed anymore since we always read from helpers prop


  // SIMPLE: Save all helpers and position assignments - use helperDataRef.current to get latest state
  const handleSaveAll = async () => {
    setError('');
    
    // Warn and require confirmation when changing Rückmeldung to "Passt nicht" for helpers with position assignments
    const currentHelperData = helperDataRef.current;
    let totalAssignmentsToRevoke = 0;
    let helpersWithRevokeCount = 0;
    for (const helperIdStr of Object.keys(currentHelperData)) {
      const helperId = parseInt(helperIdStr);
      const data = currentHelperData[helperId];
      if (!data) continue;
      const initialData = initialHelperDataRef.current[helperId];
      const newRueckmeldung = (data.rückmeldung || '').trim();
      const oldRueckmeldung = (initialData?.rückmeldung || '').trim();
      if (newRueckmeldung === 'Passt nicht' && oldRueckmeldung !== 'Passt nicht') {
        const assignmentsInFachbereich = (helperAssignments[helperId] || []).filter(
          (hp: { fachbereich?: string }) => hp.fachbereich === fachbereich
        );
        if (assignmentsInFachbereich.length > 0) {
          totalAssignmentsToRevoke += assignmentsInFachbereich.length;
          helpersWithRevokeCount++;
        }
      }
    }
    if (totalAssignmentsToRevoke > 0) {
      const msg = helpersWithRevokeCount === 1
        ? `Wenn Sie die Rückmeldung auf „Passt nicht“ ändern, werden ${totalAssignmentsToRevoke} Positionszuordnung(en) dieses Helfers in diesem Fachbereich entfernt. Möchten Sie fortfahren?`
        : `Wenn Sie die Rückmeldung auf „Passt nicht“ ändern, werden ${totalAssignmentsToRevoke} Positionszuordnung(en) von ${helpersWithRevokeCount} Helfern in diesem Fachbereich entfernt. Möchten Sie fortfahren?`;
      if (!window.confirm(msg)) {
        return;
      }
    }
    
    setSaving(true);
    
    try {
      const saves: Promise<void>[] = [];
      
      // Save helper data (rückmeldung, bemerkung, caretaker)
      const currentHelperData = helperDataRef.current;
      const helperIds = Object.keys(currentHelperData).map(id => parseInt(id));
      
      for (const helperId of helperIds) {
        const data = currentHelperData[helperId];
        if (data) {
          // Save ALL fields - SIMPLE, no checks
          saves.push(
            saveHelperDataImmediate(
              helperId,
              data.rückmeldung || '',
              data.bemerkung || '',
              data.caretaker_id || null,
              true
            )
          );
        }
      }
      
      // Save position assignments - filter out helpers no longer in this Fachbereich
      // AND filter out helpers with "Passt nicht" (backend removes them when feedback is saved,
      // but we must not re-add them via this save - otherwise we overwrite the backend's DELETE)
      const helperIdsInFachbereich = new Set((helpers || []).map(h => h.id));
      const helferpunktIds = Object.keys(pendingPositionAssignments);
      for (const helferpunktIdStr of helferpunktIds) {
        const helferpunktId = parseInt(helferpunktIdStr);
        const rawHelperIds = pendingPositionAssignments[helferpunktId] || [];
        const helperIds = rawHelperIds.filter((id: number) => {
          if (!helperIdsInFachbereich.has(id)) return false;
          const data = currentHelperData[id];
          if (data && (data.rückmeldung || '').trim() === 'Passt nicht') return false;
          return true;
        });
        saves.push(
          axios.put(
            `${API_BASE_URL}/api/helferpunkte/${helferpunktId}/helpers`,
            { helperIds },
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          ).then(response => {
            if (!response.data.success) {
              throw new Error(response.data.message || 'Fehler beim Speichern der Position');
            }
          })
        );
      }
      
      // Wait for all saves
      await Promise.allSettled(saves);
      
      // CRITICAL: Set isSavingRef BEFORE any state updates to prevent useEffect from overwriting
      // This must happen FIRST to ensure useEffect sees the flag when it runs
      isSavingRef.current = true;
      
      // CRITICAL: Update initialHelperDataRef IMMEDIATELY with what we just saved
      // Use REF so useEffect can't overwrite it
      initialHelperDataRef.current = { ...helperDataRef.current };
      // Create deep copy to ensure React detects the change
      const newInitialData = JSON.parse(JSON.stringify(initialHelperDataRef.current));
      setInitialHelperData(newInitialData);
      setInitialHelperDataVersion(prev => prev + 1);
      
      // Update position assignments ref - remove "Passt nicht" helpers to match what we actually saved
      const filteredAssignments: { [key: number]: number[] } = {};
      for (const hpIdStr of Object.keys(pendingPositionAssignments)) {
        const hpId = parseInt(hpIdStr);
        const rawIds = pendingPositionAssignments[hpId] || [];
        filteredAssignments[hpId] = rawIds.filter((id: number) => {
          const data = currentHelperData[id];
          if (data && (data.rückmeldung || '').trim() === 'Passt nicht') return false;
          return true;
        });
      }
      initialPositionAssignmentsRef.current = filteredAssignments;
      
      // Refresh data from backend - useEffect will update helperData but NOT initialHelperDataRef (because isSavingRef is true)
      if (onUpdate) {
        await new Promise(resolve => setTimeout(resolve, 300));
        const result = onUpdate();
        if (result && typeof result.then === 'function') {
          await result;
        }
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      isSavingRef.current = false;
      
      setSaving(false);
      // Close assignment UI and return to list view (keep dialog open with same filters)
      setAssigningHelperId(null);
      setPendingPositionAssignments({});
    } catch (err: any) {
      setError(`Fehler: ${err.message || 'Unbekannter Fehler'}`);
      setSaving(false);
    }
  };

  const handleRückmeldungChange = (helperId: number, value: string) => {
    // Update local state only - no auto-save, user must click Save button
    setHelperData(prev => {
      const currentData = prev[helperId] || {};
      const updated = {
        ...prev,
        [helperId]: {
          ...currentData,
          rückmeldung: value,
          bemerkung: currentData.bemerkung || '',
          caretaker_id: currentData.caretaker_id
        }
      };
      helperDataRef.current = updated;
      return updated;
    });
  };

  const handleBemerkungChange = (helperId: number, value: string) => {
    if (value.length > 200) {
      setError('Bemerkung darf maximal 200 Zeichen lang sein');
      return;
    }
    setError('');
    // Update local state only - no auto-save, user must click Save button
    setHelperData(prev => {
      const currentData = prev[helperId] || {};
      const updated = {
        ...prev,
        [helperId]: {
          ...currentData,
          rückmeldung: currentData.rückmeldung || '',
          bemerkung: value,
          caretaker_id: currentData.caretaker_id
        }
      };
      helperDataRef.current = updated;
      return updated;
    });
  };

  const handleCaretakerChange = (helperId: number, value: string) => {
    const caretakerId = value === '' ? null : parseInt(value);
    
    // SIMPLE: Just update state - user must click Save button
    setHelperData(prev => {
      const updated = {
        ...prev,
        [helperId]: {
          ...prev[helperId],
          caretaker_id: caretakerId
        }
      };
      helperDataRef.current = updated;
      return updated;
    });
  };

  // Partial save function - only sends changed fields
  const saveHelperDataPartial = async (helperId: number, payload: { rückmeldung?: string | null; bemerkung?: string | null; caretaker_id?: number | null }, skipAdminCheck: boolean = false) => {
    // Check if user is Admin and changing rückmeldung or bemerkung
    if (!skipAdminCheck && isAdmin() && user?.role === 'Administrator') {
      const hasRueckmeldungChange = payload.rückmeldung !== undefined;
      const hasBemerkungChange = payload.bemerkung !== undefined;
      
      if (hasRueckmeldungChange || hasBemerkungChange) {
        setPendingFeedbackAction(() => () => performSaveHelperDataPartial(helperId, payload));
        setShowAdminWarningFeedback(true);
        return;
      }
    }
    
    await performSaveHelperDataPartial(helperId, payload);
  };

  const performSaveHelperDataPartial = async (helperId: number, payload: { rückmeldung?: string | null; bemerkung?: string | null; caretaker_id?: number | null }) => {
    // Clear any pending timeout for this helper
      setSaveTimeout(prev => {
        const newTimeout = { ...prev };
      if (newTimeout[helperId]) {
        clearTimeout(newTimeout[helperId]);
        delete newTimeout[helperId];
      }
        return newTimeout;
      });

    setError('');

    try {
      const url = `${API_BASE_URL}/api/helper/${helperId}/fachbereich/${encodeURIComponent(fachbereich)}/feedback`;
      
      const response = await axios.put(url, payload);
      
      if (response.data.success) {
        // CRITICAL: Set isSavingRef BEFORE any state updates to prevent useEffect from overwriting
        // This must happen FIRST to ensure useEffect sees the flag when it runs
        isSavingRef.current = true;
        
        // CRITICAL: Update initialHelperDataRef IMMEDIATELY with what we just saved
        // Use REF so useEffect can't overwrite it
        // IMPORTANT: Merge payload with current helperData to get complete state
        // Normalize values EXACTLY as useEffect does:
        // - Backend converts empty rückmeldung to null, trims bemerkung and converts empty to null
        // - useEffect converts null to '' for both rückmeldung and bemerkung
        // - So savedData must match what useEffect will produce from backend response
        const currentData = helperDataRef.current[helperId] || { rückmeldung: '', bemerkung: '', caretaker_id: null };
        
        // Build savedData by merging payload with current data, normalizing as useEffect does
        const savedRückmeldung = payload.rückmeldung !== undefined ? (payload.rückmeldung || '') : (currentData.rückmeldung || '');
        const savedBemerkung = payload.bemerkung !== undefined 
          ? ((payload.bemerkung || '').trim() || '')
          : ((currentData.bemerkung || '').trim() || '');
        const savedCaretakerId = payload.caretaker_id !== undefined ? (payload.caretaker_id || null) : (currentData.caretaker_id || null);
        
        const savedData = {
          rückmeldung: savedRückmeldung,
          bemerkung: savedBemerkung,
          caretaker_id: savedCaretakerId
        };
        
        // Update ref IMMEDIATELY - this is the source of truth for comparison
        initialHelperDataRef.current[helperId] = savedData;
        lastSaveTimeRef.current = Date.now(); // Track save time
        
        // Update state IMMEDIATELY - create deep copy to ensure React detects the change
        const newInitialData = JSON.parse(JSON.stringify(initialHelperDataRef.current));
        setInitialHelperData(newInitialData);
        setInitialHelperDataVersion(prev => prev + 1);
        
        // Also update helperData to match - this ensures UI shows saved state immediately
        setHelperData(prev => {
          const updated = { ...prev };
          updated[helperId] = savedData;
          helperDataRef.current = updated;
          return updated;
        });
        
        // Refresh from backend - useEffect will update helperData but NOT initialHelperDataRef (because isSavingRef is true)
        onUpdate();
        // CRITICAL: Wait longer before allowing useEffect to update initialHelperDataRef
        // This gives the backend time to process and return the updated data
        setTimeout(() => { 
          isSavingRef.current = false;
          // Don't call onUpdate() again - let the useEffect handle it when helpers prop changes
          // The backend query from the first onUpdate() should have completed by now
        }, 2000);
      } else {
        const errorMsg = response.data.message || response.data.error || 'Fehler beim Speichern';
        const details = response.data.details ? ` (Details: ${response.data.details})` : '';
        const fullErrorMsg = errorMsg + details;
        // Save failed
        setError(fullErrorMsg);
        throw new Error(fullErrorMsg);
      }
    } catch (err: any) {
      let errorMsg = 'Fehler beim Speichern';
      
      if (err.response) {
        errorMsg = err.response.data?.message || `HTTP ${err.response.status}: ${err.response.statusText}`;
        if (err.response.data?.details) {
          errorMsg += ` (Details: ${err.response.data.details})`;
        }
      } else if (err.request) {
        errorMsg = 'Keine Antwort vom Server. Bitte prüfen Sie, ob der Server läuft.';
      } else {
        errorMsg = err.message || 'Unbekannter Fehler';
      }
      
      // Save error details
      
      setError(errorMsg);
      throw err; // Re-throw to allow caller to handle
    }
  };

  // Immediate save function (no debounce) - used for caretaker assignments, blur, and popup close
  const saveHelperDataImmediate = async (helperId: number, rückmeldung: string, bemerkung: string, caretaker_id?: number | null, skipAdminCheck: boolean = false) => {
    // Check if user is Admin and changing rückmeldung or bemerkung
    if (!skipAdminCheck && isAdmin() && user?.role === 'Administrator') {
      // Check if rückmeldung or bemerkung has changed
      const initialData = initialHelperData[helperId];
      if (initialData) {
        const rückmeldungChanged = (rückmeldung || '') !== (initialData.rückmeldung || '');
        const bemerkungChanged = (bemerkung || '').trim() !== (initialData.bemerkung || '').trim();
        
        if (rückmeldungChanged || bemerkungChanged) {
          setPendingFeedbackAction(() => () => performSaveHelperDataImmediate(helperId, rückmeldung, bemerkung, caretaker_id));
          setShowAdminWarningFeedback(true);
          return;
        }
      }
    }
    
    await performSaveHelperDataImmediate(helperId, rückmeldung, bemerkung, caretaker_id);
  };

  const performSaveHelperDataImmediate = async (helperId: number, rückmeldung: string, bemerkung: string, caretaker_id?: number | null) => {
    // Clear any pending timeout for this helper using ref to avoid stale state
    setSaveTimeout(prev => {
      const newTimeout = { ...prev };
      if (newTimeout[helperId]) {
        clearTimeout(newTimeout[helperId]);
        delete newTimeout[helperId];
      }
      return newTimeout;
    });

    setError('');

    try {
      const payload = {
        rückmeldung: rückmeldung || null,
        bemerkung: (bemerkung || '').trim() || null,
        caretaker_id: caretaker_id !== undefined ? caretaker_id : null
      };
      
      const url = `${API_BASE_URL}/api/helper/${helperId}/fachbereich/${encodeURIComponent(fachbereich)}/feedback`;
      
      const response = await axios.put(url, payload);

      if (response.data.success) {
        // CRITICAL: Set isSavingRef BEFORE any state updates to prevent useEffect from overwriting
        // This must happen FIRST to ensure useEffect sees the flag when it runs
        isSavingRef.current = true;
        
        // CRITICAL: Update initialHelperDataRef IMMEDIATELY with what we just saved
        // Use REF so useEffect can't overwrite it
        // IMPORTANT: Normalize values EXACTLY as useEffect does:
        // - Backend converts empty rückmeldung to null, trims bemerkung and converts empty to null
        // - useEffect converts null to '' for both rückmeldung and bemerkung
        // - So savedData must match what useEffect will produce from backend response
        const normalizedRückmeldung = rückmeldung || '';
        const normalizedBemerkung = (bemerkung || '').trim() || '';
        const normalizedCaretakerId = caretaker_id || null;
        
        const savedData = {
          rückmeldung: normalizedRückmeldung,
          bemerkung: normalizedBemerkung,
          caretaker_id: normalizedCaretakerId
        };
        
        // Update ref IMMEDIATELY - this is the source of truth for comparison
        initialHelperDataRef.current[helperId] = savedData;
        lastSaveTimeRef.current = Date.now(); // Track save time
        
        // Update state IMMEDIATELY - create deep copy to ensure React detects the change
        const newInitialData = JSON.parse(JSON.stringify(initialHelperDataRef.current));
        setInitialHelperData(newInitialData);
        setInitialHelperDataVersion(prev => prev + 1);
        
        // Also update helperData to match - this ensures UI shows saved state immediately
        setHelperData(prev => {
          const updated = { ...prev };
          updated[helperId] = savedData;
          helperDataRef.current = updated;
          return updated;
        });
        
        // Refresh from backend - useEffect will update helperData but NOT initialHelperDataRef (because isSavingRef is true)
        onUpdate();
        // CRITICAL: Wait longer before allowing useEffect to update initialHelperDataRef
        // This gives the backend time to process and return the updated data
        setTimeout(() => { 
          isSavingRef.current = false;
          // Don't call onUpdate() again - let the useEffect handle it when helpers prop changes
          // The backend query from the first onUpdate() should have completed by now
        }, 2000);
      } else {
        const errorMsg = response.data.message || response.data.error || 'Fehler beim Speichern';
        const details = response.data.details ? ` (Details: ${response.data.details})` : '';
        const fullErrorMsg = errorMsg + details;
        // Save failed
        setError(fullErrorMsg);
        throw new Error(fullErrorMsg);
      }
    } catch (err: any) {
      let errorMsg = 'Fehler beim Speichern';
      
      if (err.response) {
        errorMsg = err.response.data?.message || `HTTP ${err.response.status}: ${err.response.statusText}`;
        if (err.response.data?.details) {
          errorMsg += ` (Details: ${err.response.data.details})`;
        }
      } else if (err.request) {
        errorMsg = 'Keine Antwort vom Server. Bitte prüfen Sie, ob der Server läuft.';
      } else {
        errorMsg = err.message || 'Unbekannter Fehler';
      }
      
      // Save error details
      
      setError(errorMsg);
      throw err; // Re-throw to allow caller to handle
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for potential future use
  const _saveHelperData = async (helperId: number, rückmeldung: string, bemerkung: string, caretaker_id?: number | null) => {
    // Clear existing timeout for this helper
    setSaveTimeout(prev => {
      const newTimeout = { ...prev };
      if (newTimeout[helperId]) {
        clearTimeout(newTimeout[helperId]);
        delete newTimeout[helperId];
      }
      return newTimeout;
    });

    // Set new timeout to debounce saves - increased to 2 seconds to avoid interrupting typing
    // IMPORTANT: Read latest values from ref when timeout executes, not from closure
    const timeout = setTimeout(async () => {
      // Get the latest values from ref to avoid stale closure values
      const latestData = helperDataRef.current[helperId];
      if (!latestData) {
        return;
      }

      // Save silently without showing notification or disabling inputs
      setError('');

      try {
        const payload = {
          rückmeldung: latestData.rückmeldung || null,
          bemerkung: (latestData.bemerkung || '').trim() || null,
          caretaker_id: latestData.caretaker_id !== undefined ? latestData.caretaker_id : null
        };
        
        const url = `${API_BASE_URL}/api/helper/${helperId}/fachbereich/${encodeURIComponent(fachbereich)}/feedback`;
        
        const response = await axios.put(url, payload);

        if (response.data.success) {
          // CRITICAL: Update initialHelperDataRef IMMEDIATELY with what we just saved
          // Use REF so useEffect can't overwrite it
          const savedData = {
            rückmeldung: latestData.rückmeldung || '',
            bemerkung: latestData.bemerkung || '',
            caretaker_id: latestData.caretaker_id || null
          };
          initialHelperDataRef.current[helperId] = savedData;
          setInitialHelperData({ ...initialHelperDataRef.current });
          setInitialHelperDataVersion(prev => prev + 1);
          
          // Clear timeout from state after successful save
          setSaveTimeout(prev => {
            const newTimeout = { ...prev };
            delete newTimeout[helperId];
            return newTimeout;
          });
          
          // Refresh from backend - useEffect will update helperData but NOT initialHelperDataRef (because isSavingRef is true)
          isSavingRef.current = true;
          onUpdate();
          setTimeout(() => { isSavingRef.current = false; }, 1000);
        } else {
          const errorMsg = response.data.message || response.data.error || 'Fehler beim Speichern';
          const details = response.data.details ? ` (Details: ${response.data.details})` : '';
          const fullErrorMsg = errorMsg + details;
          setError(fullErrorMsg);
          // Clear timeout from state on error
          setSaveTimeout(prev => {
            const newTimeout = { ...prev };
            delete newTimeout[helperId];
            return newTimeout;
          });
        }
      } catch (err: any) {
        let errorMsg = 'Fehler beim Speichern';
        
        if (err.response) {
          // Server responded with error
          errorMsg = err.response.data?.message || `HTTP ${err.response.status}: ${err.response.statusText}`;
          if (err.response.data?.details) {
            errorMsg += ` (Details: ${err.response.data.details})`;
          }
        } else if (err.request) {
          // Request was made but no response received
          errorMsg = 'Keine Antwort vom Server. Bitte prüfen Sie, ob der Server läuft.';
        } else {
          // Something else happened
          errorMsg = err.message || 'Unbekannter Fehler';
        }
        
        // Save error details
        
        setError(errorMsg);
        // Clear timeout from state on error
        setSaveTimeout(prev => {
          const newTimeout = { ...prev };
          delete newTimeout[helperId];
          return newTimeout;
        });
      }
    }, 2000); // Wait 2 seconds after last change before saving

    // Store timeout in state so it can be cleared if needed
    setSaveTimeout(prev => ({
      ...prev,
      [helperId]: timeout
    }));
  };

  // Cleanup timeouts on unmount (saves are handled by handleClose)
  useEffect(() => {
    return () => {
      // Clear all remaining timeouts
      Object.values(saveTimeoutRef.current).forEach(timeout => clearTimeout(timeout));
    };
  }, []); // Only run on unmount

  // SIMPLE: Track position assignment changes locally - save only on Save button click
  const handlePositionAssignmentChange = (checkbox: HTMLInputElement, helper: Helper, hp: Helferpunkt, shouldBeAssigned: boolean) => {
    // Check if helper is willing to do multiple Einsätze before assigning
    if (shouldBeAssigned) {
      const currentAssignments = helperAssignments[helper.id] || [];
      const otherAssignments = currentAssignments.filter((assignment) => assignment.id !== hp.id);
      
      // Check if helper already has assignments and is not willing to do multiple Einsätze - show warning but allow
      if (otherAssignments.length > 0 && convertBereitFuerMehrereEinsaetze(helper.bereit_fuer_mehrere_einsaetze) !== 'Ja') {
        const assignmentText = otherAssignments
          .map(a => {
            const weekday = getWeekdayAbbreviation(a.einsatzdatum);
            const timeStr = `${a.einsatzuhrzeit}${a.einsatzuhrzeit_bis ? ` - ${a.einsatzuhrzeit_bis}` : ''}`;
            const timeDisplay = weekday ? `${weekday} ${timeStr}` : timeStr;
            // Include fachbereich name if different from current fachbereich
            const fachbereichPrefix = a.fachbereich && a.fachbereich !== fachbereich 
              ? `${a.fachbereich} - ` 
              : '';
            return `${fachbereichPrefix}${a.einsatzort} (${timeDisplay})`;
          })
          .join(', ');
        const confirmMessage = `⚠️ Warnung: Dieser Helfer ist bereits anderen Positionen zugeordnet (${assignmentText}) und nicht bereit für mehrere Einsätze.\n\nMöchtest du trotzdem fortfahren?`;
        if (!window.confirm(confirmMessage)) {
          checkbox.checked = !shouldBeAssigned;
          return;
        }
      }
      
      // Check for time conflicts before assigning
      const conflictingAssignments = currentAssignments.filter((assignment) => {
        if (assignment.id === hp.id) return false;
        return checkDateTimeOverlap(
          hp.einsatzdatum,
          hp.einsatzuhrzeit,
          hp.einsatzuhrzeit_bis || null,
          assignment.einsatzdatum,
          assignment.einsatzuhrzeit,
          assignment.einsatzuhrzeit_bis || null
        );
      });
      
      if (conflictingAssignments.length > 0) {
        const conflictText = conflictingAssignments
          .map(a => {
            const weekday = getWeekdayAbbreviation(a.einsatzdatum);
            const timeStr = `${a.einsatzuhrzeit}${a.einsatzuhrzeit_bis ? ` - ${a.einsatzuhrzeit_bis}` : ''}`;
            const timeDisplay = weekday ? `${weekday} ${timeStr}` : timeStr;
            // Include fachbereich name if different from current fachbereich
            const fachbereichPrefix = a.fachbereich && a.fachbereich !== fachbereich 
              ? `${a.fachbereich} - ` 
              : '';
            return `${fachbereichPrefix}${a.einsatzort} (${timeDisplay})`;
          })
          .join(', ');
        const confirmMessage = `⚠️ Warnung: Diese Position überschneidet sich zeitlich mit: ${conflictText}\n\nMöchtest du trotzdem fortfahren?`;
        if (!window.confirm(confirmMessage)) {
          checkbox.checked = !shouldBeAssigned;
          return;
        }
      }
    }
    
    // Update pending position assignments - SIMPLE, no auto-save
    setPendingPositionAssignments(prev => {
      const currentHelperIds = prev[hp.id] || [];
      let newHelperIds: number[];
      if (shouldBeAssigned) {
        // Add this helper
        if (!currentHelperIds.includes(helper.id)) {
          newHelperIds = [...currentHelperIds, helper.id];
        } else {
          newHelperIds = currentHelperIds; // Already there
        }
      } else {
        // Remove this helper
        newHelperIds = currentHelperIds.filter(id => id !== helper.id);
      }
      return {
        ...prev,
        [hp.id]: newHelperIds
      };
    });
    
    // Update local display state for immediate UI feedback
    setHelferpunktHelpersMap(prev => {
      const currentHelperIds = prev[hp.id] || [];
      let newHelperIds: number[];
      if (shouldBeAssigned) {
        if (!currentHelperIds.includes(helper.id)) {
          newHelperIds = [...currentHelperIds, helper.id];
        } else {
          newHelperIds = currentHelperIds;
        }
      } else {
        newHelperIds = currentHelperIds.filter(id => id !== helper.id);
      }
      return {
        ...prev,
        [hp.id]: newHelperIds
      };
    });
    
    // Update helperAssignments for display
    setHelperAssignments(prev => {
      const currentAssignments = prev[helper.id] || [];
      if (shouldBeAssigned) {
        if (!currentAssignments.some(a => a.id === hp.id)) {
          return {
            ...prev,
            [helper.id]: [...currentAssignments, hp]
          };
        }
      } else {
        return {
          ...prev,
          [helper.id]: currentAssignments.filter(a => a.id !== hp.id)
        };
      }
      return prev;
    });
  };

  const handleClose = React.useCallback(() => {
    // Clear everything when closing
    setHelperData({});
    initialHelperDataRef.current = {};
    setInitialHelperData({});
    setInitialHelperDataVersion(prev => prev + 1);
    helperDataRef.current = {};
    setPendingPositionAssignments({});
    initialPositionAssignmentsRef.current = {};
    onClose();
  }, [onClose]);

  // OLD handleClose - keeping for reference but replaced above
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- dead code kept for reference
  const _handleCloseOld = async () => {
    // Save only changed values to reduce network traffic
    const pendingSaves: Promise<void>[] = [];
    const helperIds = Object.keys(helperDataRef.current);
    
    helperIds.forEach(helperIdStr => {
      const helperId = parseInt(helperIdStr);
      const currentData = helperDataRef.current[helperId];
      const initialData = initialHelperData[helperId];
      
      if (!currentData) return;
      
      // Check if any field has changed
      // If no initial data exists, treat as changed (shouldn't happen, but be safe)
      if (!initialData) {
        const payload = {
          rückmeldung: currentData.rückmeldung || null,
          bemerkung: (currentData.bemerkung || '').trim() || null,
          caretaker_id: currentData.caretaker_id !== undefined ? currentData.caretaker_id : null
        };
        if (saveTimeoutRef.current[helperId]) {
          clearTimeout(saveTimeoutRef.current[helperId]);
        }
        pendingSaves.push(
          saveHelperDataPartial(helperId, payload)
            .then(() => {
              // Successfully saved
            })
            .catch((err: any) => {
              setError(`Fehler beim Speichern von Helfer ${helperId}: ${err.message || 'Unbekannter Fehler'}`);
            })
        );
        return;
      }
      
      const rückmeldungChanged = (currentData.rückmeldung || '') !== (initialData.rückmeldung || '');
      const bemerkungChanged = (currentData.bemerkung || '') !== (initialData.bemerkung || '');
      const caretakerChanged = (currentData.caretaker_id ?? null) !== (initialData.caretaker_id ?? null);
      
      if (!rückmeldungChanged && !bemerkungChanged && !caretakerChanged) {
        // No changes, skip saving
        return;
      }
      
        // Clear any pending timeout
        if (saveTimeoutRef.current[helperId]) {
          clearTimeout(saveTimeoutRef.current[helperId]);
        }
      
      // Build payload with only changed fields
      const payload: { rückmeldung?: string | null; bemerkung?: string | null; caretaker_id?: number | null } = {};
      
      if (rückmeldungChanged) {
        payload.rückmeldung = currentData.rückmeldung || null;
      }
      if (bemerkungChanged) {
        payload.bemerkung = (currentData.bemerkung || '').trim() || null;
      }
      if (caretakerChanged) {
        payload.caretaker_id = currentData.caretaker_id !== undefined ? currentData.caretaker_id : null;
      }
      
        pendingSaves.push(
        saveHelperDataPartial(helperId, payload, true) // skipAdminCheck = true for close handler
          .then(() => {
            // Successfully saved
          })
            .catch(err => {
              setError(`Fehler beim Speichern von Helfer ${helperId}: ${err.message || 'Unbekannter Fehler'}`);
            })
        );
    });
    
    // Wait for all saves to complete (with timeout to avoid blocking too long)
    try {
    await Promise.allSettled(pendingSaves);
    } catch (err) {
      // Error during save on close
    }
    
    // Small delay to ensure backend has processed all saves
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Refresh data from backend BEFORE closing to ensure fresh data is available when dialog reopens
    if (onUpdate) {
      onUpdate();
      // Wait longer for the update to complete and propagate to props
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    // Reset initialization flag so next time dialog opens, it loads fresh from backend
    isInitializedRef.current = false;
    // Clear helperData to force fresh load
    setHelperData({});
    initialHelperDataRef.current = {};
    setInitialHelperData({});
    setInitialHelperDataVersion(prev => prev + 1);
    lastHelperIdsRef.current = '';
    
    onClose();
  };

  // Handle Escape key to close modal - SIMPLE
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        e.stopPropagation();
        e.preventDefault();
        
        // If refresh confirm modal is open, close it (cancel)
        if (showRefreshConfirmModal) {
          setPendingFilterChange(null);
          setShowRefreshConfirmModal(false);
          return;
        }
        // If assigning positions, don't close
        if (assigningHelperId !== null) {
          return;
        }
        // Otherwise, close the main dialog
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEscape, true);
    return () => window.removeEventListener('keydown', handleEscape, true);
  }, [assigningHelperId, handleClose, showRefreshConfirmModal]);

  return (
    <div className="fachbereich-helper-list-overlay">
      <div className="fachbereich-helper-list-container">
        {!compactMode && (
          <div className="fachbereich-helper-list-header">
            <h3>Fachbereich Helferliste: {fachbereich}</h3>
            <button className="fachbereich-close" onClick={handleClose}>×</button>
          </div>
        )}
        {compactMode && (
          <div className="fachbereich-helper-list-header" style={{ justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px' }}>
            <h3 style={{ margin: 0 }}>Rückmeldung für Helfer</h3>
            <button className="fachbereich-close" onClick={handleClose}>×</button>
          </div>
        )}
        {/* In compact mode (Rückmeldung verwalten from position): show only Betreuer filter so user can switch to see helpers assigned to other Betreuer */}
        {compactMode && betreuersEnabled && !isAdmin() && (
          <div className="fachbereich-helper-list-filter-container" style={{ margin: '0 20px 10px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }}>
                {showMyHelfer ? `Meine betreuten Helfer (${helferCounts.myHelfer}/${helferCounts.allHelfer})` : `Helfer ungeachtet der Betreuer (${helferCounts.allHelfer})`}
              </span>
              <label className="switch-container">
                <input
                  type="checkbox"
                  className="switch-input"
                  checked={showMyHelfer}
                  onChange={(e) => setShowMyHelfer(e.target.checked)}
                />
                <span className="switch-track switch-track-red">
                  <span className="switch-thumb" />
                </span>
              </label>
            </div>
          </div>
        )}
        {yearMaintenanceDisabled && !readOnly && (
          <div className="fachbereich-error" style={{ 
            margin: '10px 20px', 
            padding: '15px', 
            backgroundColor: '#fff3cd', 
            border: '2px solid #ffc107', 
            borderRadius: '4px',
            color: '#856404',
            fontSize: '0.95rem',
            fontWeight: '600'
          }}>
            ⚠️ Das Jahr ist schreibgeschützt. Rückmeldungen und Bemerkungen können nicht geändert werden.
          </div>
        )}
        {error && (
          <div className="fachbereich-error" style={{ 
            margin: '10px 20px', 
            padding: '15px', 
            backgroundColor: '#f8d7da', 
            border: '2px solid #dc3545', 
            borderRadius: '4px',
            color: '#721c24',
            fontSize: '0.95rem',
            fontWeight: '600'
          }}>
            <strong>Fehler beim Speichern:</strong><br />
            {error}
          </div>
        )}
        <div className="fachbereich-helper-list-content" style={compactMode ? { marginTop: '20px' } : {}}>
          {/* Filters at the top - fixed when scrolling */}
          {!compactMode && (
          <div className="fachbereich-helper-list-filter-container">
            <div className="fachbereich-helper-list-filter-inner">
            <div className="fachbereich-helper-list-filter-header">
              <input
                type="text"
                id="fachbereich-helper-search"
                name="fachbereich-helper-search"
                className="fachbereich-helper-list-search-input"
                placeholder="🔍 Suche..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                title="Nach Vorname, Nachname oder E-Mail suchen"
                style={{ padding: '6px 10px', width: '180px', flexShrink: 0, maxWidth: '180px' }}
              />
              <button
                className="fachbereich-helper-list-filter-toggle-mobile"
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                aria-label={filtersExpanded ? 'Filter ausblenden' : 'Filter anzeigen'}
                title={filtersExpanded ? 'Filter ausblenden' : 'Filter anzeigen'}
              >
                {filtersExpanded ? '▼' : '▲'}
              </button>
              <button
                className="fachbereich-helper-list-refresh-btn"
                onClick={() => {
                  if (hasUnsavedChanges()) {
                    setRefreshConfirmMode('refresh');
                    setPendingFilterChange(null);
                    setShowRefreshConfirmModal(true);
                  } else if (onUpdate) {
                    onUpdate();
                  }
                }}
                title="Liste vom Server neu laden und Filter aktualisieren"
                aria-label="Liste aktualisieren"
              >
                🔄
              </button>
              {/* E-Mail button: direct for Fachbereich Admin/Manager, in kebab for global Admin */}
              {canEmailVisibleHelpers() && filteredHelpers.filter(h => h.email && h.email.trim()).length > 0 && (
                <div style={{ marginLeft: 'auto' }}>
                  {isAdmin() ? (
                    <div className="fachbereich-helper-list-header-kebab-container">
                      <button
                        className="fachbereich-helper-list-header-kebab"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowHeaderKebabMenu(!showHeaderKebabMenu);
                        }}
                        title="Optionen"
                      >
                        ⋮
                      </button>
                      {showHeaderKebabMenu && (
                        <div className="fachbereich-helper-list-header-kebab-menu">
                          <button
                            className="helferpunkt-kebab-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowEmailModal(true);
                              setShowHeaderKebabMenu(false);
                            }}
                            title="E-Mail an alle sichtbaren Helfer senden (Filter und Suche berücksichtigt)"
                          >
                            ✉️ E-Mail an sichtbare Helfer
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      className="fachbereich-helper-list-email-btn"
                      onClick={() => setShowEmailModal(true)}
                      title="E-Mail an alle sichtbaren Helfer senden (Filter und Suche berücksichtigt)"
                      aria-label="E-Mail an sichtbare Helfer"
                    >
                      <span className="fachbereich-helper-list-email-btn-icon">✉️</span>
                      <span className="fachbereich-helper-list-email-btn-text">E-Mail an sichtbare Helfer</span>
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className={`fachbereich-helper-list-filter-filters ${filtersExpanded ? 'expanded' : 'collapsed'}`}>
            {/* Rückmeldung Filter */}
            <select
              id="rueckmeldung-filter"
              name="rueckmeldung-filter"
              className="fachbereich-helper-list-filter-select"
              value={filterRueckmeldung}
              onChange={(e) => handleFilterChange('filterRueckmeldung', e.target.value)}
              title="Nach Rückmeldung filtern"
              style={{ padding: '6px 10px', width: '200px', flexShrink: 0 }}
            >
              <option value="">Alle Rückmeldungen (außer Passt nicht)</option>
              <option 
                value="none" 
                disabled={rueckmeldungCounts.none === 0}
              >
                Keine Rückmeldung ({rueckmeldungCounts.none}/{rueckmeldungCounts.all ?? 0})
              </option>
              <option 
                value="not-confirmed" 
                disabled={rueckmeldungCounts['not-confirmed'] === 0}
              >
                Alle außer Bestätigt ({rueckmeldungCounts['not-confirmed']}/{rueckmeldungCounts.all ?? 0})
              </option>
              {RÜCKMELDUNG_OPTIONS.map((option) => {
                const count = rueckmeldungCounts[option] || 0;
                const all = rueckmeldungCounts.all ?? 0;
                return (
                  <option 
                    key={option} 
                    value={option}
                    disabled={count === 0}
                  >
                    {option} ({count}/{all})
                  </option>
                );
              })}
            </select>
            
            {/* Toggle: My Helfer (ON) / All Helfer (OFF) - Betreuer Filter */}
            {betreuersEnabled && !isAdmin() && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }}>
                  {showMyHelfer ? `Meine betreuten Helfer (${helferCounts.myHelfer}/${helferCounts.allHelfer})` : `Helfer ungeachtet der Betreuer (${helferCounts.allHelfer})`}
                </span>
                <label className="switch-container">
                  <input
                    type="checkbox"
                    className="switch-input"
                    checked={showMyHelfer}
                    onChange={(e) => handleFilterChange('showMyHelfer', e.target.checked)}
                  />
                  <span className="switch-track switch-track-red">
                    <span className="switch-thumb" />
                  </span>
                </label>
              </div>
            )}
            
            {/* Toggle: Helfer ohne Betreuer - visible only when betreuersEnabled */}
            {betreuersEnabled && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }}>
                  Helfer ohne Betreuer ({withoutCaretakerCount}/{positionCounts.all})
                </span>
                <label className="switch-container">
                  <input
                    type="checkbox"
                    className="switch-input"
                    checked={showWithoutCaretaker}
                    onChange={(e) => handleFilterChange('showWithoutCaretaker', e.target.checked)}
                  />
                  <span className="switch-track switch-track-red">
                    <span className="switch-thumb" />
                  </span>
                </label>
              </div>
            )}
            
            {/* Toggle: Ohne Position */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }}>
                Ohne Position ({positionCounts.unassigned}/{positionCounts.all})
              </span>
              <label className="switch-container">
                <input
                  type="checkbox"
                  className="switch-input"
                  checked={showWithoutPosition}
                  onChange={(e) => handleFilterChange('showWithoutPosition', e.target.checked)}
                />
                <span className="switch-track switch-track-red">
                  <span className="switch-thumb" />
                </span>
              </label>
            </div>
            
            {/* Toggle: Mit Position */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }}>
                Mit Position ({positionCounts.assigned}/{positionCounts.all})
              </span>
              <label className="switch-container">
                <input
                  type="checkbox"
                  className="switch-input"
                  checked={showWithPosition}
                  onChange={(e) => handleFilterChange('showWithPosition', e.target.checked)}
                />
                <span className="switch-track switch-track-red">
                  <span className="switch-thumb" />
                </span>
              </label>
            </div>

            {/* Toggle: Helfer bereit für mehr Einsätze (default off = Einzel- und Mehrfacheinsätze) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }} title={showOnlyReadyForMultiple ? 'Nur Helfer bereit für mehr Einsätze' : 'Alle Helfer (Einzel- und Mehrfacheinsätze)'}>
                {showOnlyReadyForMultiple ? `Helfer bereit für mehr Einsätze (${readyForMultipleCount}/${positionCounts.all})` : 'Einzel- und Mehrfacheinsätze'}
              </span>
              <label className="switch-container">
                <input
                  type="checkbox"
                  className="switch-input"
                  checked={showOnlyReadyForMultiple}
                  onChange={(e) => handleFilterChange('showOnlyReadyForMultiple', e.target.checked)}
                />
                <span className="switch-track switch-track-red">
                  <span className="switch-thumb" />
                </span>
              </label>
            </div>
            </div>
            </div>
          </div>
          )}
          {filteredHelpers.length === 0 ? (
            <div className="fachbereich-empty">
              <div>
                {!showWithoutPosition && !showWithPosition
                  ? 'Keine Helfer (beide Positionen-Filter sind ausgeschaltet) gefunden'
                  : (showMyHelfer && betreuersEnabled) || filterRueckmeldung || searchQuery.trim() || showOnlyReadyForMultiple || showWithoutCaretaker
                    ? (() => {
                        // Special case: if searching and showMyHelfer is on, show specific message
                        if (searchQuery.trim() && betreuersEnabled && showMyHelfer && !isAdmin()) {
                          return `"${searchQuery.trim()}" nicht in meine Helfer gefunden${helpers.length > 0 ? ' (Filter aktiv)' : ''}`;
                        }
                        
                        const filters: string[] = [];
                        if (betreuersEnabled && showMyHelfer && !isAdmin()) {
                          filters.push('meine Helfer');
                        }
                        if (showWithoutCaretaker) {
                          filters.push('ohne Betreuer');
                        }
                        if (filterRueckmeldung) {
                          if (filterRueckmeldung === 'none') {
                            filters.push('ohne Rückmeldung');
                          } else if (filterRueckmeldung === 'not-confirmed') {
                            filters.push('Alle außer Bestätigt');
                          } else {
                            filters.push(`mit Rückmeldung "${filterRueckmeldung}"`);
                          }
                        }
                        if (searchQuery.trim()) {
                          filters.push(`mit Suchkriterien "${searchQuery.trim()}"`);
                        }
                        if (showOnlyReadyForMultiple) {
                          filters.push('bereit für mehr Einsätze');
                        }
                        return `Keine Helfer ${filters.join(' und ')} gefunden${helpers.length > 0 ? ' (Filter aktiv)' : ''}`;
                      })()
                    : helpers.length > 0
                      ? 'Keine Helfer (prüfe Positionen-Filter)'
                      : 'Noch keine Helfer für diesen Fachbereich registriert'}
              </div>
              {helpers.length > 0 && (
                <div style={{ marginTop: '12px', fontSize: '0.9rem', color: '#666', maxWidth: '400px' }}>
                  Hinweis: Die leere Liste liegt oft an den Filtereinstellungen. Versuchen Sie, andere Filter zu wählen oder die Positionen-Filter („Ohne Position“ / „Mit Position“) anzupassen.
                </div>
              )}
            </div>
          ) : (
            <div className="fachbereich-helper-list-lines">
                {filteredHelpers.map((helper) => (
                <div key={helper.id} className="fachbereich-helper-list-line">
                  <div className="fachbereich-helper-list-line-main">
                    <div className="fachbereich-helper-list-line-left">
                      <div className="fachbereich-helper-list-helper-info">
                        <strong style={{ fontSize: '1.1rem' }}>{formatHelperDisplayName(helper.first_name, helper.surname)}</strong>
                        {helper.email && (
                          <span className="helper-email">
                            {' '}<a href={`mailto:${helper.email}`} className="helper-email-link" onClick={(e) => e.stopPropagation()}>{helper.email}</a>
                          </span>
                        )}
                        {helper.phone && <span className="helper-phone"> 📞 {helper.phone}</span>}
                        {(helper.hilft_nicht_am_wettkampftag === 1 || helper.wunscheinsatzort || helper.einsatzzeit || (() => {
                          // Check if bereit_fuer_mehrere_einsaetze is true/1/'Ja'
                          const bereitValue = helper.bereit_fuer_mehrere_einsaetze;
                          if (bereitValue === true || bereitValue === 'Ja' || (typeof bereitValue === 'string' && bereitValue.toLowerCase() === 'ja')) {
                            return true;
                          }
                          if (typeof bereitValue === 'number' && bereitValue === 1) {
                            return true;
                          }
                          if (typeof bereitValue === 'string' && bereitValue === '1') {
                            return true;
                          }
                          return false;
                        })()) && (
                          <div className="fachbereich-helper-list-info" style={{ marginTop: '4px', fontSize: '0.85rem', color: '#666', lineHeight: '1.4' }}>
                            <span style={{ display: 'inline' }}>
                              {helper.hilft_nicht_am_wettkampftag === 1 ? (
                                <><strong style={{ display: 'inline' }}>Hilft nicht am Wettkampftag</strong></>
                              ) : (
                                <>
                                  {helper.wunscheinsatzort && (() => {
                                    let value = String(helper.wunscheinsatzort || '').trim();
                                    value = value.replace(/\s*\|\s*$/g, '').replace(/\|\s*$/g, '').replace(/\s*\|$/g, '').replace(/\|$/g, '');
                                    return <><strong style={{ display: 'inline' }}>Wunscheinsatzort:</strong> {value}</>;
                                  })()}
                                  {helper.wunscheinsatzort && helper.einsatzzeit && <span> | </span>}
                                  {helper.einsatzzeit && (() => {
                                    let value = String(helper.einsatzzeit || '').trim();
                                    value = value.replace(/\s*\|\s*$/g, '').replace(/\|\s*$/g, '').replace(/\s*\|$/g, '').replace(/\|$/g, '');
                                    return <><strong style={{ display: 'inline' }}>Einsatzzeit:</strong> {value}</>;
                                  })()}
                                </>
                              )}
                              {(() => {
                                // Check if bereit_fuer_mehrere_einsaetze is true/1/'Ja'
                                const bereitValue = helper.bereit_fuer_mehrere_einsaetze;
                                let isBereit = false;
                                if (bereitValue === true || bereitValue === 'Ja' || (typeof bereitValue === 'string' && bereitValue.toLowerCase() === 'ja')) {
                                  isBereit = true;
                                } else if (typeof bereitValue === 'number' && bereitValue === 1) {
                                  isBereit = true;
                                } else if (typeof bereitValue === 'string' && bereitValue === '1') {
                                  isBereit = true;
                                }
                                const showSeparator = (helper.hilft_nicht_am_wettkampftag === 1 || helper.wunscheinsatzort || helper.einsatzzeit) && isBereit;
                                return showSeparator ? <span> | </span> : null;
                              })()}
                              {(() => {
                                // Only show "Ist bereit für mehrere Einsätze" when value is true/1/'Ja'
                                const bereitValue = helper.bereit_fuer_mehrere_einsaetze;
                                let isBereit = false;
                                if (bereitValue === true || bereitValue === 'Ja' || (typeof bereitValue === 'string' && bereitValue.toLowerCase() === 'ja')) {
                                  isBereit = true;
                                } else if (typeof bereitValue === 'number' && bereitValue === 1) {
                                  isBereit = true;
                                } else if (typeof bereitValue === 'string' && bereitValue === '1') {
                                  isBereit = true;
                                }
                                return isBereit ? <><strong style={{ display: 'inline' }}>Ist bereit für mehrere Einsätze</strong></> : null;
                              })()}
                            </span>
                          </div>
                        )}
                        {helper.anmerkung && String(helper.anmerkung).trim() !== '' && (
                          <div className="fachbereich-helper-list-info" style={{ marginTop: '6px', fontSize: '0.85rem', color: '#666' }}>
                            <strong>Anmerkung:</strong> {String(helper.anmerkung).trim()}
                          </div>
                        )}
                        {helper.aufbauhilfe && String(helper.aufbauhilfe).trim() !== '' && (
                          <div className="fachbereich-helper-list-info" style={{ marginTop: '6px', fontSize: '0.85rem', color: '#666' }}>
                            <strong>Aufbauhilfe:</strong> {String(helper.aufbauhilfe).trim()}
                          </div>
                        )}
                        {(() => {
                          const otherFbFeedback = getVisibleOtherFachbereichFeedback(helper, fachbereich);
                          if (otherFbFeedback.length === 0) return null;
                          return (
                            <div className="fachbereich-helper-list-info" style={{ marginTop: '6px', fontSize: '0.85rem', color: '#666' }}>
                              <strong>Andere Fachbereiche (Rückmeldungen):</strong>
                              {otherFbFeedback.map((fb) => (
                                <div key={fb.fachbereich} style={{ marginTop: '4px', marginLeft: '8px' }}>
                                  <strong>{fb.fachbereich}:</strong>
                                  {fb.rückmeldung && String(fb.rückmeldung).trim() !== ''
                                    ? ` Rückmeldung: ${String(fb.rückmeldung).trim()}`
                                    : ' Noch keine Rückmeldung'}
                                  {fb.bemerkung && String(fb.bemerkung).trim() !== '' && (
                                    <span> — Bemerkung: {String(fb.bemerkung).trim()}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                        {allHelperAssignmentsLocal[helper.id] && allHelperAssignmentsLocal[helper.id].length > 0 && (
                          <div className="fachbereich-helper-list-helferpunkte" style={{ marginTop: '8px', fontSize: '0.85rem', color: '#666' }}>
                            <strong>Zugeordnete Positionen:</strong>
                            {(() => {
                              // Use ALL assignments across ALL Fachbereiche for global conflict detection
                              const allAssignments = allHelperAssignmentsLocal[helper.id] || [];
                              // Sort positions by date and time
                              const sortedAssignments = [...allAssignments].sort((a, b) => {
                                // First sort by date
                                if (a.einsatzdatum && b.einsatzdatum) {
                                  const dateCompare = a.einsatzdatum.localeCompare(b.einsatzdatum);
                                  if (dateCompare !== 0) return dateCompare;
                                } else if (a.einsatzdatum && !b.einsatzdatum) return -1;
                                else if (!a.einsatzdatum && b.einsatzdatum) return 1;
                                
                                // Then sort by time
                                if (a.einsatzuhrzeit && b.einsatzuhrzeit) {
                                  return a.einsatzuhrzeit.localeCompare(b.einsatzuhrzeit);
                                }
                                return 0;
                              });
                              
                              // Check for time conflicts between positions
                              const conflicts: Array<{ pos1: Helferpunkt; pos2: Helferpunkt }> = [];
                              for (let i = 0; i < sortedAssignments.length; i++) {
                                for (let j = i + 1; j < sortedAssignments.length; j++) {
                                  const pos1 = sortedAssignments[i];
                                  const pos2 = sortedAssignments[j];
                                  if (checkDateTimeOverlap(
                                    pos1.einsatzdatum,
                                    pos1.einsatzuhrzeit,
                                    pos1.einsatzuhrzeit_bis || null,
                                    pos2.einsatzdatum,
                                    pos2.einsatzuhrzeit,
                                    pos2.einsatzuhrzeit_bis || null
                                  )) {
                                    conflicts.push({ pos1, pos2 });
                                  }
                                }
                              }
                              
                              const hasConflicts = conflicts.length > 0;
                              
                              return (
                                <>
                                  {hasConflicts && (
                                    <div style={{ marginTop: '4px', marginBottom: '4px', padding: '6px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', fontSize: '0.8rem', color: '#ed1c24' }}>
                                      ⚠️ Warnung: Dieser Helfer ist mehreren Positionen gleichzeitig zugeordnet. Ein Helfer kann nur zu einer Zeit an einer Position sein!
                                    </div>
                                  )}
                                  <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {sortedAssignments.map((hp) => {
                                      // Check if this position has conflicts with any other
                                      const hasConflict = conflicts.some(c => c.pos1.id === hp.id || c.pos2.id === hp.id);
                                      const weekday = getWeekdayAbbreviation(hp.einsatzdatum);
                                      const timeStr = `${hp.einsatzuhrzeit}${hp.einsatzuhrzeit_bis ? ` - ${hp.einsatzuhrzeit_bis}` : ''}`;
                                      const timeDisplay = weekday ? `${weekday} ${timeStr}` : timeStr;
                                      // Include fachbereich name if different from current fachbereich
                                      const fachbereichPrefix = hp.fachbereich && hp.fachbereich !== fachbereich 
                                        ? `${hp.fachbereich} - ` 
                                        : '';
                                      return (
                                        <div key={hp.id} style={{ color: hasConflict ? '#ed1c24' : '#666' }}>
                                          {hasConflict && <span style={{ color: '#ed1c24', marginRight: '4px' }}>⚠️</span>}
                                          {fachbereichPrefix}{hp.einsatzort} ({timeDisplay})
                                        </div>
                                      );
                                    })}
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        )}
                        {assigningHelperId === helper.id ? (
                          <div className="fachbereich-helper-list-assignment" style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6' }}>
                            <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Positionen zuordnen:</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto', flexShrink: 0 }}>
                                <span style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }} title={hideConflictingPositionsInAssignment ? 'Zeige alle Positionen inkl. kollidierender' : 'Blende Positionen mit Zeitkonflikt aus'}>
                                  {hideConflictingPositionsInAssignment ? 'Konfliktfreie Positionen' : 'Alle Positionen'}
                                </span>
                                <label className="switch-container toggle-switch-label-override" title={hideConflictingPositionsInAssignment ? 'Zeige alle Positionen inkl. kollidierender' : 'Blende Positionen mit Zeitkonflikt aus'}>
                                  <input
                                    type="checkbox"
                                    className="switch-input"
                                    checked={hideConflictingPositionsInAssignment}
                                    onChange={(e) => setHideConflictingPositionsInAssignment(e.target.checked)}
                                  />
                                  <span className="switch-track switch-track-red">
                                    <span className="switch-thumb" />
                                  </span>
                                </label>
                              </div>
                            </div>
                            {helferpunkte.length === 0 ? (
                              <div style={{ color: '#666', fontSize: '0.85rem' }}>Keine Positionen verfügbar</div>
                            ) : (
                              <>
                                {/* Show warning if helper has "Passt nicht" for this Fachbereich */}
                                {helperData[helper.id]?.rückmeldung === 'Passt nicht' && (
                                  <div style={{ fontSize: '0.8rem', color: '#ed1c24', marginBottom: '8px', padding: '6px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px' }}>
                                    ⚠️ Warnung: Dieser Helfer hat die Rückmeldung „Passt nicht“ für diesen Fachbereich. Bitte die Rückmeldung ändern, falls der Helfer doch eingesetzt werden soll.
                                  </div>
                                )}
                                {/* Show warning if helper already has assignments and is not willing to do multiple Einsätze */}
                                {(() => {
                                  // Get saved assignments (exclude pending ones that haven't been saved yet)
                                  const savedAssignments = allHelperAssignmentsLocal[helper.id] || [];
                                  // Only show warning for assignments to OTHER helferpunkte (not the ones currently being assigned)
                                  const helferpunktIds = helferpunkte.map(hp => hp.id);
                                  const otherAssignments = savedAssignments.filter(a => !helferpunktIds.includes(a.id));
                                  const hasOtherAssignments = otherAssignments.length > 0;
                                  const isNotWillingForMultiple = convertBereitFuerMehrereEinsaetze(helper.bereit_fuer_mehrere_einsaetze) !== 'Ja';
                                  const shouldShowWarning = hasOtherAssignments && isNotWillingForMultiple;
                                  
                                  if (shouldShowWarning) {
                                    const assignmentText = otherAssignments
                                      .map(a => {
                                        const weekday = getWeekdayAbbreviation(a.einsatzdatum);
                                        const timeStr = `${a.einsatzuhrzeit}${a.einsatzuhrzeit_bis ? ` - ${a.einsatzuhrzeit_bis}` : ''}`;
                                        const timeDisplay = weekday ? `${weekday} ${timeStr}` : timeStr;
                                        return `${a.einsatzort} (${timeDisplay})`;
                                      })
                                      .join(', ');
                                    return (
                                      <div style={{ fontSize: '0.8rem', color: '#ed1c24', marginBottom: '8px', padding: '6px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px' }}>
                                        ⚠️ Warnung: Dieser Helfer ist bereits anderen Positionen zugeordnet ({assignmentText}) und nicht bereit für mehrere Einsätze. Du kannst trotzdem weitere Positionen zuordnen.
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 12px', paddingBottom: '12px' }}>
                                  {helferpunkte
                                    .filter((hp) => {
                                      // Filter out race day positions for helpers who don't help on race day
                                      // But always show positions the helper is already assigned to (to allow unassigning)
                                      const helperIdsForHelferpunkt = helferpunktHelpersMap[hp.id] || [];
                                      const isAssigned = helperIdsForHelferpunkt.includes(helper.id);
                                      
                                      // If helper is already assigned, always show the position
                                      if (isAssigned) return true;
                                      
                                      // Filter out race day positions for helpers who don't help on race day
                                      // Use same logic as HelperAssignment component (which works correctly)
                                      // Check helper.hilft_nicht_am_wettkampftag - convert to number for comparison
                                      const helperDoesNotHelpOnRaceDay = helper.hilft_nicht_am_wettkampftag === 1 || 
                                                                          Number(helper.hilft_nicht_am_wettkampftag) === 1;
                                      
                                      if (helperDoesNotHelpOnRaceDay && hp.einsatzdatum && raceDayDate) {
                                        // Check if position date matches race day date (exact string match)
                                        // Normalize dates to ensure comparison works
                                        const positionDate = String(hp.einsatzdatum).trim();
                                        const raceDate = String(raceDayDate).trim();
                                        
                                        if (positionDate === raceDate) {
                                          // Position is on race day and helper doesn't help on race day - filter it out
                                          return false;
                                        }
                                      }
                                      
                                      return true; // Show all other positions
                                    })
                                    .filter((hp) => {
                                      // When "Kollidierende ausblenden" is on: hide positions with time conflicts (except those helper is assigned to)
                                      if (!hideConflictingPositionsInAssignment) return true;
                                      const helperIdsForHelferpunkt = helferpunktHelpersMap[hp.id] || [];
                                      const isAssigned = helperIdsForHelferpunkt.includes(helper.id);
                                      if (isAssigned) return true;
                                      const currentAssignments = allHelperAssignmentsLocal[helper.id] || [];
                                      const hasTimeConflict = currentAssignments.some((assignment) => {
                                        if (assignment.id === hp.id) return false;
                                        return checkDateTimeOverlap(
                                          hp.einsatzdatum,
                                          hp.einsatzuhrzeit,
                                          hp.einsatzuhrzeit_bis || null,
                                          assignment.einsatzdatum,
                                          assignment.einsatzuhrzeit,
                                          assignment.einsatzuhrzeit_bis || null
                                        );
                                      });
                                      return !hasTimeConflict;
                                    })
                                    .map((hp) => {
                                    // Use helferpunktHelpersMap to track actual server state for each helferpunkt
                                    const helperIdsForHelferpunkt = helferpunktHelpersMap[hp.id] || [];
                                    const isAssigned = helperIdsForHelferpunkt.includes(helper.id);
                                    
                                    // Check for time conflicts with existing assignments across ALL Fachbereiche
                                    const currentAssignments = allHelperAssignmentsLocal[helper.id] || [];
                                    const hasTimeConflict = currentAssignments.some((assignment) => {
                                      if (assignment.id === hp.id) return false; // Don't count the current position itself
                                      return checkDateTimeOverlap(
                                        hp.einsatzdatum,
                                        hp.einsatzuhrzeit,
                                        hp.einsatzuhrzeit_bis || null,
                                        assignment.einsatzdatum,
                                        assignment.einsatzuhrzeit,
                                        assignment.einsatzuhrzeit_bis || null
                                      );
                                    });
                                    
                                    // Get conflicting assignments for warning display
                                    const conflictingAssignments = currentAssignments.filter((assignment) => {
                                      if (assignment.id === hp.id) return false;
                                      return checkDateTimeOverlap(
                                        hp.einsatzdatum,
                                        hp.einsatzuhrzeit,
                                        hp.einsatzuhrzeit_bis || null,
                                        assignment.einsatzdatum,
                                        assignment.einsatzuhrzeit,
                                        assignment.einsatzuhrzeit_bis || null
                                      );
                                    });
                                    
                                    return (
                                      <label key={hp.id} style={{ display: 'inline-flex', alignItems: 'center', fontSize: '0.85rem', cursor: (assigningHelperId !== helper.id || Object.keys(helferpunktHelpersMap).length === 0) ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>
                                        <input
                                          type="checkbox"
                                          id={`helferpunkt-checkbox-${helper.id}-${hp.id}`}
                                          name={`helferpunkt-${helper.id}-${hp.id}`}
                                          checked={isAssigned}
                                          disabled={assigningHelperId !== helper.id || Object.keys(helferpunktHelpersMap).length === 0 || yearMaintenanceDisabled}
                                          onChange={(e) => {
                                            const checkbox = e.target as HTMLInputElement;
                                            if (yearMaintenanceDisabled) {
                                              checkbox.checked = !checkbox.checked; // Revert change
                                              return;
                                            }
                                            const shouldBeAssigned = checkbox.checked;
                                            
                                            // Check if user is Admin - show warning before assigning
                                            if (shouldBeAssigned && isAdmin() && user?.role === 'Administrator') {
                                              setPendingAssignmentAction(() => () => {
                                                handlePositionAssignmentChange(checkbox, helper, hp, shouldBeAssigned);
                                              });
                                              setShowAdminWarningAssignment(true);
                                              return;
                                            }
                                            
                                            handlePositionAssignmentChange(checkbox, helper, hp, shouldBeAssigned);
                                          }}
                                          title={yearMaintenanceDisabled ? 'Das Jahr ist schreibgeschützt - Positionen können nicht zugeordnet werden' : ''}
                                          style={{ marginRight: '6px', flexShrink: 0, opacity: yearMaintenanceDisabled ? 0.6 : 1, cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer' }}
                                        />
                                        <span style={{ color: hasTimeConflict ? '#ed1c24' : '#000000', fontWeight: hasTimeConflict ? 'bold' : 'normal' }}>
                                          {hasTimeConflict && <span style={{ color: '#ed1c24', marginRight: '2px' }}>⚠️</span>}
                                          {hp.einsatzort} ({(() => {
                                            const weekday = getWeekdayAbbreviation(hp.einsatzdatum);
                                            const timeStr = `${hp.einsatzuhrzeit}${hp.einsatzuhrzeit_bis ? ` - ${hp.einsatzuhrzeit_bis}` : ''}`;
                                            return weekday ? `${weekday} ${timeStr}` : timeStr;
                                          })()})
                                          {(() => {
                                            const assignedCount = helperIdsForHelferpunkt.length;
                                            const neededCount = hp.required_assignees;
                                            if (neededCount != null && neededCount > 0) {
                                              return <span style={{ marginLeft: '6px', color: '#666', fontWeight: 'normal' }}>({assignedCount}/{neededCount})</span>;
                                            }
                                            return assignedCount > 0 ? <span style={{ marginLeft: '6px', color: '#666', fontWeight: 'normal' }}>({assignedCount})</span> : null;
                                          })()}
                                        </span>
                                        {hasTimeConflict && conflictingAssignments.length > 0 && (
                                          <div className="fachbereich-helper-list-conflict-message" style={{ fontSize: '0.75rem', color: '#ed1c24', marginTop: '2px', marginLeft: '20px', overflowWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'normal', maxWidth: '100%', flexBasis: '100%', minWidth: 0 }}>
                                            ⚠️ Konflikt mit: {conflictingAssignments.map(a => {
                                              const weekday = getWeekdayAbbreviation(a.einsatzdatum);
                                              const timeStr = `${a.einsatzuhrzeit}${a.einsatzuhrzeit_bis ? ` - ${a.einsatzuhrzeit_bis}` : ''}`;
                                              const timeDisplay = weekday ? `${weekday} ${timeStr}` : timeStr;
                                              // Include fachbereich name if different from current fachbereich
                                              const fachbereichPrefix = a.fachbereich && a.fachbereich !== fachbereich 
                                                ? `${a.fachbereich} - ` 
                                                : '';
                                              return `${fachbereichPrefix}${a.einsatzort} (${timeDisplay})`;
                                            }).join(', ')}
                                          </div>
                                        )}
                                      </label>
                                    );
                                  })}
                                </div>
                              </>
                            )}
                      </div>
                        ) : (
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <button
                              onClick={async () => {
                                if (!yearMaintenanceDisabled) {
                                  setAssigningHelperId(helper.id);
                                  setError(''); // Clear any errors
                                  
                                  // Fetch helferpunkte if they're empty or refresh them to ensure we have the latest
                                  let helferpunkteToUse = helferpunkte;
                                  if (helferpunkte.length === 0) {
                                    try {
                                      const response = await axios.get(`${API_BASE_URL}/api/fachbereich/${encodeURIComponent(fachbereich)}/helferpunkte`, {
                                        headers: {
                                          'Authorization': `Bearer ${token}`
                                        }
                                      });
                                      if (response.data.success) {
                                        helferpunkteToUse = response.data.helferpunkte || [];
                                        setHelferpunkte(helferpunkteToUse);
                                      } else {
                                        setError('Fehler beim Laden der Positionen');
                                        return;
                                      }
                                    } catch (err) {
                                      setError('Fehler beim Laden der Positionen');
                                      return;
                                    }
                                  }
                                  
                                  // Fetch current helpers for all helferpunkte when opening assignment panel
                                  // This ensures we have the actual server state
                                  const helpersMap: { [key: number]: number[] } = {};
                                  await Promise.all(
                                    helferpunkteToUse.map(async (hp) => {
                                      try {
                                        const response = await axios.get(`${API_BASE_URL}/api/helferpunkte/${hp.id}/helpers`);
                                        if (response.data.success) {
                                          const helperIds = response.data.helpers.map((h: Helper) => h.id);
                                          helpersMap[hp.id] = helperIds;
                                        }
                                      } catch (err) {
                                        // Error fetching helpers for helferpunkt
                                      }
                                    })
                                  );
                                  // CRITICAL: Merge with existing pending - do NOT reset, or we lose unsaved assignments
                                  // when switching from one helper to another (e.g. Helper A gets position, user clicks Helper B)
                                  const currentPending = pendingPositionAssignmentsRef.current;
                                  const merged: { [key: number]: number[] } = { ...helpersMap };
                                  for (const hpId of Object.keys(currentPending)) {
                                    const hpIdNum = parseInt(hpId);
                                    merged[hpIdNum] = currentPending[hpIdNum];
                                  }
                                  setPendingPositionAssignments(merged);
                                  setHelferpunktHelpersMap(merged);
                                  // Initialize ref only on first open (when no pending yet) - don't overwrite
                                  if (Object.keys(initialPositionAssignmentsRef.current).length === 0) {
                                    initialPositionAssignmentsRef.current = { ...helpersMap };
                                  }
                                  // Do NOT call fetchHelperAssignments - it would overwrite helperAssignments
                                  // and lose pending position assignments for other helpers
                                  await fetchAllHelperAssignments(); // Refresh for global conflict detection only
                                  // Force a small delay to ensure state is updated
                                  await new Promise(resolve => setTimeout(resolve, 50));
                                }
                              }}
                              disabled={yearMaintenanceDisabled}
                              className="fachbereich-helper-list-position-button"
                              title={yearMaintenanceDisabled ? 'Das Jahr ist schreibgeschützt - Positionen können nicht zugeordnet werden' : ''}
                              style={{ 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                gap: '4px',
                                width: '240px',
                                minWidth: '240px',
                                maxWidth: '240px',
                                height: '32px',
                                minHeight: '32px',
                                maxHeight: '32px',
                                justifyContent: 'center',
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                                boxSizing: 'border-box',
                                lineHeight: '1',
                                padding: '6px 12px',
                                opacity: yearMaintenanceDisabled ? 0.6 : 1,
                                cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer'
                              }}
                            >
                              <span>📍</span>
                              <span>{helperAssignments[helper.id] && helperAssignments[helper.id].length > 0 ? 'Positionen ändern' : 'Positionen zuordnen'}</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="fachbereich-helper-list-line-right">
                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                        {betreuersEnabled && (
                          <div className="fachbereich-helper-list-field-wrapper" style={{ minWidth: '150px' }}>
                        <select
                              id={`caretaker-${helper.id}`}
                              name={`caretaker-${helper.id}`}
                              value={helperData[helper.id]?.caretaker_id || ''}
                              onChange={(e) => {
                                if (!yearMaintenanceDisabled) {
                                  handleCaretakerChange(helper.id, e.target.value);
                                }
                              }}
                              disabled={yearMaintenanceDisabled}
                              className={`fachbereich-helper-list-select ${!helperData[helper.id]?.caretaker_id ? 'fachbereich-helper-list-select-empty' : ''}`}
                              title={yearMaintenanceDisabled ? 'Das Jahr ist schreibgeschützt' : ''}
                              style={{ opacity: yearMaintenanceDisabled ? 0.6 : 1, cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer' }}
                            >
                              <option value="" disabled>Betreuer</option>
                              {caretakers.map((caretaker) => (
                                <option key={caretaker.id} value={caretaker.id.toString()}>
                                  {caretaker.username}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div className="fachbereich-helper-list-field-wrapper" style={{ minWidth: '180px' }}>
                          <select
                            id={`rückmeldung-${helper.id}`}
                            name={`rückmeldung-${helper.id}`}
                          value={helperData[helper.id]?.rückmeldung || ''}
                          onChange={(e) => {
                            if (!yearMaintenanceDisabled) {
                              handleRückmeldungChange(helper.id, e.target.value);
                            } else {
                              e.preventDefault();
                            }
                          }}
                            disabled={yearMaintenanceDisabled}
                            className={`fachbereich-helper-list-select ${!helperData[helper.id]?.rückmeldung ? 'fachbereich-helper-list-select-empty' : ''}`}
                            title={yearMaintenanceDisabled ? 'Das Jahr ist schreibgeschützt - Rückmeldung kann nicht geändert werden' : ''}
                            style={{ 
                              opacity: yearMaintenanceDisabled ? 0.6 : 1, 
                              cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer',
                              backgroundColor: yearMaintenanceDisabled ? '#f5f5f5' : 'white'
                            }}
                        >
                            <option value="" disabled>Rückmeldung</option>
                          {RÜCKMELDUNG_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                        <div className="fachbereich-helper-list-field-wrapper" style={{ flex: '1', minWidth: '200px' }}>
                          <textarea
                            id={`bemerkung-${helper.id}`}
                            name={`bemerkung-${helper.id}`}
                          value={helperData[helper.id]?.bemerkung || ''}
                            onChange={(e) => {
                              if (!yearMaintenanceDisabled && e.target.value.length <= 200) {
                                handleBemerkungChange(helper.id, e.target.value);
                              } else if (yearMaintenanceDisabled) {
                                e.preventDefault();
                              }
                            }}
                            onKeyDown={(e) => {
                              if (yearMaintenanceDisabled) {
                                e.preventDefault();
                              }
                            }}
                          // Removed onBlur auto-save - user must click Save button
                          maxLength={200}
                            rows={3}
                            disabled={yearMaintenanceDisabled}
                            readOnly={yearMaintenanceDisabled}
                            className="fachbereich-helper-list-textarea"
                          placeholder={yearMaintenanceDisabled ? 'Schreibgeschützt' : "Bemerkung (max. 200 Zeichen)"}
                            title={yearMaintenanceDisabled ? 'Das Jahr ist schreibgeschützt - Bemerkung kann nicht geändert werden' : ''}
                            style={{ 
                              width: '100%', 
                              padding: '8px', 
                              boxSizing: 'border-box', 
                              fontFamily: 'inherit', 
                              fontSize: '0.9rem', 
                              border: yearMaintenanceDisabled ? '1px solid #ccc' : '1px solid #ebebeb', 
                              borderRadius: '0', 
                              opacity: yearMaintenanceDisabled ? 0.6 : 1, 
                              cursor: yearMaintenanceDisabled ? 'not-allowed' : 'text',
                              backgroundColor: yearMaintenanceDisabled ? '#f5f5f5' : 'white'
                            }}
                        />
                        <small className="fachbereich-helper-list-char-count">
                          {(helperData[helper.id]?.bemerkung || '').length}/200 Zeichen
                        </small>
                      </div>
                      </div>
                    </div>
                  </div>
                </div>
                ))}
            </div>
          )}
        </div>
        
        {/* Admin Warning Dialogs */}
        {showAdminWarningFeedback && (
          <div className="fachbereich-confirmation-overlay" onClick={(e) => {
            e.stopPropagation();
          }}>
            <div className="fachbereich-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
              <h3>Hinweis für Administratoren</h3>
              <p>Die Änderung von Rückmeldungen oder Bemerkungen ist normalerweise Aufgabe der Fachbereich Manager oder Fachbereich Administratoren. Als Administrator solltest du diese Aufgabe den zuständigen Fachbereich Managern/Administratoren überlassen.</p>
              <p style={{ marginTop: '15px', fontWeight: 'bold' }}>Möchtest du trotzdem fortfahren?</p>
              <div className="fachbereich-confirmation-buttons">
                <button
                  className="fachbereich-button submit-button"
                  onClick={() => {
                    setShowAdminWarningFeedback(false);
                    if (pendingFeedbackAction) {
                      pendingFeedbackAction();
                      setPendingFeedbackAction(null);
                    }
                  }}
                >
                  Trotzdem fortfahren
                </button>
                <button
                  className="fachbereich-button cancel-button"
                  onClick={() => {
                    setShowAdminWarningFeedback(false);
                    setPendingFeedbackAction(null);
                  }}
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}
        
        {showAdminWarningAssignment && (
          <div className="fachbereich-confirmation-overlay" onClick={(e) => {
            e.stopPropagation();
          }}>
            <div className="fachbereich-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
              <h3>Hinweis für Administratoren</h3>
              <p>Die Zuordnung von Helfern zu Positionen ist normalerweise Aufgabe der Fachbereich Manager oder Fachbereich Administratoren. Als Administrator solltest du diese Aufgabe den zuständigen Fachbereich Managern/Administratoren überlassen.</p>
              <p style={{ marginTop: '15px', fontWeight: 'bold' }}>Möchtest du trotzdem fortfahren?</p>
              <div className="fachbereich-confirmation-buttons">
                <button
                  className="fachbereich-button submit-button"
                  onClick={() => {
                    setShowAdminWarningAssignment(false);
                    if (pendingAssignmentAction) {
                      pendingAssignmentAction();
                      setPendingAssignmentAction(null);
                    }
                  }}
                >
                  Trotzdem fortfahren
                </button>
                <button
                  className="fachbereich-button cancel-button"
                  onClick={() => {
                    setShowAdminWarningAssignment(false);
                    setPendingAssignmentAction(null);
                  }}
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}
        
        {showRefreshConfirmModal && (
          <div className="fachbereich-confirmation-overlay" onClick={(e) => { e.stopPropagation(); }}>
            <div className="fachbereich-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
              <h3>{refreshConfirmMode === 'filter' ? 'Filter anwenden' : 'Liste aktualisieren'}</h3>
              <p>Sie haben ungespeicherte Änderungen (Rückmeldungen, Bemerkungen, Betreuer oder Positionszuordnungen).</p>
              <p style={{ marginTop: '15px', fontWeight: 'bold' }}>Wie möchten Sie fortfahren?</p>
              <div className="fachbereich-confirmation-buttons" style={{ flexDirection: 'column', gap: '10px' }}>
                <button
                  className="fachbereich-button submit-button"
                  onClick={async () => {
                    setShowRefreshConfirmModal(false);
                    await handleSaveAll();
                    if (refreshConfirmMode === 'filter') {
                      applyPendingFilterChange();
                    }
                    // handleSaveAll already calls onUpdate after save for refresh mode
                  }}
                  disabled={yearMaintenanceDisabled}
                >
                  {refreshConfirmMode === 'filter' ? 'Speichern und Filter anwenden' : 'Speichern und aktualisieren'}
                </button>
                <button
                  className="fachbereich-button cancel-button"
                  onClick={async () => {
                    setShowRefreshConfirmModal(false);
                    // Discard changes - reset to last known saved state
                    setHelperData(JSON.parse(JSON.stringify(initialHelperDataRef.current)));
                    const savedAssignments = { ...initialPositionAssignmentsRef.current };
                    setPendingPositionAssignments(savedAssignments);
                    setHelferpunktHelpersMap(savedAssignments);
                    await fetchHelperAssignments();
                    if (refreshConfirmMode === 'refresh') {
                      if (onUpdate) {
                        const result = onUpdate();
                        if (result && typeof result.then === 'function') {
                          await result;
                        }
                      }
                    } else {
                      applyPendingFilterChange();
                    }
                  }}
                  style={{ borderColor: '#ed1c24', color: '#ed1c24' }}
                >
                  {refreshConfirmMode === 'filter' ? 'Verwerfen und Filter anwenden' : 'Verwerfen und aktualisieren'}
                </button>
                <button
                  className="fachbereich-button cancel-button"
                  onClick={() => {
                    setPendingFilterChange(null);
                    setShowRefreshConfirmModal(false);
                  }}
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}
        
        {showEmailModal && (
          <HelperListEmailModal
            onClose={() => setShowEmailModal(false)}
            bccEmails={filteredHelpers
              .filter(h => h.email && h.email.trim())
              .map(h => h.email!.trim())}
          />
        )}
        
        {/* Save button and footer */}
        <div className="fachbereich-helper-list-footer" style={{ 
          backgroundColor: '#fff', 
          borderTop: '2px solid #e0e0e0', 
          padding: '15px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
          flexShrink: 0,
          width: '100%',
          position: 'relative',
          zIndex: 10
        }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              className="fachbereich-button cancel-button"
              onClick={handleClose}
              disabled={saving}
              style={{ padding: '10px 20px' }}
            >
              Abbrechen
            </button>
            <button
              className="fachbereich-button submit-button"
              onClick={() => {
                if (!yearMaintenanceDisabled) {
                  handleSaveAll();
                }
              }}
              disabled={saving || yearMaintenanceDisabled}
              title={yearMaintenanceDisabled ? 'Das Jahr ist schreibgeschützt' : ''}
              style={{ 
                padding: '10px 30px',
                opacity: yearMaintenanceDisabled ? 0.5 : 1,
                cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? 'Speichere...' : 'Speichern'}
            </button>
            <button
              className="fachbereich-button submit-button"
              onClick={async () => {
                if (!yearMaintenanceDisabled) {
                  await handleSaveAll();
                  handleClose();
                }
              }}
              disabled={saving || yearMaintenanceDisabled}
              title={yearMaintenanceDisabled ? 'Das Jahr ist schreibgeschützt' : ''}
              style={{ 
                padding: '10px 30px',
                opacity: yearMaintenanceDisabled ? 0.5 : 1,
                cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? 'Speichere...' : 'Speichern und schließen'}
            </button>
          </div>
        </div>
      </div>
      
    </div>
  );
};

export default FachbereichManagement;
export { FachbereichHelperList };
export type { Helper as FachbereichHelper, Helferpunkt as FachbereichHelferpunkt, Caretaker as FachbereichCaretaker };
